import { useFrame, useThree } from '@react-three/fiber';
import { MeshBasicMaterial, SphereGeometry, BoxGeometry, PlaneGeometry, Mesh, CylinderGeometry, BufferGeometry, Float32BufferAttribute, Quaternion as Quaternion$2, Vector3, Matrix4, Scene, InstancedMesh, Object3D, Euler, MathUtils, DynamicDrawUsage } from 'three';
import { createContext, useContext, useState, useRef, useMemo, useEffect, useLayoutEffect, useCallback } from 'react';
import { jsxs, jsx } from 'react/jsx-runtime';

var events = {exports: {}};

var R = typeof Reflect === 'object' ? Reflect : null;
var ReflectApply = R && typeof R.apply === 'function'
  ? R.apply
  : function ReflectApply(target, receiver, args) {
    return Function.prototype.apply.call(target, receiver, args);
  };

var ReflectOwnKeys;
if (R && typeof R.ownKeys === 'function') {
  ReflectOwnKeys = R.ownKeys;
} else if (Object.getOwnPropertySymbols) {
  ReflectOwnKeys = function ReflectOwnKeys(target) {
    return Object.getOwnPropertyNames(target)
      .concat(Object.getOwnPropertySymbols(target));
  };
} else {
  ReflectOwnKeys = function ReflectOwnKeys(target) {
    return Object.getOwnPropertyNames(target);
  };
}

function ProcessEmitWarning(warning) {
  if (console && console.warn) console.warn(warning);
}

var NumberIsNaN = Number.isNaN || function NumberIsNaN(value) {
  return value !== value;
};

function EventEmitter() {
  EventEmitter.init.call(this);
}
events.exports = EventEmitter;
events.exports.once = once;

// Backwards-compat with node 0.10.x
EventEmitter.EventEmitter = EventEmitter;

EventEmitter.prototype._events = undefined;
EventEmitter.prototype._eventsCount = 0;
EventEmitter.prototype._maxListeners = undefined;

// By default EventEmitters will print a warning if more than 10 listeners are
// added to it. This is a useful default which helps finding memory leaks.
var defaultMaxListeners = 10;

function checkListener(listener) {
  if (typeof listener !== 'function') {
    throw new TypeError('The "listener" argument must be of type Function. Received type ' + typeof listener);
  }
}

Object.defineProperty(EventEmitter, 'defaultMaxListeners', {
  enumerable: true,
  get: function() {
    return defaultMaxListeners;
  },
  set: function(arg) {
    if (typeof arg !== 'number' || arg < 0 || NumberIsNaN(arg)) {
      throw new RangeError('The value of "defaultMaxListeners" is out of range. It must be a non-negative number. Received ' + arg + '.');
    }
    defaultMaxListeners = arg;
  }
});

EventEmitter.init = function() {

  if (this._events === undefined ||
      this._events === Object.getPrototypeOf(this)._events) {
    this._events = Object.create(null);
    this._eventsCount = 0;
  }

  this._maxListeners = this._maxListeners || undefined;
};

// Obviously not all Emitters should be limited to 10. This function allows
// that to be increased. Set to zero for unlimited.
EventEmitter.prototype.setMaxListeners = function setMaxListeners(n) {
  if (typeof n !== 'number' || n < 0 || NumberIsNaN(n)) {
    throw new RangeError('The value of "n" is out of range. It must be a non-negative number. Received ' + n + '.');
  }
  this._maxListeners = n;
  return this;
};

function _getMaxListeners(that) {
  if (that._maxListeners === undefined)
    return EventEmitter.defaultMaxListeners;
  return that._maxListeners;
}

EventEmitter.prototype.getMaxListeners = function getMaxListeners() {
  return _getMaxListeners(this);
};

EventEmitter.prototype.emit = function emit(type) {
  var args = [];
  for (var i = 1; i < arguments.length; i++) args.push(arguments[i]);
  var doError = (type === 'error');

  var events = this._events;
  if (events !== undefined)
    doError = (doError && events.error === undefined);
  else if (!doError)
    return false;

  // If there is no 'error' event listener then throw.
  if (doError) {
    var er;
    if (args.length > 0)
      er = args[0];
    if (er instanceof Error) {
      // Note: The comments on the `throw` lines are intentional, they show
      // up in Node's output if this results in an unhandled exception.
      throw er; // Unhandled 'error' event
    }
    // At least give some kind of context to the user
    var err = new Error('Unhandled error.' + (er ? ' (' + er.message + ')' : ''));
    err.context = er;
    throw err; // Unhandled 'error' event
  }

  var handler = events[type];

  if (handler === undefined)
    return false;

  if (typeof handler === 'function') {
    ReflectApply(handler, this, args);
  } else {
    var len = handler.length;
    var listeners = arrayClone(handler, len);
    for (var i = 0; i < len; ++i)
      ReflectApply(listeners[i], this, args);
  }

  return true;
};

function _addListener(target, type, listener, prepend) {
  var m;
  var events;
  var existing;

  checkListener(listener);

  events = target._events;
  if (events === undefined) {
    events = target._events = Object.create(null);
    target._eventsCount = 0;
  } else {
    // To avoid recursion in the case that type === "newListener"! Before
    // adding it to the listeners, first emit "newListener".
    if (events.newListener !== undefined) {
      target.emit('newListener', type,
                  listener.listener ? listener.listener : listener);

      // Re-assign `events` because a newListener handler could have caused the
      // this._events to be assigned to a new object
      events = target._events;
    }
    existing = events[type];
  }

  if (existing === undefined) {
    // Optimize the case of one listener. Don't need the extra array object.
    existing = events[type] = listener;
    ++target._eventsCount;
  } else {
    if (typeof existing === 'function') {
      // Adding the second element, need to change to array.
      existing = events[type] =
        prepend ? [listener, existing] : [existing, listener];
      // If we've already got an array, just append.
    } else if (prepend) {
      existing.unshift(listener);
    } else {
      existing.push(listener);
    }

    // Check for listener leak
    m = _getMaxListeners(target);
    if (m > 0 && existing.length > m && !existing.warned) {
      existing.warned = true;
      // No error code for this since it is a Warning
      // eslint-disable-next-line no-restricted-syntax
      var w = new Error('Possible EventEmitter memory leak detected. ' +
                          existing.length + ' ' + String(type) + ' listeners ' +
                          'added. Use emitter.setMaxListeners() to ' +
                          'increase limit');
      w.name = 'MaxListenersExceededWarning';
      w.emitter = target;
      w.type = type;
      w.count = existing.length;
      ProcessEmitWarning(w);
    }
  }

  return target;
}

EventEmitter.prototype.addListener = function addListener(type, listener) {
  return _addListener(this, type, listener, false);
};

EventEmitter.prototype.on = EventEmitter.prototype.addListener;

EventEmitter.prototype.prependListener =
    function prependListener(type, listener) {
      return _addListener(this, type, listener, true);
    };

function onceWrapper() {
  if (!this.fired) {
    this.target.removeListener(this.type, this.wrapFn);
    this.fired = true;
    if (arguments.length === 0)
      return this.listener.call(this.target);
    return this.listener.apply(this.target, arguments);
  }
}

function _onceWrap(target, type, listener) {
  var state = { fired: false, wrapFn: undefined, target: target, type: type, listener: listener };
  var wrapped = onceWrapper.bind(state);
  wrapped.listener = listener;
  state.wrapFn = wrapped;
  return wrapped;
}

EventEmitter.prototype.once = function once(type, listener) {
  checkListener(listener);
  this.on(type, _onceWrap(this, type, listener));
  return this;
};

EventEmitter.prototype.prependOnceListener =
    function prependOnceListener(type, listener) {
      checkListener(listener);
      this.prependListener(type, _onceWrap(this, type, listener));
      return this;
    };

// Emits a 'removeListener' event if and only if the listener was removed.
EventEmitter.prototype.removeListener =
    function removeListener(type, listener) {
      var list, events, position, i, originalListener;

      checkListener(listener);

      events = this._events;
      if (events === undefined)
        return this;

      list = events[type];
      if (list === undefined)
        return this;

      if (list === listener || list.listener === listener) {
        if (--this._eventsCount === 0)
          this._events = Object.create(null);
        else {
          delete events[type];
          if (events.removeListener)
            this.emit('removeListener', type, list.listener || listener);
        }
      } else if (typeof list !== 'function') {
        position = -1;

        for (i = list.length - 1; i >= 0; i--) {
          if (list[i] === listener || list[i].listener === listener) {
            originalListener = list[i].listener;
            position = i;
            break;
          }
        }

        if (position < 0)
          return this;

        if (position === 0)
          list.shift();
        else {
          spliceOne(list, position);
        }

        if (list.length === 1)
          events[type] = list[0];

        if (events.removeListener !== undefined)
          this.emit('removeListener', type, originalListener || listener);
      }

      return this;
    };

EventEmitter.prototype.off = EventEmitter.prototype.removeListener;

EventEmitter.prototype.removeAllListeners =
    function removeAllListeners(type) {
      var listeners, events, i;

      events = this._events;
      if (events === undefined)
        return this;

      // not listening for removeListener, no need to emit
      if (events.removeListener === undefined) {
        if (arguments.length === 0) {
          this._events = Object.create(null);
          this._eventsCount = 0;
        } else if (events[type] !== undefined) {
          if (--this._eventsCount === 0)
            this._events = Object.create(null);
          else
            delete events[type];
        }
        return this;
      }

      // emit removeListener for all listeners on all events
      if (arguments.length === 0) {
        var keys = Object.keys(events);
        var key;
        for (i = 0; i < keys.length; ++i) {
          key = keys[i];
          if (key === 'removeListener') continue;
          this.removeAllListeners(key);
        }
        this.removeAllListeners('removeListener');
        this._events = Object.create(null);
        this._eventsCount = 0;
        return this;
      }

      listeners = events[type];

      if (typeof listeners === 'function') {
        this.removeListener(type, listeners);
      } else if (listeners !== undefined) {
        // LIFO order
        for (i = listeners.length - 1; i >= 0; i--) {
          this.removeListener(type, listeners[i]);
        }
      }

      return this;
    };

function _listeners(target, type, unwrap) {
  var events = target._events;

  if (events === undefined)
    return [];

  var evlistener = events[type];
  if (evlistener === undefined)
    return [];

  if (typeof evlistener === 'function')
    return unwrap ? [evlistener.listener || evlistener] : [evlistener];

  return unwrap ?
    unwrapListeners(evlistener) : arrayClone(evlistener, evlistener.length);
}

EventEmitter.prototype.listeners = function listeners(type) {
  return _listeners(this, type, true);
};

EventEmitter.prototype.rawListeners = function rawListeners(type) {
  return _listeners(this, type, false);
};

EventEmitter.listenerCount = function(emitter, type) {
  if (typeof emitter.listenerCount === 'function') {
    return emitter.listenerCount(type);
  } else {
    return listenerCount.call(emitter, type);
  }
};

EventEmitter.prototype.listenerCount = listenerCount;
function listenerCount(type) {
  var events = this._events;

  if (events !== undefined) {
    var evlistener = events[type];

    if (typeof evlistener === 'function') {
      return 1;
    } else if (evlistener !== undefined) {
      return evlistener.length;
    }
  }

  return 0;
}

EventEmitter.prototype.eventNames = function eventNames() {
  return this._eventsCount > 0 ? ReflectOwnKeys(this._events) : [];
};

function arrayClone(arr, n) {
  var copy = new Array(n);
  for (var i = 0; i < n; ++i)
    copy[i] = arr[i];
  return copy;
}

function spliceOne(list, index) {
  for (; index + 1 < list.length; index++)
    list[index] = list[index + 1];
  list.pop();
}

function unwrapListeners(arr) {
  var ret = new Array(arr.length);
  for (var i = 0; i < ret.length; ++i) {
    ret[i] = arr[i].listener || arr[i];
  }
  return ret;
}

function once(emitter, name) {
  return new Promise(function (resolve, reject) {
    function errorListener(err) {
      emitter.removeListener(name, resolver);
      reject(err);
    }

    function resolver() {
      if (typeof emitter.removeListener === 'function') {
        emitter.removeListener('error', errorListener);
      }
      resolve([].slice.call(arguments));
    }
    eventTargetAgnosticAddListener(emitter, name, resolver, { once: true });
    if (name !== 'error') {
      addErrorHandlerIfEventEmitter(emitter, errorListener, { once: true });
    }
  });
}

function addErrorHandlerIfEventEmitter(emitter, handler, flags) {
  if (typeof emitter.on === 'function') {
    eventTargetAgnosticAddListener(emitter, 'error', handler, flags);
  }
}

function eventTargetAgnosticAddListener(emitter, name, listener, flags) {
  if (typeof emitter.on === 'function') {
    if (flags.once) {
      emitter.once(name, listener);
    } else {
      emitter.on(name, listener);
    }
  } else if (typeof emitter.addEventListener === 'function') {
    // EventTarget does not have `error` event semantics like Node
    // EventEmitters, we do not listen for `error` events here.
    emitter.addEventListener(name, function wrapListener(arg) {
      // IE does not have builtin `{ once: true }` support so we
      // have to do it manually.
      if (flags.once) {
        emitter.removeEventListener(name, wrapListener);
      }
      listener(arg);
    });
  } else {
    throw new TypeError('The "emitter" argument must be of type EventEmitter. Received type ' + typeof emitter);
  }
}

var EventEmitter$1 = events.exports;

function decodeBase64(base64, enableUnicode) {
    var binaryString = atob(base64);
    if (enableUnicode) {
        var binaryView = new Uint8Array(binaryString.length);
        for (var i = 0, n = binaryString.length; i < n; ++i) {
            binaryView[i] = binaryString.charCodeAt(i);
        }
        return String.fromCharCode.apply(null, new Uint16Array(binaryView.buffer));
    }
    return binaryString;
}

function createURL(base64, sourcemapArg, enableUnicodeArg) {
    var sourcemap = sourcemapArg === undefined ? null : sourcemapArg;
    var enableUnicode = enableUnicodeArg === undefined ? false : enableUnicodeArg;
    var source = decodeBase64(base64, enableUnicode);
    var start = source.indexOf('\n', 10) + 1;
    var body = source.substring(start) + (sourcemap ? '\/\/# sourceMappingURL=' + sourcemap : '');
    var blob = new Blob([body], { type: 'application/javascript' });
    return URL.createObjectURL(blob);
}

function createBase64WorkerFactory(base64, sourcemapArg, enableUnicodeArg) {
    var url;
    return function WorkerFactory(options) {
        url = url || createURL(base64, sourcemapArg, enableUnicodeArg);
        return new Worker(url, options);
    };
}

var WorkerFactory = createBase64WorkerFactory('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', null, false);
/* eslint-enable */

class CannonWorkerAPI extends EventEmitter$1 {
  get axisIndex() {
    return this.config.axisIndex;
  }

  set axisIndex(value) {
    this.config.axisIndex = value;
    this.postMessage({
      op: 'setAxisIndex',
      props: value
    });
  }

  get broadphase() {
    return this.config.broadphase;
  }

  set broadphase(value) {
    this.config.broadphase = value;
    this.postMessage({
      op: 'setBroadphase',
      props: value
    });
  }

  get frictionGravity() {
    return this.config.frictionGravity;
  }

  set frictionGravity(value) {
    this.config.frictionGravity = value;
    this.postMessage({
      op: 'setFrictionGravity',
      props: value
    });
  }

  get gravity() {
    return this.config.gravity;
  }

  set gravity(value) {
    this.config.gravity = value;
    this.postMessage({
      op: 'setGravity',
      props: value
    });
  }

  get iterations() {
    return this.config.iterations;
  }

  set iterations(value) {
    this.config.iterations = value;
    this.postMessage({
      op: 'setIterations',
      props: value
    });
  }

  get tolerance() {
    return this.config.tolerance;
  }

  set tolerance(value) {
    this.config.tolerance = value;
    this.postMessage({
      op: 'setTolerance',
      props: value
    });
  }

  constructor(_ref) {
    let {
      allowSleep = false,
      axisIndex = 0,
      broadphase = 'Naive',
      defaultContactMaterial = {
        contactEquationStiffness: 1e6
      },
      frictionGravity = null,
      gravity = [0, -9.81, 0],
      iterations = 5,
      quatNormalizeFast = false,
      quatNormalizeSkip = 0,
      size = 1000,
      solver = 'GS',
      tolerance = 0.001
    } = _ref;
    super();
    this.messageQueue = [];
    this.worker = null;
    this.config = {
      allowSleep,
      axisIndex,
      broadphase,
      defaultContactMaterial,
      frictionGravity,
      gravity,
      iterations,
      quatNormalizeFast,
      quatNormalizeSkip,
      size,
      solver,
      tolerance
    };
    this.buffers = {
      positions: new Float32Array(size * 3),
      quaternions: new Float32Array(size * 4)
    };
  }

  addBodies(_ref2) {
    let {
      props,
      type,
      uuid
    } = _ref2;
    this.postMessage({
      op: 'addBodies',
      props,
      type,
      uuid
    });
  }

  addConstraint(_ref3) {
    let {
      props: [refA, refB, optns],
      type,
      uuid
    } = _ref3;
    this.postMessage({
      op: 'addConstraint',
      props: [refA, refB, optns],
      type,
      uuid
    });
  }

  addContactMaterial(_ref4) {
    let {
      props,
      uuid
    } = _ref4;
    this.postMessage({
      op: 'addContactMaterial',
      props,
      uuid
    });
  }

  addRay(_ref5) {
    let {
      props,
      uuid
    } = _ref5;
    this.postMessage({
      op: 'addRay',
      props,
      uuid
    });
  }

  addRaycastVehicle(_ref6) {
    let {
      props: [chassisBodyUUID, wheelUUIDs, wheelInfos, indexForwardAxis, indexRightAxis, indexUpAxis],
      uuid
    } = _ref6;
    this.postMessage({
      op: 'addRaycastVehicle',
      props: [chassisBodyUUID, wheelUUIDs, wheelInfos, indexForwardAxis, indexRightAxis, indexUpAxis],
      uuid
    });
  }

  addSpring(_ref7) {
    let {
      props: [refA, refB, optns],
      uuid
    } = _ref7;
    this.postMessage({
      op: 'addSpring',
      props: [refA, refB, optns],
      uuid
    });
  }

  applyForce(_ref8) {
    let {
      props,
      uuid
    } = _ref8;
    this.postMessage({
      op: 'applyForce',
      props,
      uuid
    });
  }

  applyImpulse(_ref9) {
    let {
      props,
      uuid
    } = _ref9;
    this.postMessage({
      op: 'applyImpulse',
      props,
      uuid
    });
  }

  applyLocalForce(_ref10) {
    let {
      props,
      uuid
    } = _ref10;
    this.postMessage({
      op: 'applyLocalForce',
      props,
      uuid
    });
  }

  applyLocalImpulse(_ref11) {
    let {
      props,
      uuid
    } = _ref11;
    this.postMessage({
      op: 'applyLocalImpulse',
      props,
      uuid
    });
  }

  applyRaycastVehicleEngineForce(_ref12) {
    let {
      props,
      uuid
    } = _ref12;
    this.postMessage({
      op: 'applyRaycastVehicleEngineForce',
      props,
      uuid
    });
  }

  applyTorque(_ref13) {
    let {
      props,
      uuid
    } = _ref13;
    this.postMessage({
      op: 'applyTorque',
      props,
      uuid
    });
  }

  connect() {
    this.worker = new WorkerFactory();

    this.worker.onmessage = message => {
      if (message.data.op === 'frame') {
        this.buffers.positions = message.data.positions;
        this.buffers.quaternions = message.data.quaternions;
        this.emit(message.data.op, message.data);
        return;
      }

      this.emit(message.data.type, message.data);
    };

    for (const message of this.messageQueue) {
      this.worker.postMessage(message);
    }

    this.messageQueue.length = 0;
  }

  disableConstraint(_ref14) {
    let {
      uuid
    } = _ref14;
    this.postMessage({
      op: 'disableConstraint',
      uuid
    });
  }

  disableConstraintMotor(_ref15) {
    let {
      uuid
    } = _ref15;
    this.postMessage({
      op: 'disableConstraintMotor',
      uuid
    });
  }

  disconnect() {
    if (this.worker) this.worker.onmessage = null;
  }

  enableConstraint(_ref16) {
    let {
      uuid
    } = _ref16;
    this.postMessage({
      op: 'enableConstraint',
      uuid
    });
  }

  enableConstraintMotor(_ref17) {
    let {
      uuid
    } = _ref17;
    this.postMessage({
      op: 'enableConstraintMotor',
      uuid
    });
  }

  init() {
    const {
      allowSleep,
      axisIndex,
      broadphase,
      defaultContactMaterial,
      frictionGravity,
      gravity,
      iterations,
      quatNormalizeFast,
      quatNormalizeSkip,
      solver,
      tolerance
    } = this.config;
    this.postMessage({
      op: 'init',
      props: {
        allowSleep,
        axisIndex,
        broadphase,
        defaultContactMaterial,
        frictionGravity,
        gravity,
        iterations,
        quatNormalizeFast,
        quatNormalizeSkip,
        solver,
        tolerance
      }
    });
  }

  removeBodies(_ref18) {
    let {
      uuid
    } = _ref18;
    this.postMessage({
      op: 'removeBodies',
      uuid
    });
  }

  removeConstraint(_ref19) {
    let {
      uuid
    } = _ref19;
    this.postMessage({
      op: 'removeConstraint',
      uuid
    });
  }

  removeContactMaterial(_ref20) {
    let {
      uuid
    } = _ref20;
    this.postMessage({
      op: 'removeContactMaterial',
      uuid
    });
  }

  removeRay(_ref21) {
    let {
      uuid
    } = _ref21;
    this.postMessage({
      op: 'removeRay',
      uuid
    });
  }

  removeRaycastVehicle(_ref22) {
    let {
      uuid
    } = _ref22;
    this.postMessage({
      op: 'removeRaycastVehicle',
      uuid
    });
  }

  removeSpring(_ref23) {
    let {
      uuid
    } = _ref23;
    this.postMessage({
      op: 'removeSpring',
      uuid
    });
  }

  setAllowSleep(_ref24) {
    let {
      props,
      uuid
    } = _ref24;
    this.postMessage({
      op: 'setAllowSleep',
      props,
      uuid
    });
  }

  setAngularDamping(_ref25) {
    let {
      props,
      uuid
    } = _ref25;
    this.postMessage({
      op: 'setAngularDamping',
      props,
      uuid
    });
  }

  setAngularFactor(_ref26) {
    let {
      props,
      uuid
    } = _ref26;
    this.postMessage({
      op: 'setAngularFactor',
      props,
      uuid
    });
  }

  setAngularVelocity(_ref27) {
    let {
      props,
      uuid
    } = _ref27;
    this.postMessage({
      op: 'setAngularVelocity',
      props,
      uuid
    });
  }

  setCollisionFilterGroup(_ref28) {
    let {
      props,
      uuid
    } = _ref28;
    this.postMessage({
      op: 'setCollisionFilterGroup',
      props,
      uuid
    });
  }

  setCollisionFilterMask(_ref29) {
    let {
      props,
      uuid
    } = _ref29;
    this.postMessage({
      op: 'setCollisionFilterMask',
      props,
      uuid
    });
  }

  setCollisionResponse(_ref30) {
    let {
      props,
      uuid
    } = _ref30;
    this.postMessage({
      op: 'setCollisionResponse',
      props,
      uuid
    });
  }

  setConstraintMotorMaxForce(_ref31) {
    let {
      props,
      uuid
    } = _ref31;
    this.postMessage({
      op: 'setConstraintMotorMaxForce',
      props,
      uuid
    });
  }

  setConstraintMotorSpeed(_ref32) {
    let {
      props,
      uuid
    } = _ref32;
    this.postMessage({
      op: 'setConstraintMotorSpeed',
      props,
      uuid
    });
  }

  setFixedRotation(_ref33) {
    let {
      props,
      uuid
    } = _ref33;
    this.postMessage({
      op: 'setFixedRotation',
      props,
      uuid
    });
  }

  setIsTrigger(_ref34) {
    let {
      props,
      uuid
    } = _ref34;
    this.postMessage({
      op: 'setIsTrigger',
      props,
      uuid
    });
  }

  setLinearDamping(_ref35) {
    let {
      props,
      uuid
    } = _ref35;
    this.postMessage({
      op: 'setLinearDamping',
      props,
      uuid
    });
  }

  setLinearFactor(_ref36) {
    let {
      props,
      uuid
    } = _ref36;
    this.postMessage({
      op: 'setLinearFactor',
      props,
      uuid
    });
  }

  setMass(_ref37) {
    let {
      props,
      uuid
    } = _ref37;
    this.postMessage({
      op: 'setMass',
      props,
      uuid
    });
  }

  setMaterial(_ref38) {
    let {
      props,
      uuid
    } = _ref38;
    this.postMessage({
      op: 'setMaterial',
      props,
      uuid
    });
  }

  setPosition(_ref39) {
    let {
      props,
      uuid
    } = _ref39;
    this.postMessage({
      op: 'setPosition',
      props,
      uuid
    });
  }

  setQuaternion(_ref40) {
    let {
      props: [x, y, z, w],
      uuid
    } = _ref40;
    this.postMessage({
      op: 'setQuaternion',
      props: [x, y, z, w],
      uuid
    });
  }

  setRaycastVehicleBrake(_ref41) {
    let {
      props,
      uuid
    } = _ref41;
    this.postMessage({
      op: 'setRaycastVehicleBrake',
      props,
      uuid
    });
  }

  setRaycastVehicleSteeringValue(_ref42) {
    let {
      props,
      uuid
    } = _ref42;
    this.postMessage({
      op: 'setRaycastVehicleSteeringValue',
      props,
      uuid
    });
  }

  setRotation(_ref43) {
    let {
      props,
      uuid
    } = _ref43;
    this.postMessage({
      op: 'setRotation',
      props,
      uuid
    });
  }

  setSleepSpeedLimit(_ref44) {
    let {
      props,
      uuid
    } = _ref44;
    this.postMessage({
      op: 'setSleepSpeedLimit',
      props,
      uuid
    });
  }

  setSleepTimeLimit(_ref45) {
    let {
      props,
      uuid
    } = _ref45;
    this.postMessage({
      op: 'setSleepTimeLimit',
      props,
      uuid
    });
  }

  setSpringDamping(_ref46) {
    let {
      props,
      uuid
    } = _ref46;
    this.postMessage({
      op: 'setSpringDamping',
      props,
      uuid
    });
  }

  setSpringRestLength(_ref47) {
    let {
      props,
      uuid
    } = _ref47;
    this.postMessage({
      op: 'setSpringRestLength',
      props,
      uuid
    });
  }

  setSpringStiffness(_ref48) {
    let {
      props,
      uuid
    } = _ref48;
    this.postMessage({
      op: 'setSpringStiffness',
      props,
      uuid
    });
  }

  setUserData(_ref49) {
    let {
      props,
      uuid
    } = _ref49;
    this.postMessage({
      op: 'setUserData',
      props,
      uuid
    });
  }

  setVelocity(_ref50) {
    let {
      props,
      uuid
    } = _ref50;
    this.postMessage({
      op: 'setVelocity',
      props,
      uuid
    });
  }

  sleep(_ref51) {
    let {
      uuid
    } = _ref51;
    this.postMessage({
      op: 'sleep',
      uuid
    });
  }

  step(props) {
    var _this$worker;

    const {
      buffers: {
        positions,
        quaternions
      }
    } = this;
    if (!positions.byteLength && !quaternions.byteLength) return;
    (_this$worker = this.worker) == null ? void 0 : _this$worker.postMessage({
      op: 'step',
      positions,
      props,
      quaternions
    }, [positions.buffer, quaternions.buffer]);
  }

  subscribe(_ref52) {
    let {
      props: {
        id,
        target,
        type
      },
      uuid
    } = _ref52;
    this.postMessage({
      op: 'subscribe',
      props: {
        id,
        target,
        type
      },
      uuid
    });
  }

  terminate() {
    var _this$worker2;

    (_this$worker2 = this.worker) == null ? void 0 : _this$worker2.terminate();
    this.worker = null;
  }

  unsubscribe(_ref53) {
    let {
      props
    } = _ref53;
    this.postMessage({
      op: 'unsubscribe',
      props
    });
  }

  wakeUp(_ref54) {
    let {
      uuid
    } = _ref54;
    this.postMessage({
      op: 'wakeUp',
      uuid
    });
  }

  postMessage(message) {
    if (this.worker) return this.worker.postMessage(message);
    this.messageQueue.push(message);
  }

}

/**
 * Records what objects are colliding with each other
 */

/**
 * A 3x3 matrix.
 * Authored by {@link http://github.com/schteppe/ schteppe}
 */
class Mat3$1 {
  /**
   * A vector of length 9, containing all matrix elements.
   */

  /**
   * @param elements A vector of length 9, containing all matrix elements.
   */
  constructor(elements) {
    if (elements === void 0) {
      elements = [0, 0, 0, 0, 0, 0, 0, 0, 0];
    }

    this.elements = elements;
  }
  /**
   * Sets the matrix to identity
   * @todo Should perhaps be renamed to `setIdentity()` to be more clear.
   * @todo Create another function that immediately creates an identity matrix eg. `eye()`
   */


  identity() {
    const e = this.elements;
    e[0] = 1;
    e[1] = 0;
    e[2] = 0;
    e[3] = 0;
    e[4] = 1;
    e[5] = 0;
    e[6] = 0;
    e[7] = 0;
    e[8] = 1;
  }
  /**
   * Set all elements to zero
   */


  setZero() {
    const e = this.elements;
    e[0] = 0;
    e[1] = 0;
    e[2] = 0;
    e[3] = 0;
    e[4] = 0;
    e[5] = 0;
    e[6] = 0;
    e[7] = 0;
    e[8] = 0;
  }
  /**
   * Sets the matrix diagonal elements from a Vec3
   */


  setTrace(vector) {
    const e = this.elements;
    e[0] = vector.x;
    e[4] = vector.y;
    e[8] = vector.z;
  }
  /**
   * Gets the matrix diagonal elements
   */


  getTrace(target) {
    if (target === void 0) {
      target = new Vec3$1();
    }

    const e = this.elements;
    target.x = e[0];
    target.y = e[4];
    target.z = e[8];
    return target;
  }
  /**
   * Matrix-Vector multiplication
   * @param v The vector to multiply with
   * @param target Optional, target to save the result in.
   */


  vmult(v, target) {
    if (target === void 0) {
      target = new Vec3$1();
    }

    const e = this.elements;
    const x = v.x;
    const y = v.y;
    const z = v.z;
    target.x = e[0] * x + e[1] * y + e[2] * z;
    target.y = e[3] * x + e[4] * y + e[5] * z;
    target.z = e[6] * x + e[7] * y + e[8] * z;
    return target;
  }
  /**
   * Matrix-scalar multiplication
   */


  smult(s) {
    for (let i = 0; i < this.elements.length; i++) {
      this.elements[i] *= s;
    }
  }
  /**
   * Matrix multiplication
   * @param matrix Matrix to multiply with from left side.
   */


  mmult(matrix, target) {
    if (target === void 0) {
      target = new Mat3$1();
    }

    const A = this.elements;
    const B = matrix.elements;
    const T = target.elements;
    const a11 = A[0],
          a12 = A[1],
          a13 = A[2],
          a21 = A[3],
          a22 = A[4],
          a23 = A[5],
          a31 = A[6],
          a32 = A[7],
          a33 = A[8];
    const b11 = B[0],
          b12 = B[1],
          b13 = B[2],
          b21 = B[3],
          b22 = B[4],
          b23 = B[5],
          b31 = B[6],
          b32 = B[7],
          b33 = B[8];
    T[0] = a11 * b11 + a12 * b21 + a13 * b31;
    T[1] = a11 * b12 + a12 * b22 + a13 * b32;
    T[2] = a11 * b13 + a12 * b23 + a13 * b33;
    T[3] = a21 * b11 + a22 * b21 + a23 * b31;
    T[4] = a21 * b12 + a22 * b22 + a23 * b32;
    T[5] = a21 * b13 + a22 * b23 + a23 * b33;
    T[6] = a31 * b11 + a32 * b21 + a33 * b31;
    T[7] = a31 * b12 + a32 * b22 + a33 * b32;
    T[8] = a31 * b13 + a32 * b23 + a33 * b33;
    return target;
  }
  /**
   * Scale each column of the matrix
   */


  scale(vector, target) {
    if (target === void 0) {
      target = new Mat3$1();
    }

    const e = this.elements;
    const t = target.elements;

    for (let i = 0; i !== 3; i++) {
      t[3 * i + 0] = vector.x * e[3 * i + 0];
      t[3 * i + 1] = vector.y * e[3 * i + 1];
      t[3 * i + 2] = vector.z * e[3 * i + 2];
    }

    return target;
  }
  /**
   * Solve Ax=b
   * @param b The right hand side
   * @param target Optional. Target vector to save in.
   * @return The solution x
   * @todo should reuse arrays
   */


  solve(b, target) {
    if (target === void 0) {
      target = new Vec3$1();
    }

    // Construct equations
    const nr = 3; // num rows

    const nc = 4; // num cols

    const eqns = [];
    let i;
    let j;

    for (i = 0; i < nr * nc; i++) {
      eqns.push(0);
    }

    for (i = 0; i < 3; i++) {
      for (j = 0; j < 3; j++) {
        eqns[i + nc * j] = this.elements[i + 3 * j];
      }
    }

    eqns[3 + 4 * 0] = b.x;
    eqns[3 + 4 * 1] = b.y;
    eqns[3 + 4 * 2] = b.z; // Compute right upper triangular version of the matrix - Gauss elimination

    let n = 3;
    const k = n;
    let np;
    const kp = 4; // num rows

    let p;

    do {
      i = k - n;

      if (eqns[i + nc * i] === 0) {
        // the pivot is null, swap lines
        for (j = i + 1; j < k; j++) {
          if (eqns[i + nc * j] !== 0) {
            np = kp;

            do {
              // do ligne( i ) = ligne( i ) + ligne( k )
              p = kp - np;
              eqns[p + nc * i] += eqns[p + nc * j];
            } while (--np);

            break;
          }
        }
      }

      if (eqns[i + nc * i] !== 0) {
        for (j = i + 1; j < k; j++) {
          const multiplier = eqns[i + nc * j] / eqns[i + nc * i];
          np = kp;

          do {
            // do ligne( k ) = ligne( k ) - multiplier * ligne( i )
            p = kp - np;
            eqns[p + nc * j] = p <= i ? 0 : eqns[p + nc * j] - eqns[p + nc * i] * multiplier;
          } while (--np);
        }
      }
    } while (--n); // Get the solution


    target.z = eqns[2 * nc + 3] / eqns[2 * nc + 2];
    target.y = (eqns[1 * nc + 3] - eqns[1 * nc + 2] * target.z) / eqns[1 * nc + 1];
    target.x = (eqns[0 * nc + 3] - eqns[0 * nc + 2] * target.z - eqns[0 * nc + 1] * target.y) / eqns[0 * nc + 0];

    if (isNaN(target.x) || isNaN(target.y) || isNaN(target.z) || target.x === Infinity || target.y === Infinity || target.z === Infinity) {
      throw `Could not solve equation! Got x=[${target.toString()}], b=[${b.toString()}], A=[${this.toString()}]`;
    }

    return target;
  }
  /**
   * Get an element in the matrix by index. Index starts at 0, not 1!!!
   * @param value If provided, the matrix element will be set to this value.
   */


  e(row, column, value) {
    if (value === undefined) {
      return this.elements[column + 3 * row];
    } else {
      // Set value
      this.elements[column + 3 * row] = value;
    }
  }
  /**
   * Copy another matrix into this matrix object.
   */


  copy(matrix) {
    for (let i = 0; i < matrix.elements.length; i++) {
      this.elements[i] = matrix.elements[i];
    }

    return this;
  }
  /**
   * Returns a string representation of the matrix.
   */


  toString() {
    let r = '';
    const sep = ',';

    for (let i = 0; i < 9; i++) {
      r += this.elements[i] + sep;
    }

    return r;
  }
  /**
   * reverse the matrix
   * @param target Target matrix to save in.
   * @return The solution x
   */


  reverse(target) {
    if (target === void 0) {
      target = new Mat3$1();
    }

    // Construct equations
    const nr = 3; // num rows

    const nc = 6; // num cols

    const eqns = reverse_eqns$1;
    let i;
    let j;

    for (i = 0; i < 3; i++) {
      for (j = 0; j < 3; j++) {
        eqns[i + nc * j] = this.elements[i + 3 * j];
      }
    }

    eqns[3 + 6 * 0] = 1;
    eqns[3 + 6 * 1] = 0;
    eqns[3 + 6 * 2] = 0;
    eqns[4 + 6 * 0] = 0;
    eqns[4 + 6 * 1] = 1;
    eqns[4 + 6 * 2] = 0;
    eqns[5 + 6 * 0] = 0;
    eqns[5 + 6 * 1] = 0;
    eqns[5 + 6 * 2] = 1; // Compute right upper triangular version of the matrix - Gauss elimination

    let n = 3;
    const k = n;
    let np;
    const kp = nc; // num rows

    let p;

    do {
      i = k - n;

      if (eqns[i + nc * i] === 0) {
        // the pivot is null, swap lines
        for (j = i + 1; j < k; j++) {
          if (eqns[i + nc * j] !== 0) {
            np = kp;

            do {
              // do line( i ) = line( i ) + line( k )
              p = kp - np;
              eqns[p + nc * i] += eqns[p + nc * j];
            } while (--np);

            break;
          }
        }
      }

      if (eqns[i + nc * i] !== 0) {
        for (j = i + 1; j < k; j++) {
          const multiplier = eqns[i + nc * j] / eqns[i + nc * i];
          np = kp;

          do {
            // do line( k ) = line( k ) - multiplier * line( i )
            p = kp - np;
            eqns[p + nc * j] = p <= i ? 0 : eqns[p + nc * j] - eqns[p + nc * i] * multiplier;
          } while (--np);
        }
      }
    } while (--n); // eliminate the upper left triangle of the matrix


    i = 2;

    do {
      j = i - 1;

      do {
        const multiplier = eqns[i + nc * j] / eqns[i + nc * i];
        np = nc;

        do {
          p = nc - np;
          eqns[p + nc * j] = eqns[p + nc * j] - eqns[p + nc * i] * multiplier;
        } while (--np);
      } while (j--);
    } while (--i); // operations on the diagonal


    i = 2;

    do {
      const multiplier = 1 / eqns[i + nc * i];
      np = nc;

      do {
        p = nc - np;
        eqns[p + nc * i] = eqns[p + nc * i] * multiplier;
      } while (--np);
    } while (i--);

    i = 2;

    do {
      j = 2;

      do {
        p = eqns[nr + j + nc * i];

        if (isNaN(p) || p === Infinity) {
          throw `Could not reverse! A=[${this.toString()}]`;
        }

        target.e(i, j, p);
      } while (j--);
    } while (i--);

    return target;
  }
  /**
   * Set the matrix from a quaterion
   */


  setRotationFromQuaternion(q) {
    const x = q.x;
    const y = q.y;
    const z = q.z;
    const w = q.w;
    const x2 = x + x;
    const y2 = y + y;
    const z2 = z + z;
    const xx = x * x2;
    const xy = x * y2;
    const xz = x * z2;
    const yy = y * y2;
    const yz = y * z2;
    const zz = z * z2;
    const wx = w * x2;
    const wy = w * y2;
    const wz = w * z2;
    const e = this.elements;
    e[3 * 0 + 0] = 1 - (yy + zz);
    e[3 * 0 + 1] = xy - wz;
    e[3 * 0 + 2] = xz + wy;
    e[3 * 1 + 0] = xy + wz;
    e[3 * 1 + 1] = 1 - (xx + zz);
    e[3 * 1 + 2] = yz - wx;
    e[3 * 2 + 0] = xz - wy;
    e[3 * 2 + 1] = yz + wx;
    e[3 * 2 + 2] = 1 - (xx + yy);
    return this;
  }
  /**
   * Transpose the matrix
   * @param target Optional. Where to store the result.
   * @return The target Mat3, or a new Mat3 if target was omitted.
   */


  transpose(target) {
    if (target === void 0) {
      target = new Mat3$1();
    }

    const M = this.elements;
    const T = target.elements;
    let tmp; //Set diagonals

    T[0] = M[0];
    T[4] = M[4];
    T[8] = M[8];
    tmp = M[1];
    T[1] = M[3];
    T[3] = tmp;
    tmp = M[2];
    T[2] = M[6];
    T[6] = tmp;
    tmp = M[5];
    T[5] = M[7];
    T[7] = tmp;
    return target;
  }

}
const reverse_eqns$1 = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];

/**
 * 3-dimensional vector
 * @example
 *     const v = new Vec3(1, 2, 3)
 *     console.log('x=' + v.x) // x=1
 */

class Vec3$1 {
  constructor(x, y, z) {
    if (x === void 0) {
      x = 0.0;
    }

    if (y === void 0) {
      y = 0.0;
    }

    if (z === void 0) {
      z = 0.0;
    }

    this.x = x;
    this.y = y;
    this.z = z;
  }
  /**
   * Vector cross product
   * @param target Optional target to save in.
   */


  cross(vector, target) {
    if (target === void 0) {
      target = new Vec3$1();
    }

    const vx = vector.x;
    const vy = vector.y;
    const vz = vector.z;
    const x = this.x;
    const y = this.y;
    const z = this.z;
    target.x = y * vz - z * vy;
    target.y = z * vx - x * vz;
    target.z = x * vy - y * vx;
    return target;
  }
  /**
   * Set the vectors' 3 elements
   */


  set(x, y, z) {
    this.x = x;
    this.y = y;
    this.z = z;
    return this;
  }
  /**
   * Set all components of the vector to zero.
   */


  setZero() {
    this.x = this.y = this.z = 0;
  }
  /**
   * Vector addition
   */


  vadd(vector, target) {
    if (target) {
      target.x = vector.x + this.x;
      target.y = vector.y + this.y;
      target.z = vector.z + this.z;
    } else {
      return new Vec3$1(this.x + vector.x, this.y + vector.y, this.z + vector.z);
    }
  }
  /**
   * Vector subtraction
   * @param target Optional target to save in.
   */


  vsub(vector, target) {
    if (target) {
      target.x = this.x - vector.x;
      target.y = this.y - vector.y;
      target.z = this.z - vector.z;
    } else {
      return new Vec3$1(this.x - vector.x, this.y - vector.y, this.z - vector.z);
    }
  }
  /**
   * Get the cross product matrix a_cross from a vector, such that a x b = a_cross * b = c
   *
   * See {@link https://www8.cs.umu.se/kurser/TDBD24/VT06/lectures/Lecture6.pdf Umeå University Lecture}
   */


  crossmat() {
    return new Mat3$1([0, -this.z, this.y, this.z, 0, -this.x, -this.y, this.x, 0]);
  }
  /**
   * Normalize the vector. Note that this changes the values in the vector.
    * @return Returns the norm of the vector
   */


  normalize() {
    const x = this.x;
    const y = this.y;
    const z = this.z;
    const n = Math.sqrt(x * x + y * y + z * z);

    if (n > 0.0) {
      const invN = 1 / n;
      this.x *= invN;
      this.y *= invN;
      this.z *= invN;
    } else {
      // Make something up
      this.x = 0;
      this.y = 0;
      this.z = 0;
    }

    return n;
  }
  /**
   * Get the version of this vector that is of length 1.
   * @param target Optional target to save in
   * @return Returns the unit vector
   */


  unit(target) {
    if (target === void 0) {
      target = new Vec3$1();
    }

    const x = this.x;
    const y = this.y;
    const z = this.z;
    let ninv = Math.sqrt(x * x + y * y + z * z);

    if (ninv > 0.0) {
      ninv = 1.0 / ninv;
      target.x = x * ninv;
      target.y = y * ninv;
      target.z = z * ninv;
    } else {
      target.x = 1;
      target.y = 0;
      target.z = 0;
    }

    return target;
  }
  /**
   * Get the length of the vector
   */


  length() {
    const x = this.x;
    const y = this.y;
    const z = this.z;
    return Math.sqrt(x * x + y * y + z * z);
  }
  /**
   * Get the squared length of the vector.
   */


  lengthSquared() {
    return this.dot(this);
  }
  /**
   * Get distance from this point to another point
   */


  distanceTo(p) {
    const x = this.x;
    const y = this.y;
    const z = this.z;
    const px = p.x;
    const py = p.y;
    const pz = p.z;
    return Math.sqrt((px - x) * (px - x) + (py - y) * (py - y) + (pz - z) * (pz - z));
  }
  /**
   * Get squared distance from this point to another point
   */


  distanceSquared(p) {
    const x = this.x;
    const y = this.y;
    const z = this.z;
    const px = p.x;
    const py = p.y;
    const pz = p.z;
    return (px - x) * (px - x) + (py - y) * (py - y) + (pz - z) * (pz - z);
  }
  /**
   * Multiply all the components of the vector with a scalar.
   * @param target The vector to save the result in.
   */


  scale(scalar, target) {
    if (target === void 0) {
      target = new Vec3$1();
    }

    const x = this.x;
    const y = this.y;
    const z = this.z;
    target.x = scalar * x;
    target.y = scalar * y;
    target.z = scalar * z;
    return target;
  }
  /**
   * Multiply the vector with an other vector, component-wise.
   * @param target The vector to save the result in.
   */


  vmul(vector, target) {
    if (target === void 0) {
      target = new Vec3$1();
    }

    target.x = vector.x * this.x;
    target.y = vector.y * this.y;
    target.z = vector.z * this.z;
    return target;
  }
  /**
   * Scale a vector and add it to this vector. Save the result in "target". (target = this + vector * scalar)
   * @param target The vector to save the result in.
   */


  addScaledVector(scalar, vector, target) {
    if (target === void 0) {
      target = new Vec3$1();
    }

    target.x = this.x + scalar * vector.x;
    target.y = this.y + scalar * vector.y;
    target.z = this.z + scalar * vector.z;
    return target;
  }
  /**
   * Calculate dot product
   * @param vector
   */


  dot(vector) {
    return this.x * vector.x + this.y * vector.y + this.z * vector.z;
  }

  isZero() {
    return this.x === 0 && this.y === 0 && this.z === 0;
  }
  /**
   * Make the vector point in the opposite direction.
   * @param target Optional target to save in
   */


  negate(target) {
    if (target === void 0) {
      target = new Vec3$1();
    }

    target.x = -this.x;
    target.y = -this.y;
    target.z = -this.z;
    return target;
  }
  /**
   * Compute two artificial tangents to the vector
   * @param t1 Vector object to save the first tangent in
   * @param t2 Vector object to save the second tangent in
   */


  tangents(t1, t2) {
    const norm = this.length();

    if (norm > 0.0) {
      const n = Vec3_tangents_n$1;
      const inorm = 1 / norm;
      n.set(this.x * inorm, this.y * inorm, this.z * inorm);
      const randVec = Vec3_tangents_randVec$1;

      if (Math.abs(n.x) < 0.9) {
        randVec.set(1, 0, 0);
        n.cross(randVec, t1);
      } else {
        randVec.set(0, 1, 0);
        n.cross(randVec, t1);
      }

      n.cross(t1, t2);
    } else {
      // The normal length is zero, make something up
      t1.set(1, 0, 0);
      t2.set(0, 1, 0);
    }
  }
  /**
   * Converts to a more readable format
   */


  toString() {
    return `${this.x},${this.y},${this.z}`;
  }
  /**
   * Converts to an array
   */


  toArray() {
    return [this.x, this.y, this.z];
  }
  /**
   * Copies value of source to this vector.
   */


  copy(vector) {
    this.x = vector.x;
    this.y = vector.y;
    this.z = vector.z;
    return this;
  }
  /**
   * Do a linear interpolation between two vectors
   * @param t A number between 0 and 1. 0 will make this function return u, and 1 will make it return v. Numbers in between will generate a vector in between them.
   */


  lerp(vector, t, target) {
    const x = this.x;
    const y = this.y;
    const z = this.z;
    target.x = x + (vector.x - x) * t;
    target.y = y + (vector.y - y) * t;
    target.z = z + (vector.z - z) * t;
  }
  /**
   * Check if a vector equals is almost equal to another one.
   */


  almostEquals(vector, precision) {
    if (precision === void 0) {
      precision = 1e-6;
    }

    if (Math.abs(this.x - vector.x) > precision || Math.abs(this.y - vector.y) > precision || Math.abs(this.z - vector.z) > precision) {
      return false;
    }

    return true;
  }
  /**
   * Check if a vector is almost zero
   */


  almostZero(precision) {
    if (precision === void 0) {
      precision = 1e-6;
    }

    if (Math.abs(this.x) > precision || Math.abs(this.y) > precision || Math.abs(this.z) > precision) {
      return false;
    }

    return true;
  }
  /**
   * Check if the vector is anti-parallel to another vector.
   * @param precision Set to zero for exact comparisons
   */


  isAntiparallelTo(vector, precision) {
    this.negate(antip_neg$1);
    return antip_neg$1.almostEquals(vector, precision);
  }
  /**
   * Clone the vector
   */


  clone() {
    return new Vec3$1(this.x, this.y, this.z);
  }

}
Vec3$1.ZERO = new Vec3$1(0, 0, 0);
Vec3$1.UNIT_X = new Vec3$1(1, 0, 0);
Vec3$1.UNIT_Y = new Vec3$1(0, 1, 0);
Vec3$1.UNIT_Z = new Vec3$1(0, 0, 1);
const Vec3_tangents_n$1 = new Vec3$1();
const Vec3_tangents_randVec$1 = new Vec3$1();
const antip_neg$1 = new Vec3$1();

/**
 * Axis aligned bounding box class.
 */
class AABB$1 {
  /**
   * The lower bound of the bounding box
   */

  /**
   * The upper bound of the bounding box
   */
  constructor(options) {
    if (options === void 0) {
      options = {};
    }

    this.lowerBound = new Vec3$1();
    this.upperBound = new Vec3$1();

    if (options.lowerBound) {
      this.lowerBound.copy(options.lowerBound);
    }

    if (options.upperBound) {
      this.upperBound.copy(options.upperBound);
    }
  }
  /**
   * Set the AABB bounds from a set of points.
   * @param points An array of Vec3's.
   * @return The self object
   */


  setFromPoints(points, position, quaternion, skinSize) {
    const l = this.lowerBound;
    const u = this.upperBound;
    const q = quaternion; // Set to the first point

    l.copy(points[0]);

    if (q) {
      q.vmult(l, l);
    }

    u.copy(l);

    for (let i = 1; i < points.length; i++) {
      let p = points[i];

      if (q) {
        q.vmult(p, tmp$1$1);
        p = tmp$1$1;
      }

      if (p.x > u.x) {
        u.x = p.x;
      }

      if (p.x < l.x) {
        l.x = p.x;
      }

      if (p.y > u.y) {
        u.y = p.y;
      }

      if (p.y < l.y) {
        l.y = p.y;
      }

      if (p.z > u.z) {
        u.z = p.z;
      }

      if (p.z < l.z) {
        l.z = p.z;
      }
    } // Add offset


    if (position) {
      position.vadd(l, l);
      position.vadd(u, u);
    }

    if (skinSize) {
      l.x -= skinSize;
      l.y -= skinSize;
      l.z -= skinSize;
      u.x += skinSize;
      u.y += skinSize;
      u.z += skinSize;
    }

    return this;
  }
  /**
   * Copy bounds from an AABB to this AABB
   * @param aabb Source to copy from
   * @return The this object, for chainability
   */


  copy(aabb) {
    this.lowerBound.copy(aabb.lowerBound);
    this.upperBound.copy(aabb.upperBound);
    return this;
  }
  /**
   * Clone an AABB
   */


  clone() {
    return new AABB$1().copy(this);
  }
  /**
   * Extend this AABB so that it covers the given AABB too.
   */


  extend(aabb) {
    this.lowerBound.x = Math.min(this.lowerBound.x, aabb.lowerBound.x);
    this.upperBound.x = Math.max(this.upperBound.x, aabb.upperBound.x);
    this.lowerBound.y = Math.min(this.lowerBound.y, aabb.lowerBound.y);
    this.upperBound.y = Math.max(this.upperBound.y, aabb.upperBound.y);
    this.lowerBound.z = Math.min(this.lowerBound.z, aabb.lowerBound.z);
    this.upperBound.z = Math.max(this.upperBound.z, aabb.upperBound.z);
  }
  /**
   * Returns true if the given AABB overlaps this AABB.
   */


  overlaps(aabb) {
    const l1 = this.lowerBound;
    const u1 = this.upperBound;
    const l2 = aabb.lowerBound;
    const u2 = aabb.upperBound; //      l2        u2
    //      |---------|
    // |--------|
    // l1       u1

    const overlapsX = l2.x <= u1.x && u1.x <= u2.x || l1.x <= u2.x && u2.x <= u1.x;
    const overlapsY = l2.y <= u1.y && u1.y <= u2.y || l1.y <= u2.y && u2.y <= u1.y;
    const overlapsZ = l2.z <= u1.z && u1.z <= u2.z || l1.z <= u2.z && u2.z <= u1.z;
    return overlapsX && overlapsY && overlapsZ;
  } // Mostly for debugging


  volume() {
    const l = this.lowerBound;
    const u = this.upperBound;
    return (u.x - l.x) * (u.y - l.y) * (u.z - l.z);
  }
  /**
   * Returns true if the given AABB is fully contained in this AABB.
   */


  contains(aabb) {
    const l1 = this.lowerBound;
    const u1 = this.upperBound;
    const l2 = aabb.lowerBound;
    const u2 = aabb.upperBound; //      l2        u2
    //      |---------|
    // |---------------|
    // l1              u1

    return l1.x <= l2.x && u1.x >= u2.x && l1.y <= l2.y && u1.y >= u2.y && l1.z <= l2.z && u1.z >= u2.z;
  }

  getCorners(a, b, c, d, e, f, g, h) {
    const l = this.lowerBound;
    const u = this.upperBound;
    a.copy(l);
    b.set(u.x, l.y, l.z);
    c.set(u.x, u.y, l.z);
    d.set(l.x, u.y, u.z);
    e.set(u.x, l.y, u.z);
    f.set(l.x, u.y, l.z);
    g.set(l.x, l.y, u.z);
    h.copy(u);
  }
  /**
   * Get the representation of an AABB in another frame.
   * @return The "target" AABB object.
   */


  toLocalFrame(frame, target) {
    const corners = transformIntoFrame_corners$1;
    const a = corners[0];
    const b = corners[1];
    const c = corners[2];
    const d = corners[3];
    const e = corners[4];
    const f = corners[5];
    const g = corners[6];
    const h = corners[7]; // Get corners in current frame

    this.getCorners(a, b, c, d, e, f, g, h); // Transform them to new local frame

    for (let i = 0; i !== 8; i++) {
      const corner = corners[i];
      frame.pointToLocal(corner, corner);
    }

    return target.setFromPoints(corners);
  }
  /**
   * Get the representation of an AABB in the global frame.
   * @return The "target" AABB object.
   */


  toWorldFrame(frame, target) {
    const corners = transformIntoFrame_corners$1;
    const a = corners[0];
    const b = corners[1];
    const c = corners[2];
    const d = corners[3];
    const e = corners[4];
    const f = corners[5];
    const g = corners[6];
    const h = corners[7]; // Get corners in current frame

    this.getCorners(a, b, c, d, e, f, g, h); // Transform them to new local frame

    for (let i = 0; i !== 8; i++) {
      const corner = corners[i];
      frame.pointToWorld(corner, corner);
    }

    return target.setFromPoints(corners);
  }
  /**
   * Check if the AABB is hit by a ray.
   */


  overlapsRay(ray) {
    const {
      direction,
      from
    } = ray; // const t = 0
    // ray.direction is unit direction vector of ray

    const dirFracX = 1 / direction.x;
    const dirFracY = 1 / direction.y;
    const dirFracZ = 1 / direction.z; // this.lowerBound is the corner of AABB with minimal coordinates - left bottom, rt is maximal corner

    const t1 = (this.lowerBound.x - from.x) * dirFracX;
    const t2 = (this.upperBound.x - from.x) * dirFracX;
    const t3 = (this.lowerBound.y - from.y) * dirFracY;
    const t4 = (this.upperBound.y - from.y) * dirFracY;
    const t5 = (this.lowerBound.z - from.z) * dirFracZ;
    const t6 = (this.upperBound.z - from.z) * dirFracZ; // const tmin = Math.max(Math.max(Math.min(t1, t2), Math.min(t3, t4)));
    // const tmax = Math.min(Math.min(Math.max(t1, t2), Math.max(t3, t4)));

    const tmin = Math.max(Math.max(Math.min(t1, t2), Math.min(t3, t4)), Math.min(t5, t6));
    const tmax = Math.min(Math.min(Math.max(t1, t2), Math.max(t3, t4)), Math.max(t5, t6)); // if tmax < 0, ray (line) is intersecting AABB, but whole AABB is behing us

    if (tmax < 0) {
      //t = tmax;
      return false;
    } // if tmin > tmax, ray doesn't intersect AABB


    if (tmin > tmax) {
      //t = tmax;
      return false;
    }

    return true;
  }

}
const tmp$1$1 = new Vec3$1();
const transformIntoFrame_corners$1 = [new Vec3$1(), new Vec3$1(), new Vec3$1(), new Vec3$1(), new Vec3$1(), new Vec3$1(), new Vec3$1(), new Vec3$1()];

/**
 * Base class for objects that dispatches events.
 */
class EventTarget {
  /**
   * Add an event listener
   * @return The self object, for chainability.
   */
  addEventListener(type, listener) {
    if (this._listeners === undefined) {
      this._listeners = {};
    }

    const listeners = this._listeners;

    if (listeners[type] === undefined) {
      listeners[type] = [];
    }

    if (!listeners[type].includes(listener)) {
      listeners[type].push(listener);
    }

    return this;
  }
  /**
   * Check if an event listener is added
   */


  hasEventListener(type, listener) {
    if (this._listeners === undefined) {
      return false;
    }

    const listeners = this._listeners;

    if (listeners[type] !== undefined && listeners[type].includes(listener)) {
      return true;
    }

    return false;
  }
  /**
   * Check if any event listener of the given type is added
   */


  hasAnyEventListener(type) {
    if (this._listeners === undefined) {
      return false;
    }

    const listeners = this._listeners;
    return listeners[type] !== undefined;
  }
  /**
   * Remove an event listener
   * @return The self object, for chainability.
   */


  removeEventListener(type, listener) {
    if (this._listeners === undefined) {
      return this;
    }

    const listeners = this._listeners;

    if (listeners[type] === undefined) {
      return this;
    }

    const index = listeners[type].indexOf(listener);

    if (index !== -1) {
      listeners[type].splice(index, 1);
    }

    return this;
  }
  /**
   * Emit an event.
   * @return The self object, for chainability.
   */


  dispatchEvent(event) {
    if (this._listeners === undefined) {
      return this;
    }

    const listeners = this._listeners;
    const listenerArray = listeners[event.type];

    if (listenerArray !== undefined) {
      event.target = this;

      for (let i = 0, l = listenerArray.length; i < l; i++) {
        listenerArray[i].call(this, event);
      }
    }

    return this;
  }

}

/**
 * A Quaternion describes a rotation in 3D space. The Quaternion is mathematically defined as Q = x*i + y*j + z*k + w, where (i,j,k) are imaginary basis vectors. (x,y,z) can be seen as a vector related to the axis of rotation, while the real multiplier, w, is related to the amount of rotation.
 * @param x Multiplier of the imaginary basis vector i.
 * @param y Multiplier of the imaginary basis vector j.
 * @param z Multiplier of the imaginary basis vector k.
 * @param w Multiplier of the real part.
 * @see http://en.wikipedia.org/wiki/Quaternion
 */

class Quaternion$1 {
  constructor(x, y, z, w) {
    if (x === void 0) {
      x = 0;
    }

    if (y === void 0) {
      y = 0;
    }

    if (z === void 0) {
      z = 0;
    }

    if (w === void 0) {
      w = 1;
    }

    this.x = x;
    this.y = y;
    this.z = z;
    this.w = w;
  }
  /**
   * Set the value of the quaternion.
   */


  set(x, y, z, w) {
    this.x = x;
    this.y = y;
    this.z = z;
    this.w = w;
    return this;
  }
  /**
   * Convert to a readable format
   * @return "x,y,z,w"
   */


  toString() {
    return `${this.x},${this.y},${this.z},${this.w}`;
  }
  /**
   * Convert to an Array
   * @return [x, y, z, w]
   */


  toArray() {
    return [this.x, this.y, this.z, this.w];
  }
  /**
   * Set the quaternion components given an axis and an angle in radians.
   */


  setFromAxisAngle(vector, angle) {
    const s = Math.sin(angle * 0.5);
    this.x = vector.x * s;
    this.y = vector.y * s;
    this.z = vector.z * s;
    this.w = Math.cos(angle * 0.5);
    return this;
  }
  /**
   * Converts the quaternion to [ axis, angle ] representation.
   * @param targetAxis A vector object to reuse for storing the axis.
   * @return An array, first element is the axis and the second is the angle in radians.
   */


  toAxisAngle(targetAxis) {
    if (targetAxis === void 0) {
      targetAxis = new Vec3$1();
    }

    this.normalize(); // if w>1 acos and sqrt will produce errors, this cant happen if quaternion is normalised

    const angle = 2 * Math.acos(this.w);
    const s = Math.sqrt(1 - this.w * this.w); // assuming quaternion normalised then w is less than 1, so term always positive.

    if (s < 0.001) {
      // test to avoid divide by zero, s is always positive due to sqrt
      // if s close to zero then direction of axis not important
      targetAxis.x = this.x; // if it is important that axis is normalised then replace with x=1; y=z=0;

      targetAxis.y = this.y;
      targetAxis.z = this.z;
    } else {
      targetAxis.x = this.x / s; // normalise axis

      targetAxis.y = this.y / s;
      targetAxis.z = this.z / s;
    }

    return [targetAxis, angle];
  }
  /**
   * Set the quaternion value given two vectors. The resulting rotation will be the needed rotation to rotate u to v.
   */


  setFromVectors(u, v) {
    if (u.isAntiparallelTo(v)) {
      const t1 = sfv_t1$1;
      const t2 = sfv_t2$1;
      u.tangents(t1, t2);
      this.setFromAxisAngle(t1, Math.PI);
    } else {
      const a = u.cross(v);
      this.x = a.x;
      this.y = a.y;
      this.z = a.z;
      this.w = Math.sqrt(u.length() ** 2 * v.length() ** 2) + u.dot(v);
      this.normalize();
    }

    return this;
  }
  /**
   * Multiply the quaternion with an other quaternion.
   */


  mult(quat, target) {
    if (target === void 0) {
      target = new Quaternion$1();
    }

    const ax = this.x;
    const ay = this.y;
    const az = this.z;
    const aw = this.w;
    const bx = quat.x;
    const by = quat.y;
    const bz = quat.z;
    const bw = quat.w;
    target.x = ax * bw + aw * bx + ay * bz - az * by;
    target.y = ay * bw + aw * by + az * bx - ax * bz;
    target.z = az * bw + aw * bz + ax * by - ay * bx;
    target.w = aw * bw - ax * bx - ay * by - az * bz;
    return target;
  }
  /**
   * Get the inverse quaternion rotation.
   */


  inverse(target) {
    if (target === void 0) {
      target = new Quaternion$1();
    }

    const x = this.x;
    const y = this.y;
    const z = this.z;
    const w = this.w;
    this.conjugate(target);
    const inorm2 = 1 / (x * x + y * y + z * z + w * w);
    target.x *= inorm2;
    target.y *= inorm2;
    target.z *= inorm2;
    target.w *= inorm2;
    return target;
  }
  /**
   * Get the quaternion conjugate
   */


  conjugate(target) {
    if (target === void 0) {
      target = new Quaternion$1();
    }

    target.x = -this.x;
    target.y = -this.y;
    target.z = -this.z;
    target.w = this.w;
    return target;
  }
  /**
   * Normalize the quaternion. Note that this changes the values of the quaternion.
   */


  normalize() {
    let l = Math.sqrt(this.x * this.x + this.y * this.y + this.z * this.z + this.w * this.w);

    if (l === 0) {
      this.x = 0;
      this.y = 0;
      this.z = 0;
      this.w = 0;
    } else {
      l = 1 / l;
      this.x *= l;
      this.y *= l;
      this.z *= l;
      this.w *= l;
    }

    return this;
  }
  /**
   * Approximation of quaternion normalization. Works best when quat is already almost-normalized.
   * @author unphased, https://github.com/unphased
   */


  normalizeFast() {
    const f = (3.0 - (this.x * this.x + this.y * this.y + this.z * this.z + this.w * this.w)) / 2.0;

    if (f === 0) {
      this.x = 0;
      this.y = 0;
      this.z = 0;
      this.w = 0;
    } else {
      this.x *= f;
      this.y *= f;
      this.z *= f;
      this.w *= f;
    }

    return this;
  }
  /**
   * Multiply the quaternion by a vector
   */


  vmult(v, target) {
    if (target === void 0) {
      target = new Vec3$1();
    }

    const x = v.x;
    const y = v.y;
    const z = v.z;
    const qx = this.x;
    const qy = this.y;
    const qz = this.z;
    const qw = this.w; // q*v

    const ix = qw * x + qy * z - qz * y;
    const iy = qw * y + qz * x - qx * z;
    const iz = qw * z + qx * y - qy * x;
    const iw = -qx * x - qy * y - qz * z;
    target.x = ix * qw + iw * -qx + iy * -qz - iz * -qy;
    target.y = iy * qw + iw * -qy + iz * -qx - ix * -qz;
    target.z = iz * qw + iw * -qz + ix * -qy - iy * -qx;
    return target;
  }
  /**
   * Copies value of source to this quaternion.
   * @return this
   */


  copy(quat) {
    this.x = quat.x;
    this.y = quat.y;
    this.z = quat.z;
    this.w = quat.w;
    return this;
  }
  /**
   * Convert the quaternion to euler angle representation. Order: YZX, as this page describes: https://www.euclideanspace.com/maths/standards/index.htm
   * @param order Three-character string, defaults to "YZX"
   */


  toEuler(target, order) {
    if (order === void 0) {
      order = 'YZX';
    }

    let heading;
    let attitude;
    let bank;
    const x = this.x;
    const y = this.y;
    const z = this.z;
    const w = this.w;

    switch (order) {
      case 'YZX':
        const test = x * y + z * w;

        if (test > 0.499) {
          // singularity at north pole
          heading = 2 * Math.atan2(x, w);
          attitude = Math.PI / 2;
          bank = 0;
        }

        if (test < -0.499) {
          // singularity at south pole
          heading = -2 * Math.atan2(x, w);
          attitude = -Math.PI / 2;
          bank = 0;
        }

        if (heading === undefined) {
          const sqx = x * x;
          const sqy = y * y;
          const sqz = z * z;
          heading = Math.atan2(2 * y * w - 2 * x * z, 1 - 2 * sqy - 2 * sqz); // Heading

          attitude = Math.asin(2 * test); // attitude

          bank = Math.atan2(2 * x * w - 2 * y * z, 1 - 2 * sqx - 2 * sqz); // bank
        }

        break;

      default:
        throw new Error(`Euler order ${order} not supported yet.`);
    }

    target.y = heading;
    target.z = attitude;
    target.x = bank;
  }
  /**
   * Set the quaternion components given Euler angle representation.
   *
   * @param order The order to apply angles: 'XYZ' or 'YXZ' or any other combination.
   *
   * See {@link https://www.mathworks.com/matlabcentral/fileexchange/20696-function-to-convert-between-dcm-euler-angles-quaternions-and-euler-vectors MathWorks} reference
   */


  setFromEuler(x, y, z, order) {
    if (order === void 0) {
      order = 'XYZ';
    }

    const c1 = Math.cos(x / 2);
    const c2 = Math.cos(y / 2);
    const c3 = Math.cos(z / 2);
    const s1 = Math.sin(x / 2);
    const s2 = Math.sin(y / 2);
    const s3 = Math.sin(z / 2);

    if (order === 'XYZ') {
      this.x = s1 * c2 * c3 + c1 * s2 * s3;
      this.y = c1 * s2 * c3 - s1 * c2 * s3;
      this.z = c1 * c2 * s3 + s1 * s2 * c3;
      this.w = c1 * c2 * c3 - s1 * s2 * s3;
    } else if (order === 'YXZ') {
      this.x = s1 * c2 * c3 + c1 * s2 * s3;
      this.y = c1 * s2 * c3 - s1 * c2 * s3;
      this.z = c1 * c2 * s3 - s1 * s2 * c3;
      this.w = c1 * c2 * c3 + s1 * s2 * s3;
    } else if (order === 'ZXY') {
      this.x = s1 * c2 * c3 - c1 * s2 * s3;
      this.y = c1 * s2 * c3 + s1 * c2 * s3;
      this.z = c1 * c2 * s3 + s1 * s2 * c3;
      this.w = c1 * c2 * c3 - s1 * s2 * s3;
    } else if (order === 'ZYX') {
      this.x = s1 * c2 * c3 - c1 * s2 * s3;
      this.y = c1 * s2 * c3 + s1 * c2 * s3;
      this.z = c1 * c2 * s3 - s1 * s2 * c3;
      this.w = c1 * c2 * c3 + s1 * s2 * s3;
    } else if (order === 'YZX') {
      this.x = s1 * c2 * c3 + c1 * s2 * s3;
      this.y = c1 * s2 * c3 + s1 * c2 * s3;
      this.z = c1 * c2 * s3 - s1 * s2 * c3;
      this.w = c1 * c2 * c3 - s1 * s2 * s3;
    } else if (order === 'XZY') {
      this.x = s1 * c2 * c3 - c1 * s2 * s3;
      this.y = c1 * s2 * c3 - s1 * c2 * s3;
      this.z = c1 * c2 * s3 + s1 * s2 * c3;
      this.w = c1 * c2 * c3 + s1 * s2 * s3;
    }

    return this;
  }

  clone() {
    return new Quaternion$1(this.x, this.y, this.z, this.w);
  }
  /**
   * Performs a spherical linear interpolation between two quat
   *
   * @param toQuat second operand
   * @param t interpolation amount between the self quaternion and toQuat
   * @param target A quaternion to store the result in. If not provided, a new one will be created.
   * @returns {Quaternion} The "target" object
   */


  slerp(toQuat, t, target) {
    if (target === void 0) {
      target = new Quaternion$1();
    }

    const ax = this.x;
    const ay = this.y;
    const az = this.z;
    const aw = this.w;
    let bx = toQuat.x;
    let by = toQuat.y;
    let bz = toQuat.z;
    let bw = toQuat.w;
    let omega;
    let cosom;
    let sinom;
    let scale0;
    let scale1; // calc cosine

    cosom = ax * bx + ay * by + az * bz + aw * bw; // adjust signs (if necessary)

    if (cosom < 0.0) {
      cosom = -cosom;
      bx = -bx;
      by = -by;
      bz = -bz;
      bw = -bw;
    } // calculate coefficients


    if (1.0 - cosom > 0.000001) {
      // standard case (slerp)
      omega = Math.acos(cosom);
      sinom = Math.sin(omega);
      scale0 = Math.sin((1.0 - t) * omega) / sinom;
      scale1 = Math.sin(t * omega) / sinom;
    } else {
      // "from" and "to" quaternions are very close
      //  ... so we can do a linear interpolation
      scale0 = 1.0 - t;
      scale1 = t;
    } // calculate final values


    target.x = scale0 * ax + scale1 * bx;
    target.y = scale0 * ay + scale1 * by;
    target.z = scale0 * az + scale1 * bz;
    target.w = scale0 * aw + scale1 * bw;
    return target;
  }
  /**
   * Rotate an absolute orientation quaternion given an angular velocity and a time step.
   */


  integrate(angularVelocity, dt, angularFactor, target) {
    if (target === void 0) {
      target = new Quaternion$1();
    }

    const ax = angularVelocity.x * angularFactor.x,
          ay = angularVelocity.y * angularFactor.y,
          az = angularVelocity.z * angularFactor.z,
          bx = this.x,
          by = this.y,
          bz = this.z,
          bw = this.w;
    const half_dt = dt * 0.5;
    target.x += half_dt * (ax * bw + ay * bz - az * by);
    target.y += half_dt * (ay * bw + az * bx - ax * bz);
    target.z += half_dt * (az * bw + ax * by - ay * bx);
    target.w += half_dt * (-ax * bx - ay * by - az * bz);
    return target;
  }

}
const sfv_t1$1 = new Vec3$1();
const sfv_t2$1 = new Vec3$1();

/**
 * The available shape types.
 */
const SHAPE_TYPES$1 = {
  /** SPHERE */
  SPHERE: 1,

  /** PLANE */
  PLANE: 2,

  /** BOX */
  BOX: 4,

  /** COMPOUND */
  COMPOUND: 8,

  /** CONVEXPOLYHEDRON */
  CONVEXPOLYHEDRON: 16,

  /** HEIGHTFIELD */
  HEIGHTFIELD: 32,

  /** PARTICLE */
  PARTICLE: 64,

  /** CYLINDER */
  CYLINDER: 128,

  /** TRIMESH */
  TRIMESH: 256
};
/**
 * ShapeType
 */

/**
 * Base class for shapes
 */
class Shape$1 {
  /**
   * Identifier of the Shape.
   */

  /**
   * The type of this shape. Must be set to an int > 0 by subclasses.
   */

  /**
   * The local bounding sphere radius of this shape.
   */

  /**
   * Whether to produce contact forces when in contact with other bodies. Note that contacts will be generated, but they will be disabled.
   * @default true
   */

  /**
   * @default 1
   */

  /**
   * @default -1
   */

  /**
   * Optional material of the shape that regulates contact properties.
   */

  /**
   * The body to which the shape is added to.
   */

  /**
   * All the Shape types.
   */
  constructor(options) {
    if (options === void 0) {
      options = {};
    }

    this.id = Shape$1.idCounter++;
    this.type = options.type || 0;
    this.boundingSphereRadius = 0;
    this.collisionResponse = options.collisionResponse ? options.collisionResponse : true;
    this.collisionFilterGroup = options.collisionFilterGroup !== undefined ? options.collisionFilterGroup : 1;
    this.collisionFilterMask = options.collisionFilterMask !== undefined ? options.collisionFilterMask : -1;
    this.material = options.material ? options.material : null;
    this.body = null;
  }
  /**
   * Computes the bounding sphere radius.
   * The result is stored in the property `.boundingSphereRadius`
   */


  updateBoundingSphereRadius() {
    throw `computeBoundingSphereRadius() not implemented for shape type ${this.type}`;
  }
  /**
   * Get the volume of this shape
   */


  volume() {
    throw `volume() not implemented for shape type ${this.type}`;
  }
  /**
   * Calculates the inertia in the local frame for this shape.
   * @see http://en.wikipedia.org/wiki/List_of_moments_of_inertia
   */


  calculateLocalInertia(mass, target) {
    throw `calculateLocalInertia() not implemented for shape type ${this.type}`;
  }
  /**
   * @todo use abstract for these kind of methods
   */


  calculateWorldAABB(pos, quat, min, max) {
    throw `calculateWorldAABB() not implemented for shape type ${this.type}`;
  }

}
Shape$1.idCounter = 0;
Shape$1.types = SHAPE_TYPES$1;

/**
 * Transformation utilities.
 */
class Transform$1 {
  /**
   * position
   */

  /**
   * quaternion
   */
  constructor(options) {
    if (options === void 0) {
      options = {};
    }

    this.position = new Vec3$1();
    this.quaternion = new Quaternion$1();

    if (options.position) {
      this.position.copy(options.position);
    }

    if (options.quaternion) {
      this.quaternion.copy(options.quaternion);
    }
  }
  /**
   * Get a global point in local transform coordinates.
   */


  pointToLocal(worldPoint, result) {
    return Transform$1.pointToLocalFrame(this.position, this.quaternion, worldPoint, result);
  }
  /**
   * Get a local point in global transform coordinates.
   */


  pointToWorld(localPoint, result) {
    return Transform$1.pointToWorldFrame(this.position, this.quaternion, localPoint, result);
  }
  /**
   * vectorToWorldFrame
   */


  vectorToWorldFrame(localVector, result) {
    if (result === void 0) {
      result = new Vec3$1();
    }

    this.quaternion.vmult(localVector, result);
    return result;
  }
  /**
   * pointToLocalFrame
   */


  static pointToLocalFrame(position, quaternion, worldPoint, result) {
    if (result === void 0) {
      result = new Vec3$1();
    }

    worldPoint.vsub(position, result);
    quaternion.conjugate(tmpQuat$1$1);
    tmpQuat$1$1.vmult(result, result);
    return result;
  }
  /**
   * pointToWorldFrame
   */


  static pointToWorldFrame(position, quaternion, localPoint, result) {
    if (result === void 0) {
      result = new Vec3$1();
    }

    quaternion.vmult(localPoint, result);
    result.vadd(position, result);
    return result;
  }
  /**
   * vectorToWorldFrame
   */


  static vectorToWorldFrame(quaternion, localVector, result) {
    if (result === void 0) {
      result = new Vec3$1();
    }

    quaternion.vmult(localVector, result);
    return result;
  }
  /**
   * vectorToLocalFrame
   */


  static vectorToLocalFrame(position, quaternion, worldVector, result) {
    if (result === void 0) {
      result = new Vec3$1();
    }

    quaternion.w *= -1;
    quaternion.vmult(worldVector, result);
    quaternion.w *= -1;
    return result;
  }

}
const tmpQuat$1$1 = new Quaternion$1();

/**
 * A set of polygons describing a convex shape.
 *
 * The shape MUST be convex for the code to work properly. No polygons may be coplanar (contained
 * in the same 3D plane), instead these should be merged into one polygon.
 *
 * @author qiao / https://github.com/qiao (original author, see https://github.com/qiao/three.js/commit/85026f0c769e4000148a67d45a9e9b9c5108836f)
 * @author schteppe / https://github.com/schteppe
 * @see https://www.altdevblogaday.com/2011/05/13/contact-generation-between-3d-convex-meshes/
 *
 * @todo Move the clipping functions to ContactGenerator?
 * @todo Automatically merge coplanar polygons in constructor.
 * @example
 *     const convexShape = new CANNON.ConvexPolyhedron({ vertices, faces })
 *     const convexBody = new CANNON.Body({ mass: 1, shape: convexShape })
 *     world.addBody(convexBody)
 */
class ConvexPolyhedron extends Shape$1 {
  /** vertices */

  /**
   * Array of integer arrays, indicating which vertices each face consists of
   */

  /** faceNormals */

  /** worldVertices */

  /** worldVerticesNeedsUpdate */

  /** worldFaceNormals */

  /** worldFaceNormalsNeedsUpdate */

  /**
   * If given, these locally defined, normalized axes are the only ones being checked when doing separating axis check.
   */

  /** uniqueEdges */

  /**
   * @param vertices An array of Vec3's
   * @param faces Array of integer arrays, describing which vertices that is included in each face.
   */
  constructor(props) {
    if (props === void 0) {
      props = {};
    }

    const {
      vertices = [],
      faces = [],
      normals = [],
      axes,
      boundingSphereRadius
    } = props;
    super({
      type: Shape$1.types.CONVEXPOLYHEDRON
    });
    this.vertices = vertices;
    this.faces = faces;
    this.faceNormals = normals;

    if (this.faceNormals.length === 0) {
      this.computeNormals();
    }

    if (!boundingSphereRadius) {
      this.updateBoundingSphereRadius();
    } else {
      this.boundingSphereRadius = boundingSphereRadius;
    }

    this.worldVertices = []; // World transformed version of .vertices

    this.worldVerticesNeedsUpdate = true;
    this.worldFaceNormals = []; // World transformed version of .faceNormals

    this.worldFaceNormalsNeedsUpdate = true;
    this.uniqueAxes = axes ? axes.slice() : null;
    this.uniqueEdges = [];
    this.computeEdges();
  }
  /**
   * Computes uniqueEdges
   */


  computeEdges() {
    const faces = this.faces;
    const vertices = this.vertices;
    const edges = this.uniqueEdges;
    edges.length = 0;
    const edge = new Vec3$1();

    for (let i = 0; i !== faces.length; i++) {
      const face = faces[i];
      const numVertices = face.length;

      for (let j = 0; j !== numVertices; j++) {
        const k = (j + 1) % numVertices;
        vertices[face[j]].vsub(vertices[face[k]], edge);
        edge.normalize();
        let found = false;

        for (let p = 0; p !== edges.length; p++) {
          if (edges[p].almostEquals(edge) || edges[p].almostEquals(edge)) {
            found = true;
            break;
          }
        }

        if (!found) {
          edges.push(edge.clone());
        }
      }
    }
  }
  /**
   * Compute the normals of the faces.
   * Will reuse existing Vec3 objects in the `faceNormals` array if they exist.
   */


  computeNormals() {
    this.faceNormals.length = this.faces.length; // Generate normals

    for (let i = 0; i < this.faces.length; i++) {
      // Check so all vertices exists for this face
      for (let j = 0; j < this.faces[i].length; j++) {
        if (!this.vertices[this.faces[i][j]]) {
          throw new Error(`Vertex ${this.faces[i][j]} not found!`);
        }
      }

      const n = this.faceNormals[i] || new Vec3$1();
      this.getFaceNormal(i, n);
      n.negate(n);
      this.faceNormals[i] = n;
      const vertex = this.vertices[this.faces[i][0]];

      if (n.dot(vertex) < 0) {
        console.error(`.faceNormals[${i}] = Vec3(${n.toString()}) looks like it points into the shape? The vertices follow. Make sure they are ordered CCW around the normal, using the right hand rule.`);

        for (let j = 0; j < this.faces[i].length; j++) {
          console.warn(`.vertices[${this.faces[i][j]}] = Vec3(${this.vertices[this.faces[i][j]].toString()})`);
        }
      }
    }
  }
  /**
   * Compute the normal of a face from its vertices
   */


  getFaceNormal(i, target) {
    const f = this.faces[i];
    const va = this.vertices[f[0]];
    const vb = this.vertices[f[1]];
    const vc = this.vertices[f[2]];
    ConvexPolyhedron.computeNormal(va, vb, vc, target);
  }
  /**
   * Get face normal given 3 vertices
   */


  static computeNormal(va, vb, vc, target) {
    const cb = new Vec3$1();
    const ab = new Vec3$1();
    vb.vsub(va, ab);
    vc.vsub(vb, cb);
    cb.cross(ab, target);

    if (!target.isZero()) {
      target.normalize();
    }
  }
  /**
   * @param minDist Clamp distance
   * @param result The an array of contact point objects, see clipFaceAgainstHull
   */


  clipAgainstHull(posA, quatA, hullB, posB, quatB, separatingNormal, minDist, maxDist, result) {
    const WorldNormal = new Vec3$1();
    let closestFaceB = -1;
    let dmax = -Number.MAX_VALUE;

    for (let face = 0; face < hullB.faces.length; face++) {
      WorldNormal.copy(hullB.faceNormals[face]);
      quatB.vmult(WorldNormal, WorldNormal);
      const d = WorldNormal.dot(separatingNormal);

      if (d > dmax) {
        dmax = d;
        closestFaceB = face;
      }
    }

    const worldVertsB1 = [];

    for (let i = 0; i < hullB.faces[closestFaceB].length; i++) {
      const b = hullB.vertices[hullB.faces[closestFaceB][i]];
      const worldb = new Vec3$1();
      worldb.copy(b);
      quatB.vmult(worldb, worldb);
      posB.vadd(worldb, worldb);
      worldVertsB1.push(worldb);
    }

    if (closestFaceB >= 0) {
      this.clipFaceAgainstHull(separatingNormal, posA, quatA, worldVertsB1, minDist, maxDist, result);
    }
  }
  /**
   * Find the separating axis between this hull and another
   * @param target The target vector to save the axis in
   * @return Returns false if a separation is found, else true
   */


  findSeparatingAxis(hullB, posA, quatA, posB, quatB, target, faceListA, faceListB) {
    const faceANormalWS3 = new Vec3$1();
    const Worldnormal1 = new Vec3$1();
    const deltaC = new Vec3$1();
    const worldEdge0 = new Vec3$1();
    const worldEdge1 = new Vec3$1();
    const Cross = new Vec3$1();
    let dmin = Number.MAX_VALUE;
    const hullA = this;

    if (!hullA.uniqueAxes) {
      const numFacesA = faceListA ? faceListA.length : hullA.faces.length; // Test face normals from hullA

      for (let i = 0; i < numFacesA; i++) {
        const fi = faceListA ? faceListA[i] : i; // Get world face normal

        faceANormalWS3.copy(hullA.faceNormals[fi]);
        quatA.vmult(faceANormalWS3, faceANormalWS3);
        const d = hullA.testSepAxis(faceANormalWS3, hullB, posA, quatA, posB, quatB);

        if (d === false) {
          return false;
        }

        if (d < dmin) {
          dmin = d;
          target.copy(faceANormalWS3);
        }
      }
    } else {
      // Test unique axes
      for (let i = 0; i !== hullA.uniqueAxes.length; i++) {
        // Get world axis
        quatA.vmult(hullA.uniqueAxes[i], faceANormalWS3);
        const d = hullA.testSepAxis(faceANormalWS3, hullB, posA, quatA, posB, quatB);

        if (d === false) {
          return false;
        }

        if (d < dmin) {
          dmin = d;
          target.copy(faceANormalWS3);
        }
      }
    }

    if (!hullB.uniqueAxes) {
      // Test face normals from hullB
      const numFacesB = faceListB ? faceListB.length : hullB.faces.length;

      for (let i = 0; i < numFacesB; i++) {
        const fi = faceListB ? faceListB[i] : i;
        Worldnormal1.copy(hullB.faceNormals[fi]);
        quatB.vmult(Worldnormal1, Worldnormal1);
        const d = hullA.testSepAxis(Worldnormal1, hullB, posA, quatA, posB, quatB);

        if (d === false) {
          return false;
        }

        if (d < dmin) {
          dmin = d;
          target.copy(Worldnormal1);
        }
      }
    } else {
      // Test unique axes in B
      for (let i = 0; i !== hullB.uniqueAxes.length; i++) {
        quatB.vmult(hullB.uniqueAxes[i], Worldnormal1);
        const d = hullA.testSepAxis(Worldnormal1, hullB, posA, quatA, posB, quatB);

        if (d === false) {
          return false;
        }

        if (d < dmin) {
          dmin = d;
          target.copy(Worldnormal1);
        }
      }
    } // Test edges


    for (let e0 = 0; e0 !== hullA.uniqueEdges.length; e0++) {
      // Get world edge
      quatA.vmult(hullA.uniqueEdges[e0], worldEdge0);

      for (let e1 = 0; e1 !== hullB.uniqueEdges.length; e1++) {
        // Get world edge 2
        quatB.vmult(hullB.uniqueEdges[e1], worldEdge1);
        worldEdge0.cross(worldEdge1, Cross);

        if (!Cross.almostZero()) {
          Cross.normalize();
          const dist = hullA.testSepAxis(Cross, hullB, posA, quatA, posB, quatB);

          if (dist === false) {
            return false;
          }

          if (dist < dmin) {
            dmin = dist;
            target.copy(Cross);
          }
        }
      }
    }

    posB.vsub(posA, deltaC);

    if (deltaC.dot(target) > 0.0) {
      target.negate(target);
    }

    return true;
  }
  /**
   * Test separating axis against two hulls. Both hulls are projected onto the axis and the overlap size is returned if there is one.
   * @return The overlap depth, or FALSE if no penetration.
   */


  testSepAxis(axis, hullB, posA, quatA, posB, quatB) {
    const hullA = this;
    ConvexPolyhedron.project(hullA, axis, posA, quatA, maxminA);
    ConvexPolyhedron.project(hullB, axis, posB, quatB, maxminB);
    const maxA = maxminA[0];
    const minA = maxminA[1];
    const maxB = maxminB[0];
    const minB = maxminB[1];

    if (maxA < minB || maxB < minA) {
      return false; // Separated
    }

    const d0 = maxA - minB;
    const d1 = maxB - minA;
    const depth = d0 < d1 ? d0 : d1;
    return depth;
  }
  /**
   * calculateLocalInertia
   */


  calculateLocalInertia(mass, target) {
    // Approximate with box inertia
    // Exact inertia calculation is overkill, but see http://geometrictools.com/Documentation/PolyhedralMassProperties.pdf for the correct way to do it
    const aabbmax = new Vec3$1();
    const aabbmin = new Vec3$1();
    this.computeLocalAABB(aabbmin, aabbmax);
    const x = aabbmax.x - aabbmin.x;
    const y = aabbmax.y - aabbmin.y;
    const z = aabbmax.z - aabbmin.z;
    target.x = 1.0 / 12.0 * mass * (2 * y * 2 * y + 2 * z * 2 * z);
    target.y = 1.0 / 12.0 * mass * (2 * x * 2 * x + 2 * z * 2 * z);
    target.z = 1.0 / 12.0 * mass * (2 * y * 2 * y + 2 * x * 2 * x);
  }
  /**
   * @param face_i Index of the face
   */


  getPlaneConstantOfFace(face_i) {
    const f = this.faces[face_i];
    const n = this.faceNormals[face_i];
    const v = this.vertices[f[0]];
    const c = -n.dot(v);
    return c;
  }
  /**
   * Clip a face against a hull.
   * @param worldVertsB1 An array of Vec3 with vertices in the world frame.
   * @param minDist Distance clamping
   * @param Array result Array to store resulting contact points in. Will be objects with properties: point, depth, normal. These are represented in world coordinates.
   */


  clipFaceAgainstHull(separatingNormal, posA, quatA, worldVertsB1, minDist, maxDist, result) {
    const faceANormalWS = new Vec3$1();
    const edge0 = new Vec3$1();
    const WorldEdge0 = new Vec3$1();
    const worldPlaneAnormal1 = new Vec3$1();
    const planeNormalWS1 = new Vec3$1();
    const worldA1 = new Vec3$1();
    const localPlaneNormal = new Vec3$1();
    const planeNormalWS = new Vec3$1();
    const hullA = this;
    const worldVertsB2 = [];
    const pVtxIn = worldVertsB1;
    const pVtxOut = worldVertsB2;
    let closestFaceA = -1;
    let dmin = Number.MAX_VALUE; // Find the face with normal closest to the separating axis

    for (let face = 0; face < hullA.faces.length; face++) {
      faceANormalWS.copy(hullA.faceNormals[face]);
      quatA.vmult(faceANormalWS, faceANormalWS);
      const d = faceANormalWS.dot(separatingNormal);

      if (d < dmin) {
        dmin = d;
        closestFaceA = face;
      }
    }

    if (closestFaceA < 0) {
      return;
    } // Get the face and construct connected faces


    const polyA = hullA.faces[closestFaceA];
    polyA.connectedFaces = [];

    for (let i = 0; i < hullA.faces.length; i++) {
      for (let j = 0; j < hullA.faces[i].length; j++) {
        if (
        /* Sharing a vertex*/
        polyA.indexOf(hullA.faces[i][j]) !== -1 &&
        /* Not the one we are looking for connections from */
        i !== closestFaceA &&
        /* Not already added */
        polyA.connectedFaces.indexOf(i) === -1) {
          polyA.connectedFaces.push(i);
        }
      }
    } // Clip the polygon to the back of the planes of all faces of hull A,
    // that are adjacent to the witness face


    const numVerticesA = polyA.length;

    for (let i = 0; i < numVerticesA; i++) {
      const a = hullA.vertices[polyA[i]];
      const b = hullA.vertices[polyA[(i + 1) % numVerticesA]];
      a.vsub(b, edge0);
      WorldEdge0.copy(edge0);
      quatA.vmult(WorldEdge0, WorldEdge0);
      posA.vadd(WorldEdge0, WorldEdge0);
      worldPlaneAnormal1.copy(this.faceNormals[closestFaceA]);
      quatA.vmult(worldPlaneAnormal1, worldPlaneAnormal1);
      posA.vadd(worldPlaneAnormal1, worldPlaneAnormal1);
      WorldEdge0.cross(worldPlaneAnormal1, planeNormalWS1);
      planeNormalWS1.negate(planeNormalWS1);
      worldA1.copy(a);
      quatA.vmult(worldA1, worldA1);
      posA.vadd(worldA1, worldA1);
      const otherFace = polyA.connectedFaces[i];
      localPlaneNormal.copy(this.faceNormals[otherFace]);
      const localPlaneEq = this.getPlaneConstantOfFace(otherFace);
      planeNormalWS.copy(localPlaneNormal);
      quatA.vmult(planeNormalWS, planeNormalWS);
      const planeEqWS = localPlaneEq - planeNormalWS.dot(posA); // Clip face against our constructed plane

      this.clipFaceAgainstPlane(pVtxIn, pVtxOut, planeNormalWS, planeEqWS); // Throw away all clipped points, but save the remaining until next clip

      while (pVtxIn.length) {
        pVtxIn.shift();
      }

      while (pVtxOut.length) {
        pVtxIn.push(pVtxOut.shift());
      }
    } // only keep contact points that are behind the witness face


    localPlaneNormal.copy(this.faceNormals[closestFaceA]);
    const localPlaneEq = this.getPlaneConstantOfFace(closestFaceA);
    planeNormalWS.copy(localPlaneNormal);
    quatA.vmult(planeNormalWS, planeNormalWS);
    const planeEqWS = localPlaneEq - planeNormalWS.dot(posA);

    for (let i = 0; i < pVtxIn.length; i++) {
      let depth = planeNormalWS.dot(pVtxIn[i]) + planeEqWS; // ???

      if (depth <= minDist) {
        console.log(`clamped: depth=${depth} to minDist=${minDist}`);
        depth = minDist;
      }

      if (depth <= maxDist) {
        const point = pVtxIn[i];

        if (depth <= 1e-6) {
          const p = {
            point,
            normal: planeNormalWS,
            depth
          };
          result.push(p);
        }
      }
    }
  }
  /**
   * Clip a face in a hull against the back of a plane.
   * @param planeConstant The constant in the mathematical plane equation
   */


  clipFaceAgainstPlane(inVertices, outVertices, planeNormal, planeConstant) {
    let n_dot_first;
    let n_dot_last;
    const numVerts = inVertices.length;

    if (numVerts < 2) {
      return outVertices;
    }

    let firstVertex = inVertices[inVertices.length - 1];
    let lastVertex = inVertices[0];
    n_dot_first = planeNormal.dot(firstVertex) + planeConstant;

    for (let vi = 0; vi < numVerts; vi++) {
      lastVertex = inVertices[vi];
      n_dot_last = planeNormal.dot(lastVertex) + planeConstant;

      if (n_dot_first < 0) {
        if (n_dot_last < 0) {
          // Start < 0, end < 0, so output lastVertex
          const newv = new Vec3$1();
          newv.copy(lastVertex);
          outVertices.push(newv);
        } else {
          // Start < 0, end >= 0, so output intersection
          const newv = new Vec3$1();
          firstVertex.lerp(lastVertex, n_dot_first / (n_dot_first - n_dot_last), newv);
          outVertices.push(newv);
        }
      } else {
        if (n_dot_last < 0) {
          // Start >= 0, end < 0 so output intersection and end
          const newv = new Vec3$1();
          firstVertex.lerp(lastVertex, n_dot_first / (n_dot_first - n_dot_last), newv);
          outVertices.push(newv);
          outVertices.push(lastVertex);
        }
      }

      firstVertex = lastVertex;
      n_dot_first = n_dot_last;
    }

    return outVertices;
  }
  /**
   * Updates `.worldVertices` and sets `.worldVerticesNeedsUpdate` to false.
   */


  computeWorldVertices(position, quat) {
    while (this.worldVertices.length < this.vertices.length) {
      this.worldVertices.push(new Vec3$1());
    }

    const verts = this.vertices;
    const worldVerts = this.worldVertices;

    for (let i = 0; i !== this.vertices.length; i++) {
      quat.vmult(verts[i], worldVerts[i]);
      position.vadd(worldVerts[i], worldVerts[i]);
    }

    this.worldVerticesNeedsUpdate = false;
  }

  computeLocalAABB(aabbmin, aabbmax) {
    const vertices = this.vertices;
    aabbmin.set(Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE);
    aabbmax.set(-Number.MAX_VALUE, -Number.MAX_VALUE, -Number.MAX_VALUE);

    for (let i = 0; i < this.vertices.length; i++) {
      const v = vertices[i];

      if (v.x < aabbmin.x) {
        aabbmin.x = v.x;
      } else if (v.x > aabbmax.x) {
        aabbmax.x = v.x;
      }

      if (v.y < aabbmin.y) {
        aabbmin.y = v.y;
      } else if (v.y > aabbmax.y) {
        aabbmax.y = v.y;
      }

      if (v.z < aabbmin.z) {
        aabbmin.z = v.z;
      } else if (v.z > aabbmax.z) {
        aabbmax.z = v.z;
      }
    }
  }
  /**
   * Updates `worldVertices` and sets `worldVerticesNeedsUpdate` to false.
   */


  computeWorldFaceNormals(quat) {
    const N = this.faceNormals.length;

    while (this.worldFaceNormals.length < N) {
      this.worldFaceNormals.push(new Vec3$1());
    }

    const normals = this.faceNormals;
    const worldNormals = this.worldFaceNormals;

    for (let i = 0; i !== N; i++) {
      quat.vmult(normals[i], worldNormals[i]);
    }

    this.worldFaceNormalsNeedsUpdate = false;
  }
  /**
   * updateBoundingSphereRadius
   */


  updateBoundingSphereRadius() {
    // Assume points are distributed with local (0,0,0) as center
    let max2 = 0;
    const verts = this.vertices;

    for (let i = 0; i !== verts.length; i++) {
      const norm2 = verts[i].lengthSquared();

      if (norm2 > max2) {
        max2 = norm2;
      }
    }

    this.boundingSphereRadius = Math.sqrt(max2);
  }
  /**
   * calculateWorldAABB
   */


  calculateWorldAABB(pos, quat, min, max) {
    const verts = this.vertices;
    let minx;
    let miny;
    let minz;
    let maxx;
    let maxy;
    let maxz;
    let tempWorldVertex = new Vec3$1();

    for (let i = 0; i < verts.length; i++) {
      tempWorldVertex.copy(verts[i]);
      quat.vmult(tempWorldVertex, tempWorldVertex);
      pos.vadd(tempWorldVertex, tempWorldVertex);
      const v = tempWorldVertex;

      if (minx === undefined || v.x < minx) {
        minx = v.x;
      }

      if (maxx === undefined || v.x > maxx) {
        maxx = v.x;
      }

      if (miny === undefined || v.y < miny) {
        miny = v.y;
      }

      if (maxy === undefined || v.y > maxy) {
        maxy = v.y;
      }

      if (minz === undefined || v.z < minz) {
        minz = v.z;
      }

      if (maxz === undefined || v.z > maxz) {
        maxz = v.z;
      }
    }

    min.set(minx, miny, minz);
    max.set(maxx, maxy, maxz);
  }
  /**
   * Get approximate convex volume
   */


  volume() {
    return 4.0 * Math.PI * this.boundingSphereRadius / 3.0;
  }
  /**
   * Get an average of all the vertices positions
   */


  getAveragePointLocal(target) {
    if (target === void 0) {
      target = new Vec3$1();
    }

    const verts = this.vertices;

    for (let i = 0; i < verts.length; i++) {
      target.vadd(verts[i], target);
    }

    target.scale(1 / verts.length, target);
    return target;
  }
  /**
   * Transform all local points. Will change the .vertices
   */


  transformAllPoints(offset, quat) {
    const n = this.vertices.length;
    const verts = this.vertices; // Apply rotation

    if (quat) {
      // Rotate vertices
      for (let i = 0; i < n; i++) {
        const v = verts[i];
        quat.vmult(v, v);
      } // Rotate face normals


      for (let i = 0; i < this.faceNormals.length; i++) {
        const v = this.faceNormals[i];
        quat.vmult(v, v);
      }
      /*
            // Rotate edges
            for(let i=0; i<this.uniqueEdges.length; i++){
                const v = this.uniqueEdges[i];
                quat.vmult(v,v);
            }*/

    } // Apply offset


    if (offset) {
      for (let i = 0; i < n; i++) {
        const v = verts[i];
        v.vadd(offset, v);
      }
    }
  }
  /**
   * Checks whether p is inside the polyhedra. Must be in local coords.
   * The point lies outside of the convex hull of the other points if and only if the direction
   * of all the vectors from it to those other points are on less than one half of a sphere around it.
   * @param p A point given in local coordinates
   */


  pointIsInside(p) {
    const verts = this.vertices;
    const faces = this.faces;
    const normals = this.faceNormals;
    const pointInside = new Vec3$1();
    this.getAveragePointLocal(pointInside);

    for (let i = 0; i < this.faces.length; i++) {
      let n = normals[i];
      const v = verts[faces[i][0]]; // We only need one point in the face
      // This dot product determines which side of the edge the point is

      const vToP = new Vec3$1();
      p.vsub(v, vToP);
      const r1 = n.dot(vToP);
      const vToPointInside = new Vec3$1();
      pointInside.vsub(v, vToPointInside);
      const r2 = n.dot(vToPointInside);

      if (r1 < 0 && r2 > 0 || r1 > 0 && r2 < 0) {
        return false; // Encountered some other sign. Exit.
      }
    } // If we got here, all dot products were of the same sign.


    return -1;
  }
  /**
   * Get max and min dot product of a convex hull at position (pos,quat) projected onto an axis.
   * Results are saved in the array maxmin.
   * @param result result[0] and result[1] will be set to maximum and minimum, respectively.
   */


  static project(shape, axis, pos, quat, result) {
    const n = shape.vertices.length;
    const localAxis = project_localAxis;
    let max = 0;
    let min = 0;
    const localOrigin = project_localOrigin;
    const vs = shape.vertices;
    localOrigin.setZero(); // Transform the axis to local

    Transform$1.vectorToLocalFrame(pos, quat, axis, localAxis);
    Transform$1.pointToLocalFrame(pos, quat, localOrigin, localOrigin);
    const add = localOrigin.dot(localAxis);
    min = max = vs[0].dot(localAxis);

    for (let i = 1; i < n; i++) {
      const val = vs[i].dot(localAxis);

      if (val > max) {
        max = val;
      }

      if (val < min) {
        min = val;
      }
    }

    min -= add;
    max -= add;

    if (min > max) {
      // Inconsistent - swap
      const temp = min;
      min = max;
      max = temp;
    } // Output


    result[0] = max;
    result[1] = min;
  }

}
const maxminA = [];
const maxminB = [];
new Vec3$1();
const project_localAxis = new Vec3$1();
const project_localOrigin = new Vec3$1();

/**
 * A 3d box shape.
 * @example
 *     const size = 1
 *     const halfExtents = new CANNON.Vec3(size, size, size)
 *     const boxShape = new CANNON.Box(halfExtents)
 *     const boxBody = new CANNON.Body({ mass: 1, shape: boxShape })
 *     world.addBody(boxBody)
 */
class Box extends Shape$1 {
  /**
   * The half extents of the box.
   */

  /**
   * Used by the contact generator to make contacts with other convex polyhedra for example.
   */
  constructor(halfExtents) {
    super({
      type: Shape$1.types.BOX
    });
    this.halfExtents = halfExtents;
    this.convexPolyhedronRepresentation = null;
    this.updateConvexPolyhedronRepresentation();
    this.updateBoundingSphereRadius();
  }
  /**
   * Updates the local convex polyhedron representation used for some collisions.
   */


  updateConvexPolyhedronRepresentation() {
    const sx = this.halfExtents.x;
    const sy = this.halfExtents.y;
    const sz = this.halfExtents.z;
    const V = Vec3$1;
    const vertices = [new V(-sx, -sy, -sz), new V(sx, -sy, -sz), new V(sx, sy, -sz), new V(-sx, sy, -sz), new V(-sx, -sy, sz), new V(sx, -sy, sz), new V(sx, sy, sz), new V(-sx, sy, sz)];
    const faces = [[3, 2, 1, 0], // -z
    [4, 5, 6, 7], // +z
    [5, 4, 0, 1], // -y
    [2, 3, 7, 6], // +y
    [0, 4, 7, 3], // -x
    [1, 2, 6, 5] // +x
    ];
    const axes = [new V(0, 0, 1), new V(0, 1, 0), new V(1, 0, 0)];
    const h = new ConvexPolyhedron({
      vertices,
      faces,
      axes
    });
    this.convexPolyhedronRepresentation = h;
    h.material = this.material;
  }
  /**
   * Calculate the inertia of the box.
   */


  calculateLocalInertia(mass, target) {
    if (target === void 0) {
      target = new Vec3$1();
    }

    Box.calculateInertia(this.halfExtents, mass, target);
    return target;
  }

  static calculateInertia(halfExtents, mass, target) {
    const e = halfExtents;
    target.x = 1.0 / 12.0 * mass * (2 * e.y * 2 * e.y + 2 * e.z * 2 * e.z);
    target.y = 1.0 / 12.0 * mass * (2 * e.x * 2 * e.x + 2 * e.z * 2 * e.z);
    target.z = 1.0 / 12.0 * mass * (2 * e.y * 2 * e.y + 2 * e.x * 2 * e.x);
  }
  /**
   * Get the box 6 side normals
   * @param sixTargetVectors An array of 6 vectors, to store the resulting side normals in.
   * @param quat Orientation to apply to the normal vectors. If not provided, the vectors will be in respect to the local frame.
   */


  getSideNormals(sixTargetVectors, quat) {
    const sides = sixTargetVectors;
    const ex = this.halfExtents;
    sides[0].set(ex.x, 0, 0);
    sides[1].set(0, ex.y, 0);
    sides[2].set(0, 0, ex.z);
    sides[3].set(-ex.x, 0, 0);
    sides[4].set(0, -ex.y, 0);
    sides[5].set(0, 0, -ex.z);

    if (quat !== undefined) {
      for (let i = 0; i !== sides.length; i++) {
        quat.vmult(sides[i], sides[i]);
      }
    }

    return sides;
  }
  /**
   * Returns the volume of the box.
   */


  volume() {
    return 8.0 * this.halfExtents.x * this.halfExtents.y * this.halfExtents.z;
  }
  /**
   * updateBoundingSphereRadius
   */


  updateBoundingSphereRadius() {
    this.boundingSphereRadius = this.halfExtents.length();
  }
  /**
   * forEachWorldCorner
   */


  forEachWorldCorner(pos, quat, callback) {
    const e = this.halfExtents;
    const corners = [[e.x, e.y, e.z], [-e.x, e.y, e.z], [-e.x, -e.y, e.z], [-e.x, -e.y, -e.z], [e.x, -e.y, -e.z], [e.x, e.y, -e.z], [-e.x, e.y, -e.z], [e.x, -e.y, e.z]];

    for (let i = 0; i < corners.length; i++) {
      worldCornerTempPos.set(corners[i][0], corners[i][1], corners[i][2]);
      quat.vmult(worldCornerTempPos, worldCornerTempPos);
      pos.vadd(worldCornerTempPos, worldCornerTempPos);
      callback(worldCornerTempPos.x, worldCornerTempPos.y, worldCornerTempPos.z);
    }
  }
  /**
   * calculateWorldAABB
   */


  calculateWorldAABB(pos, quat, min, max) {
    const e = this.halfExtents;
    worldCornersTemp[0].set(e.x, e.y, e.z);
    worldCornersTemp[1].set(-e.x, e.y, e.z);
    worldCornersTemp[2].set(-e.x, -e.y, e.z);
    worldCornersTemp[3].set(-e.x, -e.y, -e.z);
    worldCornersTemp[4].set(e.x, -e.y, -e.z);
    worldCornersTemp[5].set(e.x, e.y, -e.z);
    worldCornersTemp[6].set(-e.x, e.y, -e.z);
    worldCornersTemp[7].set(e.x, -e.y, e.z);
    const wc = worldCornersTemp[0];
    quat.vmult(wc, wc);
    pos.vadd(wc, wc);
    max.copy(wc);
    min.copy(wc);

    for (let i = 1; i < 8; i++) {
      const wc = worldCornersTemp[i];
      quat.vmult(wc, wc);
      pos.vadd(wc, wc);
      const x = wc.x;
      const y = wc.y;
      const z = wc.z;

      if (x > max.x) {
        max.x = x;
      }

      if (y > max.y) {
        max.y = y;
      }

      if (z > max.z) {
        max.z = z;
      }

      if (x < min.x) {
        min.x = x;
      }

      if (y < min.y) {
        min.y = y;
      }

      if (z < min.z) {
        min.z = z;
      }
    } // Get each axis max
    // min.set(Infinity,Infinity,Infinity);
    // max.set(-Infinity,-Infinity,-Infinity);
    // this.forEachWorldCorner(pos,quat,function(x,y,z){
    //     if(x > max.x){
    //         max.x = x;
    //     }
    //     if(y > max.y){
    //         max.y = y;
    //     }
    //     if(z > max.z){
    //         max.z = z;
    //     }
    //     if(x < min.x){
    //         min.x = x;
    //     }
    //     if(y < min.y){
    //         min.y = y;
    //     }
    //     if(z < min.z){
    //         min.z = z;
    //     }
    // });

  }

}
const worldCornerTempPos = new Vec3$1();
const worldCornersTemp = [new Vec3$1(), new Vec3$1(), new Vec3$1(), new Vec3$1(), new Vec3$1(), new Vec3$1(), new Vec3$1(), new Vec3$1()];

/**
 * BODY_TYPES
 */
const BODY_TYPES = {
  /** DYNAMIC */
  DYNAMIC: 1,

  /** STATIC */
  STATIC: 2,

  /** KINEMATIC */
  KINEMATIC: 4
};
/**
 * BodyType
 */

/**
 * BODY_SLEEP_STATES
 */
const BODY_SLEEP_STATES = {
  /** AWAKE */
  AWAKE: 0,

  /** SLEEPY */
  SLEEPY: 1,

  /** SLEEPING */
  SLEEPING: 2
};
/**
 * BodySleepState
 */

/**
 * Base class for all body types.
 * @example
 *     const shape = new CANNON.Sphere(1)
 *     const body = new CANNON.Body({
 *       mass: 1,
 *       shape,
 *     })
 *     world.addBody(body)
 */
class Body extends EventTarget {
  /**
   * Dispatched after two bodies collide. This event is dispatched on each
   * of the two bodies involved in the collision.
   * @event collide
   * @param body The body that was involved in the collision.
   * @param contact The details of the collision.
   */

  /**
   * A dynamic body is fully simulated. Can be moved manually by the user, but normally they move according to forces. A dynamic body can collide with all body types. A dynamic body always has finite, non-zero mass.
   */

  /**
   * A static body does not move during simulation and behaves as if it has infinite mass. Static bodies can be moved manually by setting the position of the body. The velocity of a static body is always zero. Static bodies do not collide with other static or kinematic bodies.
   */

  /**
   * A kinematic body moves under simulation according to its velocity. They do not respond to forces. They can be moved manually, but normally a kinematic body is moved by setting its velocity. A kinematic body behaves as if it has infinite mass. Kinematic bodies do not collide with other static or kinematic bodies.
   */

  /**
   * AWAKE
   */

  /**
   * SLEEPY
   */

  /**
   * SLEEPING
   */

  /**
   * Dispatched after a sleeping body has woken up.
   * @event wakeup
   */

  /**
   * Dispatched after a body has gone in to the sleepy state.
   * @event sleepy
   */

  /**
   * Dispatched after a body has fallen asleep.
   * @event sleep
   */
  constructor(options) {
    if (options === void 0) {
      options = {};
    }

    super();
    this.id = Body.idCounter++;
    this.index = -1;
    this.world = null;
    this.vlambda = new Vec3$1();
    this.collisionFilterGroup = typeof options.collisionFilterGroup === 'number' ? options.collisionFilterGroup : 1;
    this.collisionFilterMask = typeof options.collisionFilterMask === 'number' ? options.collisionFilterMask : -1;
    this.collisionResponse = typeof options.collisionResponse === 'boolean' ? options.collisionResponse : true;
    this.position = new Vec3$1();
    this.previousPosition = new Vec3$1();
    this.interpolatedPosition = new Vec3$1();
    this.initPosition = new Vec3$1();

    if (options.position) {
      this.position.copy(options.position);
      this.previousPosition.copy(options.position);
      this.interpolatedPosition.copy(options.position);
      this.initPosition.copy(options.position);
    }

    this.velocity = new Vec3$1();

    if (options.velocity) {
      this.velocity.copy(options.velocity);
    }

    this.initVelocity = new Vec3$1();
    this.force = new Vec3$1();
    const mass = typeof options.mass === 'number' ? options.mass : 0;
    this.mass = mass;
    this.invMass = mass > 0 ? 1.0 / mass : 0;
    this.material = options.material || null;
    this.linearDamping = typeof options.linearDamping === 'number' ? options.linearDamping : 0.01;
    this.type = mass <= 0.0 ? Body.STATIC : Body.DYNAMIC;

    if (typeof options.type === typeof Body.STATIC) {
      this.type = options.type;
    }

    this.allowSleep = typeof options.allowSleep !== 'undefined' ? options.allowSleep : true;
    this.sleepState = Body.AWAKE;
    this.sleepSpeedLimit = typeof options.sleepSpeedLimit !== 'undefined' ? options.sleepSpeedLimit : 0.1;
    this.sleepTimeLimit = typeof options.sleepTimeLimit !== 'undefined' ? options.sleepTimeLimit : 1;
    this.timeLastSleepy = 0;
    this.wakeUpAfterNarrowphase = false;
    this.torque = new Vec3$1();
    this.quaternion = new Quaternion$1();
    this.initQuaternion = new Quaternion$1();
    this.previousQuaternion = new Quaternion$1();
    this.interpolatedQuaternion = new Quaternion$1();

    if (options.quaternion) {
      this.quaternion.copy(options.quaternion);
      this.initQuaternion.copy(options.quaternion);
      this.previousQuaternion.copy(options.quaternion);
      this.interpolatedQuaternion.copy(options.quaternion);
    }

    this.angularVelocity = new Vec3$1();

    if (options.angularVelocity) {
      this.angularVelocity.copy(options.angularVelocity);
    }

    this.initAngularVelocity = new Vec3$1();
    this.shapes = [];
    this.shapeOffsets = [];
    this.shapeOrientations = [];
    this.inertia = new Vec3$1();
    this.invInertia = new Vec3$1();
    this.invInertiaWorld = new Mat3$1();
    this.invMassSolve = 0;
    this.invInertiaSolve = new Vec3$1();
    this.invInertiaWorldSolve = new Mat3$1();
    this.fixedRotation = typeof options.fixedRotation !== 'undefined' ? options.fixedRotation : false;
    this.angularDamping = typeof options.angularDamping !== 'undefined' ? options.angularDamping : 0.01;
    this.linearFactor = new Vec3$1(1, 1, 1);

    if (options.linearFactor) {
      this.linearFactor.copy(options.linearFactor);
    }

    this.angularFactor = new Vec3$1(1, 1, 1);

    if (options.angularFactor) {
      this.angularFactor.copy(options.angularFactor);
    }

    this.aabb = new AABB$1();
    this.aabbNeedsUpdate = true;
    this.boundingRadius = 0;
    this.wlambda = new Vec3$1();
    this.isTrigger = Boolean(options.isTrigger);

    if (options.shape) {
      this.addShape(options.shape);
    }

    this.updateMassProperties();
  }
  /**
   * Wake the body up.
   */


  wakeUp() {
    const prevState = this.sleepState;
    this.sleepState = Body.AWAKE;
    this.wakeUpAfterNarrowphase = false;

    if (prevState === Body.SLEEPING) {
      this.dispatchEvent(Body.wakeupEvent);
    }
  }
  /**
   * Force body sleep
   */


  sleep() {
    this.sleepState = Body.SLEEPING;
    this.velocity.set(0, 0, 0);
    this.angularVelocity.set(0, 0, 0);
    this.wakeUpAfterNarrowphase = false;
  }
  /**
   * Called every timestep to update internal sleep timer and change sleep state if needed.
   * @param time The world time in seconds
   */


  sleepTick(time) {
    if (this.allowSleep) {
      const sleepState = this.sleepState;
      const speedSquared = this.velocity.lengthSquared() + this.angularVelocity.lengthSquared();
      const speedLimitSquared = this.sleepSpeedLimit ** 2;

      if (sleepState === Body.AWAKE && speedSquared < speedLimitSquared) {
        this.sleepState = Body.SLEEPY; // Sleepy

        this.timeLastSleepy = time;
        this.dispatchEvent(Body.sleepyEvent);
      } else if (sleepState === Body.SLEEPY && speedSquared > speedLimitSquared) {
        this.wakeUp(); // Wake up
      } else if (sleepState === Body.SLEEPY && time - this.timeLastSleepy > this.sleepTimeLimit) {
        this.sleep(); // Sleeping

        this.dispatchEvent(Body.sleepEvent);
      }
    }
  }
  /**
   * If the body is sleeping, it should be immovable / have infinite mass during solve. We solve it by having a separate "solve mass".
   */


  updateSolveMassProperties() {
    if (this.sleepState === Body.SLEEPING || this.type === Body.KINEMATIC) {
      this.invMassSolve = 0;
      this.invInertiaSolve.setZero();
      this.invInertiaWorldSolve.setZero();
    } else {
      this.invMassSolve = this.invMass;
      this.invInertiaSolve.copy(this.invInertia);
      this.invInertiaWorldSolve.copy(this.invInertiaWorld);
    }
  }
  /**
   * Convert a world point to local body frame.
   */


  pointToLocalFrame(worldPoint, result) {
    if (result === void 0) {
      result = new Vec3$1();
    }

    worldPoint.vsub(this.position, result);
    this.quaternion.conjugate().vmult(result, result);
    return result;
  }
  /**
   * Convert a world vector to local body frame.
   */


  vectorToLocalFrame(worldVector, result) {
    if (result === void 0) {
      result = new Vec3$1();
    }

    this.quaternion.conjugate().vmult(worldVector, result);
    return result;
  }
  /**
   * Convert a local body point to world frame.
   */


  pointToWorldFrame(localPoint, result) {
    if (result === void 0) {
      result = new Vec3$1();
    }

    this.quaternion.vmult(localPoint, result);
    result.vadd(this.position, result);
    return result;
  }
  /**
   * Convert a local body point to world frame.
   */


  vectorToWorldFrame(localVector, result) {
    if (result === void 0) {
      result = new Vec3$1();
    }

    this.quaternion.vmult(localVector, result);
    return result;
  }
  /**
   * Add a shape to the body with a local offset and orientation.
   * @return The body object, for chainability.
   */


  addShape(shape, _offset, _orientation) {
    const offset = new Vec3$1();
    const orientation = new Quaternion$1();

    if (_offset) {
      offset.copy(_offset);
    }

    if (_orientation) {
      orientation.copy(_orientation);
    }

    this.shapes.push(shape);
    this.shapeOffsets.push(offset);
    this.shapeOrientations.push(orientation);
    this.updateMassProperties();
    this.updateBoundingRadius();
    this.aabbNeedsUpdate = true;
    shape.body = this;
    return this;
  }
  /**
   * Remove a shape from the body.
   * @return The body object, for chainability.
   */


  removeShape(shape) {
    const index = this.shapes.indexOf(shape);

    if (index === -1) {
      console.warn('Shape does not belong to the body');
      return this;
    }

    this.shapes.splice(index, 1);
    this.shapeOffsets.splice(index, 1);
    this.shapeOrientations.splice(index, 1);
    this.updateMassProperties();
    this.updateBoundingRadius();
    this.aabbNeedsUpdate = true;
    shape.body = null;
    return this;
  }
  /**
   * Update the bounding radius of the body. Should be done if any of the shapes are changed.
   */


  updateBoundingRadius() {
    const shapes = this.shapes;
    const shapeOffsets = this.shapeOffsets;
    const N = shapes.length;
    let radius = 0;

    for (let i = 0; i !== N; i++) {
      const shape = shapes[i];
      shape.updateBoundingSphereRadius();
      const offset = shapeOffsets[i].length();
      const r = shape.boundingSphereRadius;

      if (offset + r > radius) {
        radius = offset + r;
      }
    }

    this.boundingRadius = radius;
  }
  /**
   * Updates the .aabb
   */


  updateAABB() {
    const shapes = this.shapes;
    const shapeOffsets = this.shapeOffsets;
    const shapeOrientations = this.shapeOrientations;
    const N = shapes.length;
    const offset = tmpVec;
    const orientation = tmpQuat;
    const bodyQuat = this.quaternion;
    const aabb = this.aabb;
    const shapeAABB = updateAABB_shapeAABB;

    for (let i = 0; i !== N; i++) {
      const shape = shapes[i]; // Get shape world position

      bodyQuat.vmult(shapeOffsets[i], offset);
      offset.vadd(this.position, offset); // Get shape world quaternion

      bodyQuat.mult(shapeOrientations[i], orientation); // Get shape AABB

      shape.calculateWorldAABB(offset, orientation, shapeAABB.lowerBound, shapeAABB.upperBound);

      if (i === 0) {
        aabb.copy(shapeAABB);
      } else {
        aabb.extend(shapeAABB);
      }
    }

    this.aabbNeedsUpdate = false;
  }
  /**
   * Update `.inertiaWorld` and `.invInertiaWorld`
   */


  updateInertiaWorld(force) {
    const I = this.invInertia;

    if (I.x === I.y && I.y === I.z && !force) ; else {
      const m1 = uiw_m1;
      const m2 = uiw_m2;
      m1.setRotationFromQuaternion(this.quaternion);
      m1.transpose(m2);
      m1.scale(I, m1);
      m1.mmult(m2, this.invInertiaWorld);
    }
  }
  /**
   * Apply force to a point of the body. This could for example be a point on the Body surface.
   * Applying force this way will add to Body.force and Body.torque.
   * @param force The amount of force to add.
   * @param relativePoint A point relative to the center of mass to apply the force on.
   */


  applyForce(force, relativePoint) {
    if (relativePoint === void 0) {
      relativePoint = new Vec3$1();
    }

    // Needed?
    if (this.type !== Body.DYNAMIC) {
      return;
    }

    if (this.sleepState === Body.SLEEPING) {
      this.wakeUp();
    } // Compute produced rotational force


    const rotForce = Body_applyForce_rotForce;
    relativePoint.cross(force, rotForce); // Add linear force

    this.force.vadd(force, this.force); // Add rotational force

    this.torque.vadd(rotForce, this.torque);
  }
  /**
   * Apply force to a local point in the body.
   * @param force The force vector to apply, defined locally in the body frame.
   * @param localPoint A local point in the body to apply the force on.
   */


  applyLocalForce(localForce, localPoint) {
    if (localPoint === void 0) {
      localPoint = new Vec3$1();
    }

    if (this.type !== Body.DYNAMIC) {
      return;
    }

    const worldForce = Body_applyLocalForce_worldForce;
    const relativePointWorld = Body_applyLocalForce_relativePointWorld; // Transform the force vector to world space

    this.vectorToWorldFrame(localForce, worldForce);
    this.vectorToWorldFrame(localPoint, relativePointWorld);
    this.applyForce(worldForce, relativePointWorld);
  }
  /**
   * Apply torque to the body.
   * @param torque The amount of torque to add.
   */


  applyTorque(torque) {
    if (this.type !== Body.DYNAMIC) {
      return;
    }

    if (this.sleepState === Body.SLEEPING) {
      this.wakeUp();
    } // Add rotational force


    this.torque.vadd(torque, this.torque);
  }
  /**
   * Apply impulse to a point of the body. This could for example be a point on the Body surface.
   * An impulse is a force added to a body during a short period of time (impulse = force * time).
   * Impulses will be added to Body.velocity and Body.angularVelocity.
   * @param impulse The amount of impulse to add.
   * @param relativePoint A point relative to the center of mass to apply the force on.
   */


  applyImpulse(impulse, relativePoint) {
    if (relativePoint === void 0) {
      relativePoint = new Vec3$1();
    }

    if (this.type !== Body.DYNAMIC) {
      return;
    }

    if (this.sleepState === Body.SLEEPING) {
      this.wakeUp();
    } // Compute point position relative to the body center


    const r = relativePoint; // Compute produced central impulse velocity

    const velo = Body_applyImpulse_velo;
    velo.copy(impulse);
    velo.scale(this.invMass, velo); // Add linear impulse

    this.velocity.vadd(velo, this.velocity); // Compute produced rotational impulse velocity

    const rotVelo = Body_applyImpulse_rotVelo;
    r.cross(impulse, rotVelo);
    /*
     rotVelo.x *= this.invInertia.x;
     rotVelo.y *= this.invInertia.y;
     rotVelo.z *= this.invInertia.z;
     */

    this.invInertiaWorld.vmult(rotVelo, rotVelo); // Add rotational Impulse

    this.angularVelocity.vadd(rotVelo, this.angularVelocity);
  }
  /**
   * Apply locally-defined impulse to a local point in the body.
   * @param force The force vector to apply, defined locally in the body frame.
   * @param localPoint A local point in the body to apply the force on.
   */


  applyLocalImpulse(localImpulse, localPoint) {
    if (localPoint === void 0) {
      localPoint = new Vec3$1();
    }

    if (this.type !== Body.DYNAMIC) {
      return;
    }

    const worldImpulse = Body_applyLocalImpulse_worldImpulse;
    const relativePointWorld = Body_applyLocalImpulse_relativePoint; // Transform the force vector to world space

    this.vectorToWorldFrame(localImpulse, worldImpulse);
    this.vectorToWorldFrame(localPoint, relativePointWorld);
    this.applyImpulse(worldImpulse, relativePointWorld);
  }
  /**
   * Should be called whenever you change the body shape or mass.
   */


  updateMassProperties() {
    const halfExtents = Body_updateMassProperties_halfExtents;
    this.invMass = this.mass > 0 ? 1.0 / this.mass : 0;
    const I = this.inertia;
    const fixed = this.fixedRotation; // Approximate with AABB box

    this.updateAABB();
    halfExtents.set((this.aabb.upperBound.x - this.aabb.lowerBound.x) / 2, (this.aabb.upperBound.y - this.aabb.lowerBound.y) / 2, (this.aabb.upperBound.z - this.aabb.lowerBound.z) / 2);
    Box.calculateInertia(halfExtents, this.mass, I);
    this.invInertia.set(I.x > 0 && !fixed ? 1.0 / I.x : 0, I.y > 0 && !fixed ? 1.0 / I.y : 0, I.z > 0 && !fixed ? 1.0 / I.z : 0);
    this.updateInertiaWorld(true);
  }
  /**
   * Get world velocity of a point in the body.
   * @param worldPoint
   * @param result
   * @return The result vector.
   */


  getVelocityAtWorldPoint(worldPoint, result) {
    const r = new Vec3$1();
    worldPoint.vsub(this.position, r);
    this.angularVelocity.cross(r, result);
    this.velocity.vadd(result, result);
    return result;
  }
  /**
   * Move the body forward in time.
   * @param dt Time step
   * @param quatNormalize Set to true to normalize the body quaternion
   * @param quatNormalizeFast If the quaternion should be normalized using "fast" quaternion normalization
   */


  integrate(dt, quatNormalize, quatNormalizeFast) {
    // Save previous position
    this.previousPosition.copy(this.position);
    this.previousQuaternion.copy(this.quaternion);

    if (!(this.type === Body.DYNAMIC || this.type === Body.KINEMATIC) || this.sleepState === Body.SLEEPING) {
      // Only for dynamic
      return;
    }

    const velo = this.velocity;
    const angularVelo = this.angularVelocity;
    const pos = this.position;
    const force = this.force;
    const torque = this.torque;
    const quat = this.quaternion;
    const invMass = this.invMass;
    const invInertia = this.invInertiaWorld;
    const linearFactor = this.linearFactor;
    const iMdt = invMass * dt;
    velo.x += force.x * iMdt * linearFactor.x;
    velo.y += force.y * iMdt * linearFactor.y;
    velo.z += force.z * iMdt * linearFactor.z;
    const e = invInertia.elements;
    const angularFactor = this.angularFactor;
    const tx = torque.x * angularFactor.x;
    const ty = torque.y * angularFactor.y;
    const tz = torque.z * angularFactor.z;
    angularVelo.x += dt * (e[0] * tx + e[1] * ty + e[2] * tz);
    angularVelo.y += dt * (e[3] * tx + e[4] * ty + e[5] * tz);
    angularVelo.z += dt * (e[6] * tx + e[7] * ty + e[8] * tz); // Use new velocity  - leap frog

    pos.x += velo.x * dt;
    pos.y += velo.y * dt;
    pos.z += velo.z * dt;
    quat.integrate(this.angularVelocity, dt, this.angularFactor, quat);

    if (quatNormalize) {
      if (quatNormalizeFast) {
        quat.normalizeFast();
      } else {
        quat.normalize();
      }
    }

    this.aabbNeedsUpdate = true; // Update world inertia

    this.updateInertiaWorld();
  }

}
Body.idCounter = 0;
Body.COLLIDE_EVENT_NAME = 'collide';
Body.DYNAMIC = BODY_TYPES.DYNAMIC;
Body.STATIC = BODY_TYPES.STATIC;
Body.KINEMATIC = BODY_TYPES.KINEMATIC;
Body.AWAKE = BODY_SLEEP_STATES.AWAKE;
Body.SLEEPY = BODY_SLEEP_STATES.SLEEPY;
Body.SLEEPING = BODY_SLEEP_STATES.SLEEPING;
Body.wakeupEvent = {
  type: 'wakeup'
};
Body.sleepyEvent = {
  type: 'sleepy'
};
Body.sleepEvent = {
  type: 'sleep'
};
const tmpVec = new Vec3$1();
const tmpQuat = new Quaternion$1();
const updateAABB_shapeAABB = new AABB$1();
const uiw_m1 = new Mat3$1();
const uiw_m2 = new Mat3$1();
new Mat3$1();
const Body_applyForce_rotForce = new Vec3$1();
const Body_applyLocalForce_worldForce = new Vec3$1();
const Body_applyLocalForce_relativePointWorld = new Vec3$1();
const Body_applyImpulse_velo = new Vec3$1();
const Body_applyImpulse_rotVelo = new Vec3$1();
const Body_applyLocalImpulse_worldImpulse = new Vec3$1();
const Body_applyLocalImpulse_relativePoint = new Vec3$1();
const Body_updateMassProperties_halfExtents = new Vec3$1();

new Vec3$1();
new Vec3$1();
new Quaternion$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();

/**
 * Storage for Ray casting data
 */
class RaycastResult$1 {
  /**
   * rayFromWorld
   */

  /**
   * rayToWorld
   */

  /**
   * hitNormalWorld
   */

  /**
   * hitPointWorld
   */

  /**
   * hasHit
   */

  /**
   * shape
   */

  /**
   * body
   */

  /**
   * The index of the hit triangle, if the hit shape was a trimesh
   */

  /**
   * Distance to the hit. Will be set to -1 if there was no hit
   */

  /**
   * If the ray should stop traversing the bodies
   */
  constructor() {
    this.rayFromWorld = new Vec3$1();
    this.rayToWorld = new Vec3$1();
    this.hitNormalWorld = new Vec3$1();
    this.hitPointWorld = new Vec3$1();
    this.hasHit = false;
    this.shape = null;
    this.body = null;
    this.hitFaceIndex = -1;
    this.distance = -1;
    this.shouldStop = false;
  }
  /**
   * Reset all result data.
   */


  reset() {
    this.rayFromWorld.setZero();
    this.rayToWorld.setZero();
    this.hitNormalWorld.setZero();
    this.hitPointWorld.setZero();
    this.hasHit = false;
    this.shape = null;
    this.body = null;
    this.hitFaceIndex = -1;
    this.distance = -1;
    this.shouldStop = false;
  }
  /**
   * abort
   */


  abort() {
    this.shouldStop = true;
  }
  /**
   * Set result data.
   */


  set(rayFromWorld, rayToWorld, hitNormalWorld, hitPointWorld, shape, body, distance) {
    this.rayFromWorld.copy(rayFromWorld);
    this.rayToWorld.copy(rayToWorld);
    this.hitNormalWorld.copy(hitNormalWorld);
    this.hitPointWorld.copy(hitPointWorld);
    this.shape = shape;
    this.body = body;
    this.distance = distance;
  }

}

let _Shape$types$SPHERE$1, _Shape$types$PLANE$1, _Shape$types$BOX$1, _Shape$types$CYLINDER$1, _Shape$types$CONVEXPO$1, _Shape$types$HEIGHTFI$1, _Shape$types$TRIMESH$1;

/**
 * RAY_MODES
 */
const RAY_MODES$1 = {
  /** CLOSEST */
  CLOSEST: 1,

  /** ANY */
  ANY: 2,

  /** ALL */
  ALL: 4
};
/**
 * RayMode
 */

_Shape$types$SPHERE$1 = Shape$1.types.SPHERE;
_Shape$types$PLANE$1 = Shape$1.types.PLANE;
_Shape$types$BOX$1 = Shape$1.types.BOX;
_Shape$types$CYLINDER$1 = Shape$1.types.CYLINDER;
_Shape$types$CONVEXPO$1 = Shape$1.types.CONVEXPOLYHEDRON;
_Shape$types$HEIGHTFI$1 = Shape$1.types.HEIGHTFIELD;
_Shape$types$TRIMESH$1 = Shape$1.types.TRIMESH;

/**
 * A line in 3D space that intersects bodies and return points.
 */
class Ray$1 {
  /**
   * from
   */

  /**
   * to
   */

  /**
   * direction
   */

  /**
   * The precision of the ray. Used when checking parallelity etc.
   * @default 0.0001
   */

  /**
   * Set to `false` if you don't want the Ray to take `collisionResponse` flags into account on bodies and shapes.
   * @default true
   */

  /**
   * If set to `true`, the ray skips any hits with normal.dot(rayDirection) < 0.
   * @default false
   */

  /**
   * collisionFilterMask
   * @default -1
   */

  /**
   * collisionFilterGroup
   * @default -1
   */

  /**
   * The intersection mode. Should be Ray.ANY, Ray.ALL or Ray.CLOSEST.
   * @default RAY.ANY
   */

  /**
   * Current result object.
   */

  /**
   * Will be set to `true` during intersectWorld() if the ray hit anything.
   */

  /**
   * User-provided result callback. Will be used if mode is Ray.ALL.
   */

  /**
   * CLOSEST
   */

  /**
   * ANY
   */

  /**
   * ALL
   */
  get [_Shape$types$SPHERE$1]() {
    return this._intersectSphere;
  }

  get [_Shape$types$PLANE$1]() {
    return this._intersectPlane;
  }

  get [_Shape$types$BOX$1]() {
    return this._intersectBox;
  }

  get [_Shape$types$CYLINDER$1]() {
    return this._intersectConvex;
  }

  get [_Shape$types$CONVEXPO$1]() {
    return this._intersectConvex;
  }

  get [_Shape$types$HEIGHTFI$1]() {
    return this._intersectHeightfield;
  }

  get [_Shape$types$TRIMESH$1]() {
    return this._intersectTrimesh;
  }

  constructor(from, to) {
    if (from === void 0) {
      from = new Vec3$1();
    }

    if (to === void 0) {
      to = new Vec3$1();
    }

    this.from = from.clone();
    this.to = to.clone();
    this.direction = new Vec3$1();
    this.precision = 0.0001;
    this.checkCollisionResponse = true;
    this.skipBackfaces = false;
    this.collisionFilterMask = -1;
    this.collisionFilterGroup = -1;
    this.mode = Ray$1.ANY;
    this.result = new RaycastResult$1();
    this.hasHit = false;

    this.callback = result => {};
  }
  /**
   * Do itersection against all bodies in the given World.
   * @return True if the ray hit anything, otherwise false.
   */


  intersectWorld(world, options) {
    this.mode = options.mode || Ray$1.ANY;
    this.result = options.result || new RaycastResult$1();
    this.skipBackfaces = !!options.skipBackfaces;
    this.collisionFilterMask = typeof options.collisionFilterMask !== 'undefined' ? options.collisionFilterMask : -1;
    this.collisionFilterGroup = typeof options.collisionFilterGroup !== 'undefined' ? options.collisionFilterGroup : -1;
    this.checkCollisionResponse = typeof options.checkCollisionResponse !== 'undefined' ? options.checkCollisionResponse : true;

    if (options.from) {
      this.from.copy(options.from);
    }

    if (options.to) {
      this.to.copy(options.to);
    }

    this.callback = options.callback || (() => {});

    this.hasHit = false;
    this.result.reset();
    this.updateDirection();
    this.getAABB(tmpAABB$1$1);
    tmpArray$1.length = 0;
    world.broadphase.aabbQuery(world, tmpAABB$1$1, tmpArray$1);
    this.intersectBodies(tmpArray$1);
    return this.hasHit;
  }
  /**
   * Shoot a ray at a body, get back information about the hit.
   * @deprecated @param result set the result property of the Ray instead.
   */


  intersectBody(body, result) {
    if (result) {
      this.result = result;
      this.updateDirection();
    }

    const checkCollisionResponse = this.checkCollisionResponse;

    if (checkCollisionResponse && !body.collisionResponse) {
      return;
    }

    if ((this.collisionFilterGroup & body.collisionFilterMask) === 0 || (body.collisionFilterGroup & this.collisionFilterMask) === 0) {
      return;
    }

    const xi = intersectBody_xi$1;
    const qi = intersectBody_qi$1;

    for (let i = 0, N = body.shapes.length; i < N; i++) {
      const shape = body.shapes[i];

      if (checkCollisionResponse && !shape.collisionResponse) {
        continue; // Skip
      }

      body.quaternion.mult(body.shapeOrientations[i], qi);
      body.quaternion.vmult(body.shapeOffsets[i], xi);
      xi.vadd(body.position, xi);
      this.intersectShape(shape, qi, xi, body);

      if (this.result.shouldStop) {
        break;
      }
    }
  }
  /**
   * Shoot a ray at an array bodies, get back information about the hit.
   * @param bodies An array of Body objects.
   * @deprecated @param result set the result property of the Ray instead.
   *
   */


  intersectBodies(bodies, result) {
    if (result) {
      this.result = result;
      this.updateDirection();
    }

    for (let i = 0, l = bodies.length; !this.result.shouldStop && i < l; i++) {
      this.intersectBody(bodies[i]);
    }
  }
  /**
   * Updates the direction vector.
   */


  updateDirection() {
    this.to.vsub(this.from, this.direction);
    this.direction.normalize();
  }

  intersectShape(shape, quat, position, body) {
    const from = this.from; // Checking boundingSphere

    const distance = distanceFromIntersection$1(from, this.direction, position);

    if (distance > shape.boundingSphereRadius) {
      return;
    }

    const intersectMethod = this[shape.type];

    if (intersectMethod) {
      intersectMethod.call(this, shape, quat, position, body, shape);
    }
  }

  _intersectBox(box, quat, position, body, reportedShape) {
    return this._intersectConvex(box.convexPolyhedronRepresentation, quat, position, body, reportedShape);
  }

  _intersectPlane(shape, quat, position, body, reportedShape) {
    const from = this.from;
    const to = this.to;
    const direction = this.direction; // Get plane normal

    const worldNormal = new Vec3$1(0, 0, 1);
    quat.vmult(worldNormal, worldNormal);
    const len = new Vec3$1();
    from.vsub(position, len);
    const planeToFrom = len.dot(worldNormal);
    to.vsub(position, len);
    const planeToTo = len.dot(worldNormal);

    if (planeToFrom * planeToTo > 0) {
      // "from" and "to" are on the same side of the plane... bail out
      return;
    }

    if (from.distanceTo(to) < planeToFrom) {
      return;
    }

    const n_dot_dir = worldNormal.dot(direction);

    if (Math.abs(n_dot_dir) < this.precision) {
      // No intersection
      return;
    }

    const planePointToFrom = new Vec3$1();
    const dir_scaled_with_t = new Vec3$1();
    const hitPointWorld = new Vec3$1();
    from.vsub(position, planePointToFrom);
    const t = -worldNormal.dot(planePointToFrom) / n_dot_dir;
    direction.scale(t, dir_scaled_with_t);
    from.vadd(dir_scaled_with_t, hitPointWorld);
    this.reportIntersection(worldNormal, hitPointWorld, reportedShape, body, -1);
  }
  /**
   * Get the world AABB of the ray.
   */


  getAABB(aabb) {
    const {
      lowerBound,
      upperBound
    } = aabb;
    const to = this.to;
    const from = this.from;
    lowerBound.x = Math.min(to.x, from.x);
    lowerBound.y = Math.min(to.y, from.y);
    lowerBound.z = Math.min(to.z, from.z);
    upperBound.x = Math.max(to.x, from.x);
    upperBound.y = Math.max(to.y, from.y);
    upperBound.z = Math.max(to.z, from.z);
  }

  _intersectHeightfield(shape, quat, position, body, reportedShape) {
    shape.data;
    shape.elementSize; // Convert the ray to local heightfield coordinates

    const localRay = intersectHeightfield_localRay$1; //new Ray(this.from, this.to);

    localRay.from.copy(this.from);
    localRay.to.copy(this.to);
    Transform$1.pointToLocalFrame(position, quat, localRay.from, localRay.from);
    Transform$1.pointToLocalFrame(position, quat, localRay.to, localRay.to);
    localRay.updateDirection(); // Get the index of the data points to test against

    const index = intersectHeightfield_index$1;
    let iMinX;
    let iMinY;
    let iMaxX;
    let iMaxY; // Set to max

    iMinX = iMinY = 0;
    iMaxX = iMaxY = shape.data.length - 1;
    const aabb = new AABB$1();
    localRay.getAABB(aabb);
    shape.getIndexOfPosition(aabb.lowerBound.x, aabb.lowerBound.y, index, true);
    iMinX = Math.max(iMinX, index[0]);
    iMinY = Math.max(iMinY, index[1]);
    shape.getIndexOfPosition(aabb.upperBound.x, aabb.upperBound.y, index, true);
    iMaxX = Math.min(iMaxX, index[0] + 1);
    iMaxY = Math.min(iMaxY, index[1] + 1);

    for (let i = iMinX; i < iMaxX; i++) {
      for (let j = iMinY; j < iMaxY; j++) {
        if (this.result.shouldStop) {
          return;
        }

        shape.getAabbAtIndex(i, j, aabb);

        if (!aabb.overlapsRay(localRay)) {
          continue;
        } // Lower triangle


        shape.getConvexTrianglePillar(i, j, false);
        Transform$1.pointToWorldFrame(position, quat, shape.pillarOffset, worldPillarOffset$1);

        this._intersectConvex(shape.pillarConvex, quat, worldPillarOffset$1, body, reportedShape, intersectConvexOptions$1);

        if (this.result.shouldStop) {
          return;
        } // Upper triangle


        shape.getConvexTrianglePillar(i, j, true);
        Transform$1.pointToWorldFrame(position, quat, shape.pillarOffset, worldPillarOffset$1);

        this._intersectConvex(shape.pillarConvex, quat, worldPillarOffset$1, body, reportedShape, intersectConvexOptions$1);
      }
    }
  }

  _intersectSphere(sphere, quat, position, body, reportedShape) {
    const from = this.from;
    const to = this.to;
    const r = sphere.radius;
    const a = (to.x - from.x) ** 2 + (to.y - from.y) ** 2 + (to.z - from.z) ** 2;
    const b = 2 * ((to.x - from.x) * (from.x - position.x) + (to.y - from.y) * (from.y - position.y) + (to.z - from.z) * (from.z - position.z));
    const c = (from.x - position.x) ** 2 + (from.y - position.y) ** 2 + (from.z - position.z) ** 2 - r ** 2;
    const delta = b ** 2 - 4 * a * c;
    const intersectionPoint = Ray_intersectSphere_intersectionPoint$1;
    const normal = Ray_intersectSphere_normal$1;

    if (delta < 0) {
      // No intersection
      return;
    } else if (delta === 0) {
      // single intersection point
      from.lerp(to, delta, intersectionPoint);
      intersectionPoint.vsub(position, normal);
      normal.normalize();
      this.reportIntersection(normal, intersectionPoint, reportedShape, body, -1);
    } else {
      const d1 = (-b - Math.sqrt(delta)) / (2 * a);
      const d2 = (-b + Math.sqrt(delta)) / (2 * a);

      if (d1 >= 0 && d1 <= 1) {
        from.lerp(to, d1, intersectionPoint);
        intersectionPoint.vsub(position, normal);
        normal.normalize();
        this.reportIntersection(normal, intersectionPoint, reportedShape, body, -1);
      }

      if (this.result.shouldStop) {
        return;
      }

      if (d2 >= 0 && d2 <= 1) {
        from.lerp(to, d2, intersectionPoint);
        intersectionPoint.vsub(position, normal);
        normal.normalize();
        this.reportIntersection(normal, intersectionPoint, reportedShape, body, -1);
      }
    }
  }

  _intersectConvex(shape, quat, position, body, reportedShape, options) {
    const normal = intersectConvex_normal$1;
    const vector = intersectConvex_vector$1;
    const faceList = options && options.faceList || null; // Checking faces

    const faces = shape.faces;
    const vertices = shape.vertices;
    const normals = shape.faceNormals;
    const direction = this.direction;
    const from = this.from;
    const to = this.to;
    const fromToDistance = from.distanceTo(to);
    const Nfaces = faceList ? faceList.length : faces.length;
    const result = this.result;

    for (let j = 0; !result.shouldStop && j < Nfaces; j++) {
      const fi = faceList ? faceList[j] : j;
      const face = faces[fi];
      const faceNormal = normals[fi];
      const q = quat;
      const x = position; // determine if ray intersects the plane of the face
      // note: this works regardless of the direction of the face normal
      // Get plane point in world coordinates...

      vector.copy(vertices[face[0]]);
      q.vmult(vector, vector);
      vector.vadd(x, vector); // ...but make it relative to the ray from. We'll fix this later.

      vector.vsub(from, vector); // Get plane normal

      q.vmult(faceNormal, normal); // If this dot product is negative, we have something interesting

      const dot = direction.dot(normal); // Bail out if ray and plane are parallel

      if (Math.abs(dot) < this.precision) {
        continue;
      } // calc distance to plane


      const scalar = normal.dot(vector) / dot; // if negative distance, then plane is behind ray

      if (scalar < 0) {
        continue;
      } // if (dot < 0) {
      // Intersection point is from + direction * scalar


      direction.scale(scalar, intersectPoint$1);
      intersectPoint$1.vadd(from, intersectPoint$1); // a is the point we compare points b and c with.

      a$1.copy(vertices[face[0]]);
      q.vmult(a$1, a$1);
      x.vadd(a$1, a$1);

      for (let i = 1; !result.shouldStop && i < face.length - 1; i++) {
        // Transform 3 vertices to world coords
        b$1.copy(vertices[face[i]]);
        c$1.copy(vertices[face[i + 1]]);
        q.vmult(b$1, b$1);
        q.vmult(c$1, c$1);
        x.vadd(b$1, b$1);
        x.vadd(c$1, c$1);
        const distance = intersectPoint$1.distanceTo(from);

        if (!(Ray$1.pointInTriangle(intersectPoint$1, a$1, b$1, c$1) || Ray$1.pointInTriangle(intersectPoint$1, b$1, a$1, c$1)) || distance > fromToDistance) {
          continue;
        }

        this.reportIntersection(normal, intersectPoint$1, reportedShape, body, fi);
      } // }

    }
  }
  /**
   * @todo Optimize by transforming the world to local space first.
   * @todo Use Octree lookup
   */


  _intersectTrimesh(mesh, quat, position, body, reportedShape, options) {
    const normal = intersectTrimesh_normal$1;
    const triangles = intersectTrimesh_triangles$1;
    const treeTransform = intersectTrimesh_treeTransform$1;
    const vector = intersectConvex_vector$1;
    const localDirection = intersectTrimesh_localDirection$1;
    const localFrom = intersectTrimesh_localFrom$1;
    const localTo = intersectTrimesh_localTo$1;
    const worldIntersectPoint = intersectTrimesh_worldIntersectPoint$1;
    const worldNormal = intersectTrimesh_worldNormal$1; // Checking faces

    const indices = mesh.indices;
    mesh.vertices; // const normals = mesh.faceNormals

    const from = this.from;
    const to = this.to;
    const direction = this.direction;
    treeTransform.position.copy(position);
    treeTransform.quaternion.copy(quat); // Transform ray to local space!

    Transform$1.vectorToLocalFrame(position, quat, direction, localDirection);
    Transform$1.pointToLocalFrame(position, quat, from, localFrom);
    Transform$1.pointToLocalFrame(position, quat, to, localTo);
    localTo.x *= mesh.scale.x;
    localTo.y *= mesh.scale.y;
    localTo.z *= mesh.scale.z;
    localFrom.x *= mesh.scale.x;
    localFrom.y *= mesh.scale.y;
    localFrom.z *= mesh.scale.z;
    localTo.vsub(localFrom, localDirection);
    localDirection.normalize();
    const fromToDistanceSquared = localFrom.distanceSquared(localTo);
    mesh.tree.rayQuery(this, treeTransform, triangles);

    for (let i = 0, N = triangles.length; !this.result.shouldStop && i !== N; i++) {
      const trianglesIndex = triangles[i];
      mesh.getNormal(trianglesIndex, normal); // determine if ray intersects the plane of the face
      // note: this works regardless of the direction of the face normal
      // Get plane point in world coordinates...

      mesh.getVertex(indices[trianglesIndex * 3], a$1); // ...but make it relative to the ray from. We'll fix this later.

      a$1.vsub(localFrom, vector); // If this dot product is negative, we have something interesting

      const dot = localDirection.dot(normal); // Bail out if ray and plane are parallel
      // if (Math.abs( dot ) < this.precision){
      //     continue;
      // }
      // calc distance to plane

      const scalar = normal.dot(vector) / dot; // if negative distance, then plane is behind ray

      if (scalar < 0) {
        continue;
      } // Intersection point is from + direction * scalar


      localDirection.scale(scalar, intersectPoint$1);
      intersectPoint$1.vadd(localFrom, intersectPoint$1); // Get triangle vertices

      mesh.getVertex(indices[trianglesIndex * 3 + 1], b$1);
      mesh.getVertex(indices[trianglesIndex * 3 + 2], c$1);
      const squaredDistance = intersectPoint$1.distanceSquared(localFrom);

      if (!(Ray$1.pointInTriangle(intersectPoint$1, b$1, a$1, c$1) || Ray$1.pointInTriangle(intersectPoint$1, a$1, b$1, c$1)) || squaredDistance > fromToDistanceSquared) {
        continue;
      } // transform intersectpoint and normal to world


      Transform$1.vectorToWorldFrame(quat, normal, worldNormal);
      Transform$1.pointToWorldFrame(position, quat, intersectPoint$1, worldIntersectPoint);
      this.reportIntersection(worldNormal, worldIntersectPoint, reportedShape, body, trianglesIndex);
    }

    triangles.length = 0;
  }
  /**
   * @return True if the intersections should continue
   */


  reportIntersection(normal, hitPointWorld, shape, body, hitFaceIndex) {
    const from = this.from;
    const to = this.to;
    const distance = from.distanceTo(hitPointWorld);
    const result = this.result; // Skip back faces?

    if (this.skipBackfaces && normal.dot(this.direction) > 0) {
      return;
    }

    result.hitFaceIndex = typeof hitFaceIndex !== 'undefined' ? hitFaceIndex : -1;

    switch (this.mode) {
      case Ray$1.ALL:
        this.hasHit = true;
        result.set(from, to, normal, hitPointWorld, shape, body, distance);
        result.hasHit = true;
        this.callback(result);
        break;

      case Ray$1.CLOSEST:
        // Store if closer than current closest
        if (distance < result.distance || !result.hasHit) {
          this.hasHit = true;
          result.hasHit = true;
          result.set(from, to, normal, hitPointWorld, shape, body, distance);
        }

        break;

      case Ray$1.ANY:
        // Report and stop.
        this.hasHit = true;
        result.hasHit = true;
        result.set(from, to, normal, hitPointWorld, shape, body, distance);
        result.shouldStop = true;
        break;
    }
  }
  /**
   * As per "Barycentric Technique" as named
   * {@link https://www.blackpawn.com/texts/pointinpoly/default.html here} but without the division
   */


  static pointInTriangle(p, a, b, c) {
    c.vsub(a, v0$1);
    b.vsub(a, v1$1);
    p.vsub(a, v2$1);
    const dot00 = v0$1.dot(v0$1);
    const dot01 = v0$1.dot(v1$1);
    const dot02 = v0$1.dot(v2$1);
    const dot11 = v1$1.dot(v1$1);
    const dot12 = v1$1.dot(v2$1);
    let u;
    let v;
    return (u = dot11 * dot02 - dot01 * dot12) >= 0 && (v = dot00 * dot12 - dot01 * dot02) >= 0 && u + v < dot00 * dot11 - dot01 * dot01;
  }

}
Ray$1.CLOSEST = RAY_MODES$1.CLOSEST;
Ray$1.ANY = RAY_MODES$1.ANY;
Ray$1.ALL = RAY_MODES$1.ALL;
const tmpAABB$1$1 = new AABB$1();
const tmpArray$1 = [];
const v1$1 = new Vec3$1();
const v2$1 = new Vec3$1();
const intersectBody_xi$1 = new Vec3$1();
const intersectBody_qi$1 = new Quaternion$1();
const intersectPoint$1 = new Vec3$1();
const a$1 = new Vec3$1();
const b$1 = new Vec3$1();
const c$1 = new Vec3$1();
new Vec3$1();
new RaycastResult$1();
const intersectConvexOptions$1 = {
  faceList: [0]
};
const worldPillarOffset$1 = new Vec3$1();
const intersectHeightfield_localRay$1 = new Ray$1();
const intersectHeightfield_index$1 = [];
const Ray_intersectSphere_intersectionPoint$1 = new Vec3$1();
const Ray_intersectSphere_normal$1 = new Vec3$1();
const intersectConvex_normal$1 = new Vec3$1();
new Vec3$1();
new Vec3$1();
const intersectConvex_vector$1 = new Vec3$1();
const intersectTrimesh_normal$1 = new Vec3$1();
const intersectTrimesh_localDirection$1 = new Vec3$1();
const intersectTrimesh_localFrom$1 = new Vec3$1();
const intersectTrimesh_localTo$1 = new Vec3$1();
const intersectTrimesh_worldNormal$1 = new Vec3$1();
const intersectTrimesh_worldIntersectPoint$1 = new Vec3$1();
new AABB$1();
const intersectTrimesh_triangles$1 = [];
const intersectTrimesh_treeTransform$1 = new Transform$1();
const v0$1 = new Vec3$1();
const intersect$1 = new Vec3$1();

function distanceFromIntersection$1(from, direction, position) {
  // v0 is vector from from to position
  position.vsub(from, v0$1);
  const dot = v0$1.dot(direction); // intersect = direction*dot + from

  direction.scale(dot, intersect$1);
  intersect$1.vadd(from, intersect$1);
  const distance = position.distanceTo(intersect$1);
  return distance;
}

class Utils {
  /**
   * Extend an options object with default values.
   * @param options The options object. May be falsy: in this case, a new object is created and returned.
   * @param defaults An object containing default values.
   * @return The modified options object.
   */
  static defaults(options, defaults) {
    if (options === void 0) {
      options = {};
    }

    for (let key in defaults) {
      if (!(key in options)) {
        options[key] = defaults[key];
      }
    }

    return options;
  }

}
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1(); // Temp vectors

new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();

/**
 * Defines a physics material.
 */
class Material {
  /**
   * Material name.
   * If options is a string, name will be set to that string.
   * @todo Deprecate this
   */

  /** Material id. */

  /**
   * Friction for this material.
   * If non-negative, it will be used instead of the friction given by ContactMaterials. If there's no matching ContactMaterial, the value from `defaultContactMaterial` in the World will be used.
   */

  /**
   * Restitution for this material.
   * If non-negative, it will be used instead of the restitution given by ContactMaterials. If there's no matching ContactMaterial, the value from `defaultContactMaterial` in the World will be used.
   */
  constructor(options) {
    if (options === void 0) {
      options = {};
    }

    let name = ''; // Backwards compatibility fix

    if (typeof options === 'string') {
      //console.warn(`Passing a string to MaterialOptions is deprecated, and has no effect`)
      name = options;
      options = {};
    }

    this.name = name;
    this.id = Material.idCounter++;
    this.friction = typeof options.friction !== 'undefined' ? options.friction : -1;
    this.restitution = typeof options.restitution !== 'undefined' ? options.restitution : -1;
  }

}
Material.idCounter = 0;
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Ray$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
[new Vec3$1(1, 0, 0), new Vec3$1(0, 1, 0), new Vec3$1(0, 0, 1)];
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();

new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();

new Vec3$1();
new Vec3$1();
new Vec3$1(); // bilateral constraint between two dynamic objects

/**
 * Spherical shape
 * @example
 *     const radius = 1
 *     const sphereShape = new CANNON.Sphere(radius)
 *     const sphereBody = new CANNON.Body({ mass: 1, shape: sphereShape })
 *     world.addBody(sphereBody)
 */
class Sphere extends Shape$1 {
  /**
   * The radius of the sphere.
   */

  /**
   *
   * @param radius The radius of the sphere, a non-negative number.
   */
  constructor(radius) {
    super({
      type: Shape$1.types.SPHERE
    });
    this.radius = radius !== undefined ? radius : 1.0;

    if (this.radius < 0) {
      throw new Error('The sphere radius cannot be negative.');
    }

    this.updateBoundingSphereRadius();
  }
  /** calculateLocalInertia */


  calculateLocalInertia(mass, target) {
    if (target === void 0) {
      target = new Vec3$1();
    }

    const I = 2.0 * mass * this.radius * this.radius / 5.0;
    target.x = I;
    target.y = I;
    target.z = I;
    return target;
  }
  /** volume */


  volume() {
    return 4.0 * Math.PI * Math.pow(this.radius, 3) / 3.0;
  }

  updateBoundingSphereRadius() {
    this.boundingSphereRadius = this.radius;
  }

  calculateWorldAABB(pos, quat, min, max) {
    const r = this.radius;
    const axes = ['x', 'y', 'z'];

    for (let i = 0; i < axes.length; i++) {
      const ax = axes[i];
      min[ax] = pos[ax] - r;
      max[ax] = pos[ax] + r;
    }
  }

}
new Vec3$1();
new Vec3$1();
new Vec3$1(); // Temp vectors for calculation

new Vec3$1(); // Relative velocity

new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();

/**
 * Cylinder class.
 * @example
 *     const radiusTop = 0.5
 *     const radiusBottom = 0.5
 *     const height = 2
 *     const numSegments = 12
 *     const cylinderShape = new CANNON.Cylinder(radiusTop, radiusBottom, height, numSegments)
 *     const cylinderBody = new CANNON.Body({ mass: 1, shape: cylinderShape })
 *     world.addBody(cylinderBody)
 */

class Cylinder extends ConvexPolyhedron {
  /** The radius of the top of the Cylinder. */

  /** The radius of the bottom of the Cylinder. */

  /** The height of the Cylinder. */

  /** The number of segments to build the cylinder out of. */

  /**
   * @param radiusTop The radius of the top of the Cylinder.
   * @param radiusBottom The radius of the bottom of the Cylinder.
   * @param height The height of the Cylinder.
   * @param numSegments The number of segments to build the cylinder out of.
   */
  constructor(radiusTop, radiusBottom, height, numSegments) {
    if (radiusTop === void 0) {
      radiusTop = 1;
    }

    if (radiusBottom === void 0) {
      radiusBottom = 1;
    }

    if (height === void 0) {
      height = 1;
    }

    if (numSegments === void 0) {
      numSegments = 8;
    }

    if (radiusTop < 0) {
      throw new Error('The cylinder radiusTop cannot be negative.');
    }

    if (radiusBottom < 0) {
      throw new Error('The cylinder radiusBottom cannot be negative.');
    }

    const N = numSegments;
    const vertices = [];
    const axes = [];
    const faces = [];
    const bottomface = [];
    const topface = [];
    const cos = Math.cos;
    const sin = Math.sin; // First bottom point

    vertices.push(new Vec3$1(-radiusBottom * sin(0), -height * 0.5, radiusBottom * cos(0)));
    bottomface.push(0); // First top point

    vertices.push(new Vec3$1(-radiusTop * sin(0), height * 0.5, radiusTop * cos(0)));
    topface.push(1);

    for (let i = 0; i < N; i++) {
      const theta = 2 * Math.PI / N * (i + 1);
      const thetaN = 2 * Math.PI / N * (i + 0.5);

      if (i < N - 1) {
        // Bottom
        vertices.push(new Vec3$1(-radiusBottom * sin(theta), -height * 0.5, radiusBottom * cos(theta)));
        bottomface.push(2 * i + 2); // Top

        vertices.push(new Vec3$1(-radiusTop * sin(theta), height * 0.5, radiusTop * cos(theta)));
        topface.push(2 * i + 3); // Face

        faces.push([2 * i, 2 * i + 1, 2 * i + 3, 2 * i + 2]);
      } else {
        faces.push([2 * i, 2 * i + 1, 1, 0]); // Connect
      } // Axis: we can cut off half of them if we have even number of segments


      if (N % 2 === 1 || i < N / 2) {
        axes.push(new Vec3$1(-sin(thetaN), 0, cos(thetaN)));
      }
    }

    faces.push(bottomface);
    axes.push(new Vec3$1(0, 1, 0)); // Reorder top face

    const temp = [];

    for (let i = 0; i < topface.length; i++) {
      temp.push(topface[topface.length - i - 1]);
    }

    faces.push(temp);
    super({
      vertices,
      faces,
      axes
    });
    this.type = Shape$1.types.CYLINDER;
    this.radiusTop = radiusTop;
    this.radiusBottom = radiusBottom;
    this.height = height;
    this.numSegments = numSegments;
  }

}

/**
 * Particle shape.
 * @example
 *     const particleShape = new CANNON.Particle()
 *     const particleBody = new CANNON.Body({ mass: 1, shape: particleShape })
 *     world.addBody(particleBody)
 */
class Particle extends Shape$1 {
  constructor() {
    super({
      type: Shape$1.types.PARTICLE
    });
  }
  /**
   * calculateLocalInertia
   */


  calculateLocalInertia(mass, target) {
    if (target === void 0) {
      target = new Vec3$1();
    }

    target.set(0, 0, 0);
    return target;
  }

  volume() {
    return 0;
  }

  updateBoundingSphereRadius() {
    this.boundingSphereRadius = 0;
  }

  calculateWorldAABB(pos, quat, min, max) {
    // Get each axis max
    min.copy(pos);
    max.copy(pos);
  }

}

/**
 * A plane, facing in the Z direction. The plane has its surface at z=0 and everything below z=0 is assumed to be solid plane. To make the plane face in some other direction than z, you must put it inside a Body and rotate that body. See the demos.
 * @example
 *     const planeShape = new CANNON.Plane()
 *     const planeBody = new CANNON.Body({ mass: 0, shape:  planeShape })
 *     planeBody.quaternion.setFromEuler(-Math.PI / 2, 0, 0) // make it face up
 *     world.addBody(planeBody)
 */
class Plane extends Shape$1 {
  /** worldNormal */

  /** worldNormalNeedsUpdate */
  constructor() {
    super({
      type: Shape$1.types.PLANE
    }); // World oriented normal

    this.worldNormal = new Vec3$1();
    this.worldNormalNeedsUpdate = true;
    this.boundingSphereRadius = Number.MAX_VALUE;
  }
  /** computeWorldNormal */


  computeWorldNormal(quat) {
    const n = this.worldNormal;
    n.set(0, 0, 1);
    quat.vmult(n, n);
    this.worldNormalNeedsUpdate = false;
  }

  calculateLocalInertia(mass, target) {
    if (target === void 0) {
      target = new Vec3$1();
    }

    return target;
  }

  volume() {
    return (// The plane is infinite...
      Number.MAX_VALUE
    );
  }

  calculateWorldAABB(pos, quat, min, max) {
    // The plane AABB is infinite, except if the normal is pointing along any axis
    tempNormal.set(0, 0, 1); // Default plane normal is z

    quat.vmult(tempNormal, tempNormal);
    const maxVal = Number.MAX_VALUE;
    min.set(-maxVal, -maxVal, -maxVal);
    max.set(maxVal, maxVal, maxVal);

    if (tempNormal.x === 1) {
      max.x = pos.x;
    } else if (tempNormal.x === -1) {
      min.x = pos.x;
    }

    if (tempNormal.y === 1) {
      max.y = pos.y;
    } else if (tempNormal.y === -1) {
      min.y = pos.y;
    }

    if (tempNormal.z === 1) {
      max.z = pos.z;
    } else if (tempNormal.z === -1) {
      min.z = pos.z;
    }
  }

  updateBoundingSphereRadius() {
    this.boundingSphereRadius = Number.MAX_VALUE;
  }

}
const tempNormal = new Vec3$1();

/**
 * Heightfield shape class. Height data is given as an array. These data points are spread out evenly with a given distance.
 * @todo Should be possible to use along all axes, not just y
 * @todo should be possible to scale along all axes
 * @todo Refactor elementSize to elementSizeX and elementSizeY
 *
 * @example
 *     // Generate some height data (y-values).
 *     const data = []
 *     for (let i = 0; i < 1000; i++) {
 *         const y = 0.5 * Math.cos(0.2 * i)
 *         data.push(y)
 *     }
 *
 *     // Create the heightfield shape
 *     const heightfieldShape = new CANNON.Heightfield(data, {
 *         elementSize: 1 // Distance between the data points in X and Y directions
 *     })
 *     const heightfieldBody = new CANNON.Body({ shape: heightfieldShape })
 *     world.addBody(heightfieldBody)
 */
class Heightfield extends Shape$1 {
  /**
   * An array of numbers, or height values, that are spread out along the x axis.
   */

  /**
   * Max value of the data points in the data array.
   */

  /**
   * Minimum value of the data points in the data array.
   */

  /**
   * World spacing between the data points in X and Y direction.
   * @todo elementSizeX and Y
   * @default 1
   */

  /**
   * @default true
   */

  /**
   * @param data An array of numbers, or height values, that are spread out along the x axis.
   */
  constructor(data, options) {
    if (options === void 0) {
      options = {};
    }

    options = Utils.defaults(options, {
      maxValue: null,
      minValue: null,
      elementSize: 1
    });
    super({
      type: Shape$1.types.HEIGHTFIELD
    });
    this.data = data;
    this.maxValue = options.maxValue;
    this.minValue = options.minValue;
    this.elementSize = options.elementSize;

    if (options.minValue === null) {
      this.updateMinValue();
    }

    if (options.maxValue === null) {
      this.updateMaxValue();
    }

    this.cacheEnabled = true;
    this.pillarConvex = new ConvexPolyhedron();
    this.pillarOffset = new Vec3$1();
    this.updateBoundingSphereRadius(); // "i_j_isUpper" => { convex: ..., offset: ... }
    // for example:
    // _cachedPillars["0_2_1"]

    this._cachedPillars = {};
  }
  /**
   * Call whenever you change the data array.
   */


  update() {
    this._cachedPillars = {};
  }
  /**
   * Update the `minValue` property
   */


  updateMinValue() {
    const data = this.data;
    let minValue = data[0][0];

    for (let i = 0; i !== data.length; i++) {
      for (let j = 0; j !== data[i].length; j++) {
        const v = data[i][j];

        if (v < minValue) {
          minValue = v;
        }
      }
    }

    this.minValue = minValue;
  }
  /**
   * Update the `maxValue` property
   */


  updateMaxValue() {
    const data = this.data;
    let maxValue = data[0][0];

    for (let i = 0; i !== data.length; i++) {
      for (let j = 0; j !== data[i].length; j++) {
        const v = data[i][j];

        if (v > maxValue) {
          maxValue = v;
        }
      }
    }

    this.maxValue = maxValue;
  }
  /**
   * Set the height value at an index. Don't forget to update maxValue and minValue after you're done.
   */


  setHeightValueAtIndex(xi, yi, value) {
    const data = this.data;
    data[xi][yi] = value; // Invalidate cache

    this.clearCachedConvexTrianglePillar(xi, yi, false);

    if (xi > 0) {
      this.clearCachedConvexTrianglePillar(xi - 1, yi, true);
      this.clearCachedConvexTrianglePillar(xi - 1, yi, false);
    }

    if (yi > 0) {
      this.clearCachedConvexTrianglePillar(xi, yi - 1, true);
      this.clearCachedConvexTrianglePillar(xi, yi - 1, false);
    }

    if (yi > 0 && xi > 0) {
      this.clearCachedConvexTrianglePillar(xi - 1, yi - 1, true);
    }
  }
  /**
   * Get max/min in a rectangle in the matrix data
   * @param result An array to store the results in.
   * @return The result array, if it was passed in. Minimum will be at position 0 and max at 1.
   */


  getRectMinMax(iMinX, iMinY, iMaxX, iMaxY, result) {
    if (result === void 0) {
      result = [];
    }

    // Get max and min of the data
    const data = this.data; // Set first value

    let max = this.minValue;

    for (let i = iMinX; i <= iMaxX; i++) {
      for (let j = iMinY; j <= iMaxY; j++) {
        const height = data[i][j];

        if (height > max) {
          max = height;
        }
      }
    }

    result[0] = this.minValue;
    result[1] = max;
  }
  /**
   * Get the index of a local position on the heightfield. The indexes indicate the rectangles, so if your terrain is made of N x N height data points, you will have rectangle indexes ranging from 0 to N-1.
   * @param result Two-element array
   * @param clamp If the position should be clamped to the heightfield edge.
   */


  getIndexOfPosition(x, y, result, clamp) {
    // Get the index of the data points to test against
    const w = this.elementSize;
    const data = this.data;
    let xi = Math.floor(x / w);
    let yi = Math.floor(y / w);
    result[0] = xi;
    result[1] = yi;

    if (clamp) {
      // Clamp index to edges
      if (xi < 0) {
        xi = 0;
      }

      if (yi < 0) {
        yi = 0;
      }

      if (xi >= data.length - 1) {
        xi = data.length - 1;
      }

      if (yi >= data[0].length - 1) {
        yi = data[0].length - 1;
      }
    } // Bail out if we are out of the terrain


    if (xi < 0 || yi < 0 || xi >= data.length - 1 || yi >= data[0].length - 1) {
      return false;
    }

    return true;
  }

  getTriangleAt(x, y, edgeClamp, a, b, c) {
    const idx = getHeightAt_idx;
    this.getIndexOfPosition(x, y, idx, edgeClamp);
    let xi = idx[0];
    let yi = idx[1];
    const data = this.data;

    if (edgeClamp) {
      xi = Math.min(data.length - 2, Math.max(0, xi));
      yi = Math.min(data[0].length - 2, Math.max(0, yi));
    }

    const elementSize = this.elementSize;
    const lowerDist2 = (x / elementSize - xi) ** 2 + (y / elementSize - yi) ** 2;
    const upperDist2 = (x / elementSize - (xi + 1)) ** 2 + (y / elementSize - (yi + 1)) ** 2;
    const upper = lowerDist2 > upperDist2;
    this.getTriangle(xi, yi, upper, a, b, c);
    return upper;
  }

  getNormalAt(x, y, edgeClamp, result) {
    const a = getNormalAt_a;
    const b = getNormalAt_b;
    const c = getNormalAt_c;
    const e0 = getNormalAt_e0;
    const e1 = getNormalAt_e1;
    this.getTriangleAt(x, y, edgeClamp, a, b, c);
    b.vsub(a, e0);
    c.vsub(a, e1);
    e0.cross(e1, result);
    result.normalize();
  }
  /**
   * Get an AABB of a square in the heightfield
   * @param xi
   * @param yi
   * @param result
   */


  getAabbAtIndex(xi, yi, _ref) {
    let {
      lowerBound,
      upperBound
    } = _ref;
    const data = this.data;
    const elementSize = this.elementSize;
    lowerBound.set(xi * elementSize, yi * elementSize, data[xi][yi]);
    upperBound.set((xi + 1) * elementSize, (yi + 1) * elementSize, data[xi + 1][yi + 1]);
  }
  /**
   * Get the height in the heightfield at a given position
   */


  getHeightAt(x, y, edgeClamp) {
    const data = this.data;
    const a = getHeightAt_a;
    const b = getHeightAt_b;
    const c = getHeightAt_c;
    const idx = getHeightAt_idx;
    this.getIndexOfPosition(x, y, idx, edgeClamp);
    let xi = idx[0];
    let yi = idx[1];

    if (edgeClamp) {
      xi = Math.min(data.length - 2, Math.max(0, xi));
      yi = Math.min(data[0].length - 2, Math.max(0, yi));
    }

    const upper = this.getTriangleAt(x, y, edgeClamp, a, b, c);
    barycentricWeights(x, y, a.x, a.y, b.x, b.y, c.x, c.y, getHeightAt_weights);
    const w = getHeightAt_weights;

    if (upper) {
      // Top triangle verts
      return data[xi + 1][yi + 1] * w.x + data[xi][yi + 1] * w.y + data[xi + 1][yi] * w.z;
    } else {
      // Top triangle verts
      return data[xi][yi] * w.x + data[xi + 1][yi] * w.y + data[xi][yi + 1] * w.z;
    }
  }

  getCacheConvexTrianglePillarKey(xi, yi, getUpperTriangle) {
    return `${xi}_${yi}_${getUpperTriangle ? 1 : 0}`;
  }

  getCachedConvexTrianglePillar(xi, yi, getUpperTriangle) {
    return this._cachedPillars[this.getCacheConvexTrianglePillarKey(xi, yi, getUpperTriangle)];
  }

  setCachedConvexTrianglePillar(xi, yi, getUpperTriangle, convex, offset) {
    this._cachedPillars[this.getCacheConvexTrianglePillarKey(xi, yi, getUpperTriangle)] = {
      convex,
      offset
    };
  }

  clearCachedConvexTrianglePillar(xi, yi, getUpperTriangle) {
    delete this._cachedPillars[this.getCacheConvexTrianglePillarKey(xi, yi, getUpperTriangle)];
  }
  /**
   * Get a triangle from the heightfield
   */


  getTriangle(xi, yi, upper, a, b, c) {
    const data = this.data;
    const elementSize = this.elementSize;

    if (upper) {
      // Top triangle verts
      a.set((xi + 1) * elementSize, (yi + 1) * elementSize, data[xi + 1][yi + 1]);
      b.set(xi * elementSize, (yi + 1) * elementSize, data[xi][yi + 1]);
      c.set((xi + 1) * elementSize, yi * elementSize, data[xi + 1][yi]);
    } else {
      // Top triangle verts
      a.set(xi * elementSize, yi * elementSize, data[xi][yi]);
      b.set((xi + 1) * elementSize, yi * elementSize, data[xi + 1][yi]);
      c.set(xi * elementSize, (yi + 1) * elementSize, data[xi][yi + 1]);
    }
  }
  /**
   * Get a triangle in the terrain in the form of a triangular convex shape.
   */


  getConvexTrianglePillar(xi, yi, getUpperTriangle) {
    let result = this.pillarConvex;
    let offsetResult = this.pillarOffset;

    if (this.cacheEnabled) {
      const data = this.getCachedConvexTrianglePillar(xi, yi, getUpperTriangle);

      if (data) {
        this.pillarConvex = data.convex;
        this.pillarOffset = data.offset;
        return;
      }

      result = new ConvexPolyhedron();
      offsetResult = new Vec3$1();
      this.pillarConvex = result;
      this.pillarOffset = offsetResult;
    }

    const data = this.data;
    const elementSize = this.elementSize;
    const faces = result.faces; // Reuse verts if possible

    result.vertices.length = 6;

    for (let i = 0; i < 6; i++) {
      if (!result.vertices[i]) {
        result.vertices[i] = new Vec3$1();
      }
    } // Reuse faces if possible


    faces.length = 5;

    for (let i = 0; i < 5; i++) {
      if (!faces[i]) {
        faces[i] = [];
      }
    }

    const verts = result.vertices;
    const h = (Math.min(data[xi][yi], data[xi + 1][yi], data[xi][yi + 1], data[xi + 1][yi + 1]) - this.minValue) / 2 + this.minValue;

    if (!getUpperTriangle) {
      // Center of the triangle pillar - all polygons are given relative to this one
      offsetResult.set((xi + 0.25) * elementSize, // sort of center of a triangle
      (yi + 0.25) * elementSize, h // vertical center
      ); // Top triangle verts

      verts[0].set(-0.25 * elementSize, -0.25 * elementSize, data[xi][yi] - h);
      verts[1].set(0.75 * elementSize, -0.25 * elementSize, data[xi + 1][yi] - h);
      verts[2].set(-0.25 * elementSize, 0.75 * elementSize, data[xi][yi + 1] - h); // bottom triangle verts

      verts[3].set(-0.25 * elementSize, -0.25 * elementSize, -Math.abs(h) - 1);
      verts[4].set(0.75 * elementSize, -0.25 * elementSize, -Math.abs(h) - 1);
      verts[5].set(-0.25 * elementSize, 0.75 * elementSize, -Math.abs(h) - 1); // top triangle

      faces[0][0] = 0;
      faces[0][1] = 1;
      faces[0][2] = 2; // bottom triangle

      faces[1][0] = 5;
      faces[1][1] = 4;
      faces[1][2] = 3; // -x facing quad

      faces[2][0] = 0;
      faces[2][1] = 2;
      faces[2][2] = 5;
      faces[2][3] = 3; // -y facing quad

      faces[3][0] = 1;
      faces[3][1] = 0;
      faces[3][2] = 3;
      faces[3][3] = 4; // +xy facing quad

      faces[4][0] = 4;
      faces[4][1] = 5;
      faces[4][2] = 2;
      faces[4][3] = 1;
    } else {
      // Center of the triangle pillar - all polygons are given relative to this one
      offsetResult.set((xi + 0.75) * elementSize, // sort of center of a triangle
      (yi + 0.75) * elementSize, h // vertical center
      ); // Top triangle verts

      verts[0].set(0.25 * elementSize, 0.25 * elementSize, data[xi + 1][yi + 1] - h);
      verts[1].set(-0.75 * elementSize, 0.25 * elementSize, data[xi][yi + 1] - h);
      verts[2].set(0.25 * elementSize, -0.75 * elementSize, data[xi + 1][yi] - h); // bottom triangle verts

      verts[3].set(0.25 * elementSize, 0.25 * elementSize, -Math.abs(h) - 1);
      verts[4].set(-0.75 * elementSize, 0.25 * elementSize, -Math.abs(h) - 1);
      verts[5].set(0.25 * elementSize, -0.75 * elementSize, -Math.abs(h) - 1); // Top triangle

      faces[0][0] = 0;
      faces[0][1] = 1;
      faces[0][2] = 2; // bottom triangle

      faces[1][0] = 5;
      faces[1][1] = 4;
      faces[1][2] = 3; // +x facing quad

      faces[2][0] = 2;
      faces[2][1] = 5;
      faces[2][2] = 3;
      faces[2][3] = 0; // +y facing quad

      faces[3][0] = 3;
      faces[3][1] = 4;
      faces[3][2] = 1;
      faces[3][3] = 0; // -xy facing quad

      faces[4][0] = 1;
      faces[4][1] = 4;
      faces[4][2] = 5;
      faces[4][3] = 2;
    }

    result.computeNormals();
    result.computeEdges();
    result.updateBoundingSphereRadius();
    this.setCachedConvexTrianglePillar(xi, yi, getUpperTriangle, result, offsetResult);
  }

  calculateLocalInertia(mass, target) {
    if (target === void 0) {
      target = new Vec3$1();
    }

    target.set(0, 0, 0);
    return target;
  }

  volume() {
    return (// The terrain is infinite
      Number.MAX_VALUE
    );
  }

  calculateWorldAABB(pos, quat, min, max) {
    /** @TODO do it properly */
    min.set(-Number.MAX_VALUE, -Number.MAX_VALUE, -Number.MAX_VALUE);
    max.set(Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE);
  }

  updateBoundingSphereRadius() {
    // Use the bounding box of the min/max values
    const data = this.data;
    const s = this.elementSize;
    this.boundingSphereRadius = new Vec3$1(data.length * s, data[0].length * s, Math.max(Math.abs(this.maxValue), Math.abs(this.minValue))).length();
  }
  /**
   * Sets the height values from an image. Currently only supported in browser.
   */


  setHeightsFromImage(image, scale) {
    const {
      x,
      z,
      y
    } = scale;
    const canvas = document.createElement('canvas');
    canvas.width = image.width;
    canvas.height = image.height;
    const context = canvas.getContext('2d');
    context.drawImage(image, 0, 0);
    const imageData = context.getImageData(0, 0, image.width, image.height);
    const matrix = this.data;
    matrix.length = 0;
    this.elementSize = Math.abs(x) / imageData.width;

    for (let i = 0; i < imageData.height; i++) {
      const row = [];

      for (let j = 0; j < imageData.width; j++) {
        const a = imageData.data[(i * imageData.height + j) * 4];
        const b = imageData.data[(i * imageData.height + j) * 4 + 1];
        const c = imageData.data[(i * imageData.height + j) * 4 + 2];
        const height = (a + b + c) / 4 / 255 * z;

        if (x < 0) {
          row.push(height);
        } else {
          row.unshift(height);
        }
      }

      if (y < 0) {
        matrix.unshift(row);
      } else {
        matrix.push(row);
      }
    }

    this.updateMaxValue();
    this.updateMinValue();
    this.update();
  }

}
const getHeightAt_idx = [];
const getHeightAt_weights = new Vec3$1();
const getHeightAt_a = new Vec3$1();
const getHeightAt_b = new Vec3$1();
const getHeightAt_c = new Vec3$1();
const getNormalAt_a = new Vec3$1();
const getNormalAt_b = new Vec3$1();
const getNormalAt_c = new Vec3$1();
const getNormalAt_e0 = new Vec3$1();
const getNormalAt_e1 = new Vec3$1(); // from https://en.wikipedia.org/wiki/Barycentric_coordinate_system

function barycentricWeights(x, y, ax, ay, bx, by, cx, cy, result) {
  result.x = ((by - cy) * (x - cx) + (cx - bx) * (y - cy)) / ((by - cy) * (ax - cx) + (cx - bx) * (ay - cy));
  result.y = ((cy - ay) * (x - cx) + (ax - cx) * (y - cy)) / ((by - cy) * (ax - cx) + (cx - bx) * (ay - cy));
  result.z = 1 - result.x - result.y;
}

/**
 * OctreeNode
 */
class OctreeNode {
  /** The root node */

  /** Boundary of this node */

  /** Contained data at the current node level */

  /** Children to this node */
  constructor(options) {
    if (options === void 0) {
      options = {};
    }

    this.root = options.root || null;
    this.aabb = options.aabb ? options.aabb.clone() : new AABB$1();
    this.data = [];
    this.children = [];
  }
  /**
   * reset
   */


  reset() {
    this.children.length = this.data.length = 0;
  }
  /**
   * Insert data into this node
   * @return True if successful, otherwise false
   */


  insert(aabb, elementData, level) {
    if (level === void 0) {
      level = 0;
    }

    const nodeData = this.data; // Ignore objects that do not belong in this node

    if (!this.aabb.contains(aabb)) {
      return false; // object cannot be added
    }

    const children = this.children;
    const maxDepth = this.maxDepth || this.root.maxDepth;

    if (level < maxDepth) {
      // Subdivide if there are no children yet
      let subdivided = false;

      if (!children.length) {
        this.subdivide();
        subdivided = true;
      } // add to whichever node will accept it


      for (let i = 0; i !== 8; i++) {
        if (children[i].insert(aabb, elementData, level + 1)) {
          return true;
        }
      }

      if (subdivided) {
        // No children accepted! Might as well just remove em since they contain none
        children.length = 0;
      }
    } // Too deep, or children didnt want it. add it in current node


    nodeData.push(elementData);
    return true;
  }
  /**
   * Create 8 equally sized children nodes and put them in the `children` array.
   */


  subdivide() {
    const aabb = this.aabb;
    const l = aabb.lowerBound;
    const u = aabb.upperBound;
    const children = this.children;
    children.push(new OctreeNode({
      aabb: new AABB$1({
        lowerBound: new Vec3$1(0, 0, 0)
      })
    }), new OctreeNode({
      aabb: new AABB$1({
        lowerBound: new Vec3$1(1, 0, 0)
      })
    }), new OctreeNode({
      aabb: new AABB$1({
        lowerBound: new Vec3$1(1, 1, 0)
      })
    }), new OctreeNode({
      aabb: new AABB$1({
        lowerBound: new Vec3$1(1, 1, 1)
      })
    }), new OctreeNode({
      aabb: new AABB$1({
        lowerBound: new Vec3$1(0, 1, 1)
      })
    }), new OctreeNode({
      aabb: new AABB$1({
        lowerBound: new Vec3$1(0, 0, 1)
      })
    }), new OctreeNode({
      aabb: new AABB$1({
        lowerBound: new Vec3$1(1, 0, 1)
      })
    }), new OctreeNode({
      aabb: new AABB$1({
        lowerBound: new Vec3$1(0, 1, 0)
      })
    }));
    u.vsub(l, halfDiagonal);
    halfDiagonal.scale(0.5, halfDiagonal);
    const root = this.root || this;

    for (let i = 0; i !== 8; i++) {
      const child = children[i]; // Set current node as root

      child.root = root; // Compute bounds

      const lowerBound = child.aabb.lowerBound;
      lowerBound.x *= halfDiagonal.x;
      lowerBound.y *= halfDiagonal.y;
      lowerBound.z *= halfDiagonal.z;
      lowerBound.vadd(l, lowerBound); // Upper bound is always lower bound + halfDiagonal

      lowerBound.vadd(halfDiagonal, child.aabb.upperBound);
    }
  }
  /**
   * Get all data, potentially within an AABB
   * @return The "result" object
   */


  aabbQuery(aabb, result) {
    this.data; // abort if the range does not intersect this node
    // if (!this.aabb.overlaps(aabb)){
    //     return result;
    // }
    // Add objects at this level
    // Array.prototype.push.apply(result, nodeData);
    // Add child data
    // @todo unwrap recursion into a queue / loop, that's faster in JS

    this.children; // for (let i = 0, N = this.children.length; i !== N; i++) {
    //     children[i].aabbQuery(aabb, result);
    // }

    const queue = [this];

    while (queue.length) {
      const node = queue.pop();

      if (node.aabb.overlaps(aabb)) {
        Array.prototype.push.apply(result, node.data);
      }

      Array.prototype.push.apply(queue, node.children);
    }

    return result;
  }
  /**
   * Get all data, potentially intersected by a ray.
   * @return The "result" object
   */


  rayQuery(ray, treeTransform, result) {
    // Use aabb query for now.

    /** @todo implement real ray query which needs less lookups */
    ray.getAABB(tmpAABB);
    tmpAABB.toLocalFrame(treeTransform, tmpAABB);
    this.aabbQuery(tmpAABB, result);
    return result;
  }
  /**
   * removeEmptyNodes
   */


  removeEmptyNodes() {
    for (let i = this.children.length - 1; i >= 0; i--) {
      this.children[i].removeEmptyNodes();

      if (!this.children[i].children.length && !this.children[i].data.length) {
        this.children.splice(i, 1);
      }
    }
  }

}
/**
 * Octree
 */


class Octree extends OctreeNode {
  /**
   * Maximum subdivision depth
   * @default 8
   */

  /**
   * @param aabb The total AABB of the tree
   */
  constructor(aabb, options) {
    if (options === void 0) {
      options = {};
    }

    super({
      root: null,
      aabb
    });
    this.maxDepth = typeof options.maxDepth !== 'undefined' ? options.maxDepth : 8;
  }

}
const halfDiagonal = new Vec3$1();
const tmpAABB = new AABB$1();

/**
 * Trimesh.
 * @example
 *     // How to make a mesh with a single triangle
 *     const vertices = [
 *         0, 0, 0, // vertex 0
 *         1, 0, 0, // vertex 1
 *         0, 1, 0  // vertex 2
 *     ]
 *     const indices = [
 *         0, 1, 2  // triangle 0
 *     ]
 *     const trimeshShape = new CANNON.Trimesh(vertices, indices)
 */
class Trimesh extends Shape$1 {
  /**
   * vertices
   */

  /**
   * Array of integers, indicating which vertices each triangle consists of. The length of this array is thus 3 times the number of triangles.
   */

  /**
   * The normals data.
   */

  /**
   * The local AABB of the mesh.
   */

  /**
   * References to vertex pairs, making up all unique edges in the trimesh.
   */

  /**
   * Local scaling of the mesh. Use .setScale() to set it.
   */

  /**
   * The indexed triangles. Use .updateTree() to update it.
   */
  constructor(vertices, indices) {
    super({
      type: Shape$1.types.TRIMESH
    });
    this.vertices = new Float32Array(vertices);
    this.indices = new Int16Array(indices);
    this.normals = new Float32Array(indices.length);
    this.aabb = new AABB$1();
    this.edges = null;
    this.scale = new Vec3$1(1, 1, 1);
    this.tree = new Octree();
    this.updateEdges();
    this.updateNormals();
    this.updateAABB();
    this.updateBoundingSphereRadius();
    this.updateTree();
  }
  /**
   * updateTree
   */


  updateTree() {
    const tree = this.tree;
    tree.reset();
    tree.aabb.copy(this.aabb);
    const scale = this.scale; // The local mesh AABB is scaled, but the octree AABB should be unscaled

    tree.aabb.lowerBound.x *= 1 / scale.x;
    tree.aabb.lowerBound.y *= 1 / scale.y;
    tree.aabb.lowerBound.z *= 1 / scale.z;
    tree.aabb.upperBound.x *= 1 / scale.x;
    tree.aabb.upperBound.y *= 1 / scale.y;
    tree.aabb.upperBound.z *= 1 / scale.z; // Insert all triangles

    const triangleAABB = new AABB$1();
    const a = new Vec3$1();
    const b = new Vec3$1();
    const c = new Vec3$1();
    const points = [a, b, c];

    for (let i = 0; i < this.indices.length / 3; i++) {
      //this.getTriangleVertices(i, a, b, c);
      // Get unscaled triangle verts
      const i3 = i * 3;

      this._getUnscaledVertex(this.indices[i3], a);

      this._getUnscaledVertex(this.indices[i3 + 1], b);

      this._getUnscaledVertex(this.indices[i3 + 2], c);

      triangleAABB.setFromPoints(points);
      tree.insert(triangleAABB, i);
    }

    tree.removeEmptyNodes();
  }
  /**
   * Get triangles in a local AABB from the trimesh.
   * @param result An array of integers, referencing the queried triangles.
   */


  getTrianglesInAABB(aabb, result) {
    unscaledAABB.copy(aabb); // Scale it to local

    const scale = this.scale;
    const isx = scale.x;
    const isy = scale.y;
    const isz = scale.z;
    const l = unscaledAABB.lowerBound;
    const u = unscaledAABB.upperBound;
    l.x /= isx;
    l.y /= isy;
    l.z /= isz;
    u.x /= isx;
    u.y /= isy;
    u.z /= isz;
    return this.tree.aabbQuery(unscaledAABB, result);
  }
  /**
   * setScale
   */


  setScale(scale) {
    const wasUniform = this.scale.x === this.scale.y && this.scale.y === this.scale.z;
    const isUniform = scale.x === scale.y && scale.y === scale.z;

    if (!(wasUniform && isUniform)) {
      // Non-uniform scaling. Need to update normals.
      this.updateNormals();
    }

    this.scale.copy(scale);
    this.updateAABB();
    this.updateBoundingSphereRadius();
  }
  /**
   * Compute the normals of the faces. Will save in the `.normals` array.
   */


  updateNormals() {
    const n = computeNormals_n; // Generate normals

    const normals = this.normals;

    for (let i = 0; i < this.indices.length / 3; i++) {
      const i3 = i * 3;
      const a = this.indices[i3];
      const b = this.indices[i3 + 1];
      const c = this.indices[i3 + 2];
      this.getVertex(a, va);
      this.getVertex(b, vb);
      this.getVertex(c, vc);
      Trimesh.computeNormal(vb, va, vc, n);
      normals[i3] = n.x;
      normals[i3 + 1] = n.y;
      normals[i3 + 2] = n.z;
    }
  }
  /**
   * Update the `.edges` property
   */


  updateEdges() {
    const edges = {};

    const add = (a, b) => {
      const key = a < b ? `${a}_${b}` : `${b}_${a}`;
      edges[key] = true;
    };

    for (let i = 0; i < this.indices.length / 3; i++) {
      const i3 = i * 3;
      const a = this.indices[i3];
      const b = this.indices[i3 + 1];
      const c = this.indices[i3 + 2];
      add(a, b);
      add(b, c);
      add(c, a);
    }

    const keys = Object.keys(edges);
    this.edges = new Int16Array(keys.length * 2);

    for (let i = 0; i < keys.length; i++) {
      const indices = keys[i].split('_');
      this.edges[2 * i] = parseInt(indices[0], 10);
      this.edges[2 * i + 1] = parseInt(indices[1], 10);
    }
  }
  /**
   * Get an edge vertex
   * @param firstOrSecond 0 or 1, depending on which one of the vertices you need.
   * @param vertexStore Where to store the result
   */


  getEdgeVertex(edgeIndex, firstOrSecond, vertexStore) {
    const vertexIndex = this.edges[edgeIndex * 2 + (firstOrSecond ? 1 : 0)];
    this.getVertex(vertexIndex, vertexStore);
  }
  /**
   * Get a vector along an edge.
   */


  getEdgeVector(edgeIndex, vectorStore) {
    const va = getEdgeVector_va;
    const vb = getEdgeVector_vb;
    this.getEdgeVertex(edgeIndex, 0, va);
    this.getEdgeVertex(edgeIndex, 1, vb);
    vb.vsub(va, vectorStore);
  }
  /**
   * Get face normal given 3 vertices
   */


  static computeNormal(va, vb, vc, target) {
    vb.vsub(va, ab);
    vc.vsub(vb, cb);
    cb.cross(ab, target);

    if (!target.isZero()) {
      target.normalize();
    }
  }
  /**
   * Get vertex i.
   * @return The "out" vector object
   */


  getVertex(i, out) {
    const scale = this.scale;

    this._getUnscaledVertex(i, out);

    out.x *= scale.x;
    out.y *= scale.y;
    out.z *= scale.z;
    return out;
  }
  /**
   * Get raw vertex i
   * @return The "out" vector object
   */


  _getUnscaledVertex(i, out) {
    const i3 = i * 3;
    const vertices = this.vertices;
    return out.set(vertices[i3], vertices[i3 + 1], vertices[i3 + 2]);
  }
  /**
   * Get a vertex from the trimesh,transformed by the given position and quaternion.
   * @return The "out" vector object
   */


  getWorldVertex(i, pos, quat, out) {
    this.getVertex(i, out);
    Transform$1.pointToWorldFrame(pos, quat, out, out);
    return out;
  }
  /**
   * Get the three vertices for triangle i.
   */


  getTriangleVertices(i, a, b, c) {
    const i3 = i * 3;
    this.getVertex(this.indices[i3], a);
    this.getVertex(this.indices[i3 + 1], b);
    this.getVertex(this.indices[i3 + 2], c);
  }
  /**
   * Compute the normal of triangle i.
   * @return The "target" vector object
   */


  getNormal(i, target) {
    const i3 = i * 3;
    return target.set(this.normals[i3], this.normals[i3 + 1], this.normals[i3 + 2]);
  }
  /**
   * @return The "target" vector object
   */


  calculateLocalInertia(mass, target) {
    // Approximate with box inertia
    // Exact inertia calculation is overkill, but see http://geometrictools.com/Documentation/PolyhedralMassProperties.pdf for the correct way to do it
    this.computeLocalAABB(cli_aabb);
    const x = cli_aabb.upperBound.x - cli_aabb.lowerBound.x;
    const y = cli_aabb.upperBound.y - cli_aabb.lowerBound.y;
    const z = cli_aabb.upperBound.z - cli_aabb.lowerBound.z;
    return target.set(1.0 / 12.0 * mass * (2 * y * 2 * y + 2 * z * 2 * z), 1.0 / 12.0 * mass * (2 * x * 2 * x + 2 * z * 2 * z), 1.0 / 12.0 * mass * (2 * y * 2 * y + 2 * x * 2 * x));
  }
  /**
   * Compute the local AABB for the trimesh
   */


  computeLocalAABB(aabb) {
    const l = aabb.lowerBound;
    const u = aabb.upperBound;
    const n = this.vertices.length;
    this.vertices;
    const v = computeLocalAABB_worldVert;
    this.getVertex(0, v);
    l.copy(v);
    u.copy(v);

    for (let i = 0; i !== n; i++) {
      this.getVertex(i, v);

      if (v.x < l.x) {
        l.x = v.x;
      } else if (v.x > u.x) {
        u.x = v.x;
      }

      if (v.y < l.y) {
        l.y = v.y;
      } else if (v.y > u.y) {
        u.y = v.y;
      }

      if (v.z < l.z) {
        l.z = v.z;
      } else if (v.z > u.z) {
        u.z = v.z;
      }
    }
  }
  /**
   * Update the `.aabb` property
   */


  updateAABB() {
    this.computeLocalAABB(this.aabb);
  }
  /**
   * Will update the `.boundingSphereRadius` property
   */


  updateBoundingSphereRadius() {
    // Assume points are distributed with local (0,0,0) as center
    let max2 = 0;
    const vertices = this.vertices;
    const v = new Vec3$1();

    for (let i = 0, N = vertices.length / 3; i !== N; i++) {
      this.getVertex(i, v);
      const norm2 = v.lengthSquared();

      if (norm2 > max2) {
        max2 = norm2;
      }
    }

    this.boundingSphereRadius = Math.sqrt(max2);
  }
  /**
   * calculateWorldAABB
   */


  calculateWorldAABB(pos, quat, min, max) {
    /*
        const n = this.vertices.length / 3,
            verts = this.vertices;
        const minx,miny,minz,maxx,maxy,maxz;
         const v = tempWorldVertex;
        for(let i=0; i<n; i++){
            this.getVertex(i, v);
            quat.vmult(v, v);
            pos.vadd(v, v);
            if (v.x < minx || minx===undefined){
                minx = v.x;
            } else if(v.x > maxx || maxx===undefined){
                maxx = v.x;
            }
             if (v.y < miny || miny===undefined){
                miny = v.y;
            } else if(v.y > maxy || maxy===undefined){
                maxy = v.y;
            }
             if (v.z < minz || minz===undefined){
                minz = v.z;
            } else if(v.z > maxz || maxz===undefined){
                maxz = v.z;
            }
        }
        min.set(minx,miny,minz);
        max.set(maxx,maxy,maxz);
        */
    // Faster approximation using local AABB
    const frame = calculateWorldAABB_frame;
    const result = calculateWorldAABB_aabb;
    frame.position = pos;
    frame.quaternion = quat;
    this.aabb.toWorldFrame(frame, result);
    min.copy(result.lowerBound);
    max.copy(result.upperBound);
  }
  /**
   * Get approximate volume
   */


  volume() {
    return 4.0 * Math.PI * this.boundingSphereRadius / 3.0;
  }
  /**
   * Create a Trimesh instance, shaped as a torus.
   */


  static createTorus(radius, tube, radialSegments, tubularSegments, arc) {
    if (radius === void 0) {
      radius = 1;
    }

    if (tube === void 0) {
      tube = 0.5;
    }

    if (radialSegments === void 0) {
      radialSegments = 8;
    }

    if (tubularSegments === void 0) {
      tubularSegments = 6;
    }

    if (arc === void 0) {
      arc = Math.PI * 2;
    }

    const vertices = [];
    const indices = [];

    for (let j = 0; j <= radialSegments; j++) {
      for (let i = 0; i <= tubularSegments; i++) {
        const u = i / tubularSegments * arc;
        const v = j / radialSegments * Math.PI * 2;
        const x = (radius + tube * Math.cos(v)) * Math.cos(u);
        const y = (radius + tube * Math.cos(v)) * Math.sin(u);
        const z = tube * Math.sin(v);
        vertices.push(x, y, z);
      }
    }

    for (let j = 1; j <= radialSegments; j++) {
      for (let i = 1; i <= tubularSegments; i++) {
        const a = (tubularSegments + 1) * j + i - 1;
        const b = (tubularSegments + 1) * (j - 1) + i - 1;
        const c = (tubularSegments + 1) * (j - 1) + i;
        const d = (tubularSegments + 1) * j + i;
        indices.push(a, b, d);
        indices.push(b, c, d);
      }
    }

    return new Trimesh(vertices, indices);
  }

}
const computeNormals_n = new Vec3$1();
const unscaledAABB = new AABB$1();
const getEdgeVector_va = new Vec3$1();
const getEdgeVector_vb = new Vec3$1();
const cb = new Vec3$1();
const ab = new Vec3$1();
const va = new Vec3$1();
const vb = new Vec3$1();
const vc = new Vec3$1();
const cli_aabb = new AABB$1();
const computeLocalAABB_worldVert = new Vec3$1();
const calculateWorldAABB_frame = new Transform$1();
const calculateWorldAABB_aabb = new AABB$1();

// Naming rule: based of the order in SHAPE_TYPES,
// the first part of the method is formed by the
// shape type that comes before, in the second part
// there is the shape type that comes after in the SHAPE_TYPES list
({
  sphereSphere: Shape$1.types.SPHERE,
  spherePlane: Shape$1.types.SPHERE | Shape$1.types.PLANE,
  boxBox: Shape$1.types.BOX | Shape$1.types.BOX,
  sphereBox: Shape$1.types.SPHERE | Shape$1.types.BOX,
  planeBox: Shape$1.types.PLANE | Shape$1.types.BOX,
  convexConvex: Shape$1.types.CONVEXPOLYHEDRON,
  sphereConvex: Shape$1.types.SPHERE | Shape$1.types.CONVEXPOLYHEDRON,
  planeConvex: Shape$1.types.PLANE | Shape$1.types.CONVEXPOLYHEDRON,
  boxConvex: Shape$1.types.BOX | Shape$1.types.CONVEXPOLYHEDRON,
  sphereHeightfield: Shape$1.types.SPHERE | Shape$1.types.HEIGHTFIELD,
  boxHeightfield: Shape$1.types.BOX | Shape$1.types.HEIGHTFIELD,
  convexHeightfield: Shape$1.types.CONVEXPOLYHEDRON | Shape$1.types.HEIGHTFIELD,
  sphereParticle: Shape$1.types.PARTICLE | Shape$1.types.SPHERE,
  planeParticle: Shape$1.types.PLANE | Shape$1.types.PARTICLE,
  boxParticle: Shape$1.types.BOX | Shape$1.types.PARTICLE,
  convexParticle: Shape$1.types.PARTICLE | Shape$1.types.CONVEXPOLYHEDRON,
  cylinderCylinder: Shape$1.types.CYLINDER,
  sphereCylinder: Shape$1.types.SPHERE | Shape$1.types.CYLINDER,
  planeCylinder: Shape$1.types.PLANE | Shape$1.types.CYLINDER,
  boxCylinder: Shape$1.types.BOX | Shape$1.types.CYLINDER,
  convexCylinder: Shape$1.types.CONVEXPOLYHEDRON | Shape$1.types.CYLINDER,
  heightfieldCylinder: Shape$1.types.HEIGHTFIELD | Shape$1.types.CYLINDER,
  particleCylinder: Shape$1.types.PARTICLE | Shape$1.types.CYLINDER,
  sphereTrimesh: Shape$1.types.SPHERE | Shape$1.types.TRIMESH,
  planeTrimesh: Shape$1.types.PLANE | Shape$1.types.TRIMESH
});
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Quaternion$1();
new Quaternion$1();

new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new AABB$1();
new Vec3$1();
new Vec3$1(); // See http://bulletphysics.com/Bullet/BulletFull/SphereTriangleDetector_8cpp_source.html

new Vec3$1();
new Vec3$1();
new Vec3$1();

new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
[new Vec3$1(), new Vec3$1(), new Vec3$1(), new Vec3$1(), new Vec3$1(), new Vec3$1()];
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1(); // WIP

new Quaternion$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();
new Vec3$1();

new AABB$1();
new Ray$1(); // performance.now() fallback on Date.now()

const performance$1 = globalThis.performance || {};

if (!performance$1.now) {
  let nowOffset = Date.now();

  if (performance$1.timing && performance$1.timing.navigationStart) {
    nowOffset = performance$1.timing.navigationStart;
  }

  performance$1.now = () => Date.now() - nowOffset;
}

new Vec3$1(); // Dispatched after the world has stepped forward in time.

/**
 * @typedef { import('cannon-es').MaterialOptions } MaterialOptions
 */

const makeVec3 = _ref => {
  let [x, y, z] = _ref;
  return new Vec3$1(x, y, z);
};

const prepareSphere = args => Array.isArray(args) ? args : [args];

const prepareConvexPolyhedron = _ref2 => {
  let [v, faces, n, a, boundingSphereRadius] = _ref2;
  return [{
    axes: a ? a.map(makeVec3) : undefined,
    boundingSphereRadius,
    faces,
    normals: n ? n.map(makeVec3) : undefined,
    vertices: v ? v.map(makeVec3) : undefined
  }];
};

function createShape(type, args) {
  switch (type) {
    case 'Box':
      return new Box(new Vec3$1(...args.map(v => v / 2)));
    // extents => halfExtents

    case 'ConvexPolyhedron':
      return new ConvexPolyhedron(...prepareConvexPolyhedron(args));

    case 'Cylinder':
      return new Cylinder(...args);
    // [ radiusTop, radiusBottom, height, numSegments ] = args

    case 'Heightfield':
      return new Heightfield(...args);
    // [ Array data, options: {minValue, maxValue, elementSize}  ] = args

    case 'Particle':
      return new Particle();
    // no args

    case 'Plane':
      return new Plane();
    // no args, infinite x and y

    case 'Sphere':
      return new Sphere(...prepareSphere(args));
    // radius = args

    case 'Trimesh':
      return new Trimesh(...args);
    // [vertices, indices] = args
  }
}
/**
 * @function
 * @param {Object} options
 * @param {string} options.uuid
 * @param {BodyProps} options.props
 * @param {BodyShapeType} options.type
 * @param {(materialOptions: MaterialOptions) => Material =} options.createMaterial
 * @returns {Body}
 */


const propsToBody = options => {
  const {
    uuid,
    props,
    type,
    createMaterial = materialOptions => new Material(materialOptions)
  } = options;
  const {
    angularFactor = [1, 1, 1],
    angularVelocity = [0, 0, 0],
    args = [],
    collisionResponse,
    linearFactor = [1, 1, 1],
    mass,
    material,
    onCollide,
    position = [0, 0, 0],
    rotation = [0, 0, 0],
    shapes,
    type: bodyType,
    velocity = [0, 0, 0],
    ...extra
  } = props;
  const body = new Body({ ...extra,
    mass: bodyType === 'Static' ? 0 : mass,
    material: material ? createMaterial(material) : undefined,
    type: bodyType ? Body[bodyType.toUpperCase()] : undefined
  });
  body.uuid = uuid;

  if (collisionResponse !== undefined) {
    body.collisionResponse = collisionResponse;
  }

  if (type === 'Compound') {
    shapes.forEach(_ref3 => {
      let {
        type,
        args,
        position,
        rotation,
        material,
        ...extra
      } = _ref3;
      const shapeBody = body.addShape(createShape(type, args), position ? new Vec3$1(...position) : undefined, rotation ? new Quaternion$1().setFromEuler(...rotation) : undefined);
      if (material) shapeBody.material = createMaterial(material);
      Object.assign(shapeBody, extra);
    });
  } else {
    body.addShape(createShape(type, args));
  }

  body.position.set(position[0], position[1], position[2]);
  body.quaternion.setFromEuler(rotation[0], rotation[1], rotation[2]);
  body.velocity.set(velocity[0], velocity[1], velocity[2]);
  body.angularVelocity.set(angularVelocity[0], angularVelocity[1], angularVelocity[2]);
  body.linearFactor.set(linearFactor[0], linearFactor[1], linearFactor[2]);
  body.angularFactor.set(angularFactor[0], angularFactor[1], angularFactor[2]);
  return body;
};

/**
 * Records what objects are colliding with each other
 */

/**
 * A 3x3 matrix.
 * Authored by {@link http://github.com/schteppe/ schteppe}
 */
class Mat3 {
  /**
   * A vector of length 9, containing all matrix elements.
   */

  /**
   * @param elements A vector of length 9, containing all matrix elements.
   */
  constructor(elements) {
    if (elements === void 0) {
      elements = [0, 0, 0, 0, 0, 0, 0, 0, 0];
    }

    this.elements = elements;
  }
  /**
   * Sets the matrix to identity
   * @todo Should perhaps be renamed to `setIdentity()` to be more clear.
   * @todo Create another function that immediately creates an identity matrix eg. `eye()`
   */


  identity() {
    const e = this.elements;
    e[0] = 1;
    e[1] = 0;
    e[2] = 0;
    e[3] = 0;
    e[4] = 1;
    e[5] = 0;
    e[6] = 0;
    e[7] = 0;
    e[8] = 1;
  }
  /**
   * Set all elements to zero
   */


  setZero() {
    const e = this.elements;
    e[0] = 0;
    e[1] = 0;
    e[2] = 0;
    e[3] = 0;
    e[4] = 0;
    e[5] = 0;
    e[6] = 0;
    e[7] = 0;
    e[8] = 0;
  }
  /**
   * Sets the matrix diagonal elements from a Vec3
   */


  setTrace(vector) {
    const e = this.elements;
    e[0] = vector.x;
    e[4] = vector.y;
    e[8] = vector.z;
  }
  /**
   * Gets the matrix diagonal elements
   */


  getTrace(target) {
    if (target === void 0) {
      target = new Vec3();
    }

    const e = this.elements;
    target.x = e[0];
    target.y = e[4];
    target.z = e[8];
    return target;
  }
  /**
   * Matrix-Vector multiplication
   * @param v The vector to multiply with
   * @param target Optional, target to save the result in.
   */


  vmult(v, target) {
    if (target === void 0) {
      target = new Vec3();
    }

    const e = this.elements;
    const x = v.x;
    const y = v.y;
    const z = v.z;
    target.x = e[0] * x + e[1] * y + e[2] * z;
    target.y = e[3] * x + e[4] * y + e[5] * z;
    target.z = e[6] * x + e[7] * y + e[8] * z;
    return target;
  }
  /**
   * Matrix-scalar multiplication
   */


  smult(s) {
    for (let i = 0; i < this.elements.length; i++) {
      this.elements[i] *= s;
    }
  }
  /**
   * Matrix multiplication
   * @param matrix Matrix to multiply with from left side.
   */


  mmult(matrix, target) {
    if (target === void 0) {
      target = new Mat3();
    }

    const A = this.elements;
    const B = matrix.elements;
    const T = target.elements;
    const a11 = A[0],
          a12 = A[1],
          a13 = A[2],
          a21 = A[3],
          a22 = A[4],
          a23 = A[5],
          a31 = A[6],
          a32 = A[7],
          a33 = A[8];
    const b11 = B[0],
          b12 = B[1],
          b13 = B[2],
          b21 = B[3],
          b22 = B[4],
          b23 = B[5],
          b31 = B[6],
          b32 = B[7],
          b33 = B[8];
    T[0] = a11 * b11 + a12 * b21 + a13 * b31;
    T[1] = a11 * b12 + a12 * b22 + a13 * b32;
    T[2] = a11 * b13 + a12 * b23 + a13 * b33;
    T[3] = a21 * b11 + a22 * b21 + a23 * b31;
    T[4] = a21 * b12 + a22 * b22 + a23 * b32;
    T[5] = a21 * b13 + a22 * b23 + a23 * b33;
    T[6] = a31 * b11 + a32 * b21 + a33 * b31;
    T[7] = a31 * b12 + a32 * b22 + a33 * b32;
    T[8] = a31 * b13 + a32 * b23 + a33 * b33;
    return target;
  }
  /**
   * Scale each column of the matrix
   */


  scale(vector, target) {
    if (target === void 0) {
      target = new Mat3();
    }

    const e = this.elements;
    const t = target.elements;

    for (let i = 0; i !== 3; i++) {
      t[3 * i + 0] = vector.x * e[3 * i + 0];
      t[3 * i + 1] = vector.y * e[3 * i + 1];
      t[3 * i + 2] = vector.z * e[3 * i + 2];
    }

    return target;
  }
  /**
   * Solve Ax=b
   * @param b The right hand side
   * @param target Optional. Target vector to save in.
   * @return The solution x
   * @todo should reuse arrays
   */


  solve(b, target) {
    if (target === void 0) {
      target = new Vec3();
    }

    // Construct equations
    const nr = 3; // num rows

    const nc = 4; // num cols

    const eqns = [];
    let i;
    let j;

    for (i = 0; i < nr * nc; i++) {
      eqns.push(0);
    }

    for (i = 0; i < 3; i++) {
      for (j = 0; j < 3; j++) {
        eqns[i + nc * j] = this.elements[i + 3 * j];
      }
    }

    eqns[3 + 4 * 0] = b.x;
    eqns[3 + 4 * 1] = b.y;
    eqns[3 + 4 * 2] = b.z; // Compute right upper triangular version of the matrix - Gauss elimination

    let n = 3;
    const k = n;
    let np;
    const kp = 4; // num rows

    let p;

    do {
      i = k - n;

      if (eqns[i + nc * i] === 0) {
        // the pivot is null, swap lines
        for (j = i + 1; j < k; j++) {
          if (eqns[i + nc * j] !== 0) {
            np = kp;

            do {
              // do ligne( i ) = ligne( i ) + ligne( k )
              p = kp - np;
              eqns[p + nc * i] += eqns[p + nc * j];
            } while (--np);

            break;
          }
        }
      }

      if (eqns[i + nc * i] !== 0) {
        for (j = i + 1; j < k; j++) {
          const multiplier = eqns[i + nc * j] / eqns[i + nc * i];
          np = kp;

          do {
            // do ligne( k ) = ligne( k ) - multiplier * ligne( i )
            p = kp - np;
            eqns[p + nc * j] = p <= i ? 0 : eqns[p + nc * j] - eqns[p + nc * i] * multiplier;
          } while (--np);
        }
      }
    } while (--n); // Get the solution


    target.z = eqns[2 * nc + 3] / eqns[2 * nc + 2];
    target.y = (eqns[1 * nc + 3] - eqns[1 * nc + 2] * target.z) / eqns[1 * nc + 1];
    target.x = (eqns[0 * nc + 3] - eqns[0 * nc + 2] * target.z - eqns[0 * nc + 1] * target.y) / eqns[0 * nc + 0];

    if (isNaN(target.x) || isNaN(target.y) || isNaN(target.z) || target.x === Infinity || target.y === Infinity || target.z === Infinity) {
      throw `Could not solve equation! Got x=[${target.toString()}], b=[${b.toString()}], A=[${this.toString()}]`;
    }

    return target;
  }
  /**
   * Get an element in the matrix by index. Index starts at 0, not 1!!!
   * @param value If provided, the matrix element will be set to this value.
   */


  e(row, column, value) {
    if (value === undefined) {
      return this.elements[column + 3 * row];
    } else {
      // Set value
      this.elements[column + 3 * row] = value;
    }
  }
  /**
   * Copy another matrix into this matrix object.
   */


  copy(matrix) {
    for (let i = 0; i < matrix.elements.length; i++) {
      this.elements[i] = matrix.elements[i];
    }

    return this;
  }
  /**
   * Returns a string representation of the matrix.
   */


  toString() {
    let r = '';
    const sep = ',';

    for (let i = 0; i < 9; i++) {
      r += this.elements[i] + sep;
    }

    return r;
  }
  /**
   * reverse the matrix
   * @param target Target matrix to save in.
   * @return The solution x
   */


  reverse(target) {
    if (target === void 0) {
      target = new Mat3();
    }

    // Construct equations
    const nr = 3; // num rows

    const nc = 6; // num cols

    const eqns = reverse_eqns;
    let i;
    let j;

    for (i = 0; i < 3; i++) {
      for (j = 0; j < 3; j++) {
        eqns[i + nc * j] = this.elements[i + 3 * j];
      }
    }

    eqns[3 + 6 * 0] = 1;
    eqns[3 + 6 * 1] = 0;
    eqns[3 + 6 * 2] = 0;
    eqns[4 + 6 * 0] = 0;
    eqns[4 + 6 * 1] = 1;
    eqns[4 + 6 * 2] = 0;
    eqns[5 + 6 * 0] = 0;
    eqns[5 + 6 * 1] = 0;
    eqns[5 + 6 * 2] = 1; // Compute right upper triangular version of the matrix - Gauss elimination

    let n = 3;
    const k = n;
    let np;
    const kp = nc; // num rows

    let p;

    do {
      i = k - n;

      if (eqns[i + nc * i] === 0) {
        // the pivot is null, swap lines
        for (j = i + 1; j < k; j++) {
          if (eqns[i + nc * j] !== 0) {
            np = kp;

            do {
              // do line( i ) = line( i ) + line( k )
              p = kp - np;
              eqns[p + nc * i] += eqns[p + nc * j];
            } while (--np);

            break;
          }
        }
      }

      if (eqns[i + nc * i] !== 0) {
        for (j = i + 1; j < k; j++) {
          const multiplier = eqns[i + nc * j] / eqns[i + nc * i];
          np = kp;

          do {
            // do line( k ) = line( k ) - multiplier * line( i )
            p = kp - np;
            eqns[p + nc * j] = p <= i ? 0 : eqns[p + nc * j] - eqns[p + nc * i] * multiplier;
          } while (--np);
        }
      }
    } while (--n); // eliminate the upper left triangle of the matrix


    i = 2;

    do {
      j = i - 1;

      do {
        const multiplier = eqns[i + nc * j] / eqns[i + nc * i];
        np = nc;

        do {
          p = nc - np;
          eqns[p + nc * j] = eqns[p + nc * j] - eqns[p + nc * i] * multiplier;
        } while (--np);
      } while (j--);
    } while (--i); // operations on the diagonal


    i = 2;

    do {
      const multiplier = 1 / eqns[i + nc * i];
      np = nc;

      do {
        p = nc - np;
        eqns[p + nc * i] = eqns[p + nc * i] * multiplier;
      } while (--np);
    } while (i--);

    i = 2;

    do {
      j = 2;

      do {
        p = eqns[nr + j + nc * i];

        if (isNaN(p) || p === Infinity) {
          throw `Could not reverse! A=[${this.toString()}]`;
        }

        target.e(i, j, p);
      } while (j--);
    } while (i--);

    return target;
  }
  /**
   * Set the matrix from a quaterion
   */


  setRotationFromQuaternion(q) {
    const x = q.x;
    const y = q.y;
    const z = q.z;
    const w = q.w;
    const x2 = x + x;
    const y2 = y + y;
    const z2 = z + z;
    const xx = x * x2;
    const xy = x * y2;
    const xz = x * z2;
    const yy = y * y2;
    const yz = y * z2;
    const zz = z * z2;
    const wx = w * x2;
    const wy = w * y2;
    const wz = w * z2;
    const e = this.elements;
    e[3 * 0 + 0] = 1 - (yy + zz);
    e[3 * 0 + 1] = xy - wz;
    e[3 * 0 + 2] = xz + wy;
    e[3 * 1 + 0] = xy + wz;
    e[3 * 1 + 1] = 1 - (xx + zz);
    e[3 * 1 + 2] = yz - wx;
    e[3 * 2 + 0] = xz - wy;
    e[3 * 2 + 1] = yz + wx;
    e[3 * 2 + 2] = 1 - (xx + yy);
    return this;
  }
  /**
   * Transpose the matrix
   * @param target Optional. Where to store the result.
   * @return The target Mat3, or a new Mat3 if target was omitted.
   */


  transpose(target) {
    if (target === void 0) {
      target = new Mat3();
    }

    const M = this.elements;
    const T = target.elements;
    let tmp; //Set diagonals

    T[0] = M[0];
    T[4] = M[4];
    T[8] = M[8];
    tmp = M[1];
    T[1] = M[3];
    T[3] = tmp;
    tmp = M[2];
    T[2] = M[6];
    T[6] = tmp;
    tmp = M[5];
    T[5] = M[7];
    T[7] = tmp;
    return target;
  }

}
const reverse_eqns = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];

/**
 * 3-dimensional vector
 * @example
 *     const v = new Vec3(1, 2, 3)
 *     console.log('x=' + v.x) // x=1
 */

class Vec3 {
  constructor(x, y, z) {
    if (x === void 0) {
      x = 0.0;
    }

    if (y === void 0) {
      y = 0.0;
    }

    if (z === void 0) {
      z = 0.0;
    }

    this.x = x;
    this.y = y;
    this.z = z;
  }
  /**
   * Vector cross product
   * @param target Optional target to save in.
   */


  cross(vector, target) {
    if (target === void 0) {
      target = new Vec3();
    }

    const vx = vector.x;
    const vy = vector.y;
    const vz = vector.z;
    const x = this.x;
    const y = this.y;
    const z = this.z;
    target.x = y * vz - z * vy;
    target.y = z * vx - x * vz;
    target.z = x * vy - y * vx;
    return target;
  }
  /**
   * Set the vectors' 3 elements
   */


  set(x, y, z) {
    this.x = x;
    this.y = y;
    this.z = z;
    return this;
  }
  /**
   * Set all components of the vector to zero.
   */


  setZero() {
    this.x = this.y = this.z = 0;
  }
  /**
   * Vector addition
   */


  vadd(vector, target) {
    if (target) {
      target.x = vector.x + this.x;
      target.y = vector.y + this.y;
      target.z = vector.z + this.z;
    } else {
      return new Vec3(this.x + vector.x, this.y + vector.y, this.z + vector.z);
    }
  }
  /**
   * Vector subtraction
   * @param target Optional target to save in.
   */


  vsub(vector, target) {
    if (target) {
      target.x = this.x - vector.x;
      target.y = this.y - vector.y;
      target.z = this.z - vector.z;
    } else {
      return new Vec3(this.x - vector.x, this.y - vector.y, this.z - vector.z);
    }
  }
  /**
   * Get the cross product matrix a_cross from a vector, such that a x b = a_cross * b = c
   *
   * See {@link https://www8.cs.umu.se/kurser/TDBD24/VT06/lectures/Lecture6.pdf Umeå University Lecture}
   */


  crossmat() {
    return new Mat3([0, -this.z, this.y, this.z, 0, -this.x, -this.y, this.x, 0]);
  }
  /**
   * Normalize the vector. Note that this changes the values in the vector.
    * @return Returns the norm of the vector
   */


  normalize() {
    const x = this.x;
    const y = this.y;
    const z = this.z;
    const n = Math.sqrt(x * x + y * y + z * z);

    if (n > 0.0) {
      const invN = 1 / n;
      this.x *= invN;
      this.y *= invN;
      this.z *= invN;
    } else {
      // Make something up
      this.x = 0;
      this.y = 0;
      this.z = 0;
    }

    return n;
  }
  /**
   * Get the version of this vector that is of length 1.
   * @param target Optional target to save in
   * @return Returns the unit vector
   */


  unit(target) {
    if (target === void 0) {
      target = new Vec3();
    }

    const x = this.x;
    const y = this.y;
    const z = this.z;
    let ninv = Math.sqrt(x * x + y * y + z * z);

    if (ninv > 0.0) {
      ninv = 1.0 / ninv;
      target.x = x * ninv;
      target.y = y * ninv;
      target.z = z * ninv;
    } else {
      target.x = 1;
      target.y = 0;
      target.z = 0;
    }

    return target;
  }
  /**
   * Get the length of the vector
   */


  length() {
    const x = this.x;
    const y = this.y;
    const z = this.z;
    return Math.sqrt(x * x + y * y + z * z);
  }
  /**
   * Get the squared length of the vector.
   */


  lengthSquared() {
    return this.dot(this);
  }
  /**
   * Get distance from this point to another point
   */


  distanceTo(p) {
    const x = this.x;
    const y = this.y;
    const z = this.z;
    const px = p.x;
    const py = p.y;
    const pz = p.z;
    return Math.sqrt((px - x) * (px - x) + (py - y) * (py - y) + (pz - z) * (pz - z));
  }
  /**
   * Get squared distance from this point to another point
   */


  distanceSquared(p) {
    const x = this.x;
    const y = this.y;
    const z = this.z;
    const px = p.x;
    const py = p.y;
    const pz = p.z;
    return (px - x) * (px - x) + (py - y) * (py - y) + (pz - z) * (pz - z);
  }
  /**
   * Multiply all the components of the vector with a scalar.
   * @param target The vector to save the result in.
   */


  scale(scalar, target) {
    if (target === void 0) {
      target = new Vec3();
    }

    const x = this.x;
    const y = this.y;
    const z = this.z;
    target.x = scalar * x;
    target.y = scalar * y;
    target.z = scalar * z;
    return target;
  }
  /**
   * Multiply the vector with an other vector, component-wise.
   * @param target The vector to save the result in.
   */


  vmul(vector, target) {
    if (target === void 0) {
      target = new Vec3();
    }

    target.x = vector.x * this.x;
    target.y = vector.y * this.y;
    target.z = vector.z * this.z;
    return target;
  }
  /**
   * Scale a vector and add it to this vector. Save the result in "target". (target = this + vector * scalar)
   * @param target The vector to save the result in.
   */


  addScaledVector(scalar, vector, target) {
    if (target === void 0) {
      target = new Vec3();
    }

    target.x = this.x + scalar * vector.x;
    target.y = this.y + scalar * vector.y;
    target.z = this.z + scalar * vector.z;
    return target;
  }
  /**
   * Calculate dot product
   * @param vector
   */


  dot(vector) {
    return this.x * vector.x + this.y * vector.y + this.z * vector.z;
  }

  isZero() {
    return this.x === 0 && this.y === 0 && this.z === 0;
  }
  /**
   * Make the vector point in the opposite direction.
   * @param target Optional target to save in
   */


  negate(target) {
    if (target === void 0) {
      target = new Vec3();
    }

    target.x = -this.x;
    target.y = -this.y;
    target.z = -this.z;
    return target;
  }
  /**
   * Compute two artificial tangents to the vector
   * @param t1 Vector object to save the first tangent in
   * @param t2 Vector object to save the second tangent in
   */


  tangents(t1, t2) {
    const norm = this.length();

    if (norm > 0.0) {
      const n = Vec3_tangents_n;
      const inorm = 1 / norm;
      n.set(this.x * inorm, this.y * inorm, this.z * inorm);
      const randVec = Vec3_tangents_randVec;

      if (Math.abs(n.x) < 0.9) {
        randVec.set(1, 0, 0);
        n.cross(randVec, t1);
      } else {
        randVec.set(0, 1, 0);
        n.cross(randVec, t1);
      }

      n.cross(t1, t2);
    } else {
      // The normal length is zero, make something up
      t1.set(1, 0, 0);
      t2.set(0, 1, 0);
    }
  }
  /**
   * Converts to a more readable format
   */


  toString() {
    return `${this.x},${this.y},${this.z}`;
  }
  /**
   * Converts to an array
   */


  toArray() {
    return [this.x, this.y, this.z];
  }
  /**
   * Copies value of source to this vector.
   */


  copy(vector) {
    this.x = vector.x;
    this.y = vector.y;
    this.z = vector.z;
    return this;
  }
  /**
   * Do a linear interpolation between two vectors
   * @param t A number between 0 and 1. 0 will make this function return u, and 1 will make it return v. Numbers in between will generate a vector in between them.
   */


  lerp(vector, t, target) {
    const x = this.x;
    const y = this.y;
    const z = this.z;
    target.x = x + (vector.x - x) * t;
    target.y = y + (vector.y - y) * t;
    target.z = z + (vector.z - z) * t;
  }
  /**
   * Check if a vector equals is almost equal to another one.
   */


  almostEquals(vector, precision) {
    if (precision === void 0) {
      precision = 1e-6;
    }

    if (Math.abs(this.x - vector.x) > precision || Math.abs(this.y - vector.y) > precision || Math.abs(this.z - vector.z) > precision) {
      return false;
    }

    return true;
  }
  /**
   * Check if a vector is almost zero
   */


  almostZero(precision) {
    if (precision === void 0) {
      precision = 1e-6;
    }

    if (Math.abs(this.x) > precision || Math.abs(this.y) > precision || Math.abs(this.z) > precision) {
      return false;
    }

    return true;
  }
  /**
   * Check if the vector is anti-parallel to another vector.
   * @param precision Set to zero for exact comparisons
   */


  isAntiparallelTo(vector, precision) {
    this.negate(antip_neg);
    return antip_neg.almostEquals(vector, precision);
  }
  /**
   * Clone the vector
   */


  clone() {
    return new Vec3(this.x, this.y, this.z);
  }

}
Vec3.ZERO = new Vec3(0, 0, 0);
Vec3.UNIT_X = new Vec3(1, 0, 0);
Vec3.UNIT_Y = new Vec3(0, 1, 0);
Vec3.UNIT_Z = new Vec3(0, 0, 1);
const Vec3_tangents_n = new Vec3();
const Vec3_tangents_randVec = new Vec3();
const antip_neg = new Vec3();

/**
 * Axis aligned bounding box class.
 */
class AABB {
  /**
   * The lower bound of the bounding box
   */

  /**
   * The upper bound of the bounding box
   */
  constructor(options) {
    if (options === void 0) {
      options = {};
    }

    this.lowerBound = new Vec3();
    this.upperBound = new Vec3();

    if (options.lowerBound) {
      this.lowerBound.copy(options.lowerBound);
    }

    if (options.upperBound) {
      this.upperBound.copy(options.upperBound);
    }
  }
  /**
   * Set the AABB bounds from a set of points.
   * @param points An array of Vec3's.
   * @return The self object
   */


  setFromPoints(points, position, quaternion, skinSize) {
    const l = this.lowerBound;
    const u = this.upperBound;
    const q = quaternion; // Set to the first point

    l.copy(points[0]);

    if (q) {
      q.vmult(l, l);
    }

    u.copy(l);

    for (let i = 1; i < points.length; i++) {
      let p = points[i];

      if (q) {
        q.vmult(p, tmp$1);
        p = tmp$1;
      }

      if (p.x > u.x) {
        u.x = p.x;
      }

      if (p.x < l.x) {
        l.x = p.x;
      }

      if (p.y > u.y) {
        u.y = p.y;
      }

      if (p.y < l.y) {
        l.y = p.y;
      }

      if (p.z > u.z) {
        u.z = p.z;
      }

      if (p.z < l.z) {
        l.z = p.z;
      }
    } // Add offset


    if (position) {
      position.vadd(l, l);
      position.vadd(u, u);
    }

    if (skinSize) {
      l.x -= skinSize;
      l.y -= skinSize;
      l.z -= skinSize;
      u.x += skinSize;
      u.y += skinSize;
      u.z += skinSize;
    }

    return this;
  }
  /**
   * Copy bounds from an AABB to this AABB
   * @param aabb Source to copy from
   * @return The this object, for chainability
   */


  copy(aabb) {
    this.lowerBound.copy(aabb.lowerBound);
    this.upperBound.copy(aabb.upperBound);
    return this;
  }
  /**
   * Clone an AABB
   */


  clone() {
    return new AABB().copy(this);
  }
  /**
   * Extend this AABB so that it covers the given AABB too.
   */


  extend(aabb) {
    this.lowerBound.x = Math.min(this.lowerBound.x, aabb.lowerBound.x);
    this.upperBound.x = Math.max(this.upperBound.x, aabb.upperBound.x);
    this.lowerBound.y = Math.min(this.lowerBound.y, aabb.lowerBound.y);
    this.upperBound.y = Math.max(this.upperBound.y, aabb.upperBound.y);
    this.lowerBound.z = Math.min(this.lowerBound.z, aabb.lowerBound.z);
    this.upperBound.z = Math.max(this.upperBound.z, aabb.upperBound.z);
  }
  /**
   * Returns true if the given AABB overlaps this AABB.
   */


  overlaps(aabb) {
    const l1 = this.lowerBound;
    const u1 = this.upperBound;
    const l2 = aabb.lowerBound;
    const u2 = aabb.upperBound; //      l2        u2
    //      |---------|
    // |--------|
    // l1       u1

    const overlapsX = l2.x <= u1.x && u1.x <= u2.x || l1.x <= u2.x && u2.x <= u1.x;
    const overlapsY = l2.y <= u1.y && u1.y <= u2.y || l1.y <= u2.y && u2.y <= u1.y;
    const overlapsZ = l2.z <= u1.z && u1.z <= u2.z || l1.z <= u2.z && u2.z <= u1.z;
    return overlapsX && overlapsY && overlapsZ;
  } // Mostly for debugging


  volume() {
    const l = this.lowerBound;
    const u = this.upperBound;
    return (u.x - l.x) * (u.y - l.y) * (u.z - l.z);
  }
  /**
   * Returns true if the given AABB is fully contained in this AABB.
   */


  contains(aabb) {
    const l1 = this.lowerBound;
    const u1 = this.upperBound;
    const l2 = aabb.lowerBound;
    const u2 = aabb.upperBound; //      l2        u2
    //      |---------|
    // |---------------|
    // l1              u1

    return l1.x <= l2.x && u1.x >= u2.x && l1.y <= l2.y && u1.y >= u2.y && l1.z <= l2.z && u1.z >= u2.z;
  }

  getCorners(a, b, c, d, e, f, g, h) {
    const l = this.lowerBound;
    const u = this.upperBound;
    a.copy(l);
    b.set(u.x, l.y, l.z);
    c.set(u.x, u.y, l.z);
    d.set(l.x, u.y, u.z);
    e.set(u.x, l.y, u.z);
    f.set(l.x, u.y, l.z);
    g.set(l.x, l.y, u.z);
    h.copy(u);
  }
  /**
   * Get the representation of an AABB in another frame.
   * @return The "target" AABB object.
   */


  toLocalFrame(frame, target) {
    const corners = transformIntoFrame_corners;
    const a = corners[0];
    const b = corners[1];
    const c = corners[2];
    const d = corners[3];
    const e = corners[4];
    const f = corners[5];
    const g = corners[6];
    const h = corners[7]; // Get corners in current frame

    this.getCorners(a, b, c, d, e, f, g, h); // Transform them to new local frame

    for (let i = 0; i !== 8; i++) {
      const corner = corners[i];
      frame.pointToLocal(corner, corner);
    }

    return target.setFromPoints(corners);
  }
  /**
   * Get the representation of an AABB in the global frame.
   * @return The "target" AABB object.
   */


  toWorldFrame(frame, target) {
    const corners = transformIntoFrame_corners;
    const a = corners[0];
    const b = corners[1];
    const c = corners[2];
    const d = corners[3];
    const e = corners[4];
    const f = corners[5];
    const g = corners[6];
    const h = corners[7]; // Get corners in current frame

    this.getCorners(a, b, c, d, e, f, g, h); // Transform them to new local frame

    for (let i = 0; i !== 8; i++) {
      const corner = corners[i];
      frame.pointToWorld(corner, corner);
    }

    return target.setFromPoints(corners);
  }
  /**
   * Check if the AABB is hit by a ray.
   */


  overlapsRay(ray) {
    const {
      direction,
      from
    } = ray; // const t = 0
    // ray.direction is unit direction vector of ray

    const dirFracX = 1 / direction.x;
    const dirFracY = 1 / direction.y;
    const dirFracZ = 1 / direction.z; // this.lowerBound is the corner of AABB with minimal coordinates - left bottom, rt is maximal corner

    const t1 = (this.lowerBound.x - from.x) * dirFracX;
    const t2 = (this.upperBound.x - from.x) * dirFracX;
    const t3 = (this.lowerBound.y - from.y) * dirFracY;
    const t4 = (this.upperBound.y - from.y) * dirFracY;
    const t5 = (this.lowerBound.z - from.z) * dirFracZ;
    const t6 = (this.upperBound.z - from.z) * dirFracZ; // const tmin = Math.max(Math.max(Math.min(t1, t2), Math.min(t3, t4)));
    // const tmax = Math.min(Math.min(Math.max(t1, t2), Math.max(t3, t4)));

    const tmin = Math.max(Math.max(Math.min(t1, t2), Math.min(t3, t4)), Math.min(t5, t6));
    const tmax = Math.min(Math.min(Math.max(t1, t2), Math.max(t3, t4)), Math.max(t5, t6)); // if tmax < 0, ray (line) is intersecting AABB, but whole AABB is behing us

    if (tmax < 0) {
      //t = tmax;
      return false;
    } // if tmin > tmax, ray doesn't intersect AABB


    if (tmin > tmax) {
      //t = tmax;
      return false;
    }

    return true;
  }

}
const tmp$1 = new Vec3();
const transformIntoFrame_corners = [new Vec3(), new Vec3(), new Vec3(), new Vec3(), new Vec3(), new Vec3(), new Vec3(), new Vec3()];

/**
 * A Quaternion describes a rotation in 3D space. The Quaternion is mathematically defined as Q = x*i + y*j + z*k + w, where (i,j,k) are imaginary basis vectors. (x,y,z) can be seen as a vector related to the axis of rotation, while the real multiplier, w, is related to the amount of rotation.
 * @param x Multiplier of the imaginary basis vector i.
 * @param y Multiplier of the imaginary basis vector j.
 * @param z Multiplier of the imaginary basis vector k.
 * @param w Multiplier of the real part.
 * @see http://en.wikipedia.org/wiki/Quaternion
 */

class Quaternion {
  constructor(x, y, z, w) {
    if (x === void 0) {
      x = 0;
    }

    if (y === void 0) {
      y = 0;
    }

    if (z === void 0) {
      z = 0;
    }

    if (w === void 0) {
      w = 1;
    }

    this.x = x;
    this.y = y;
    this.z = z;
    this.w = w;
  }
  /**
   * Set the value of the quaternion.
   */


  set(x, y, z, w) {
    this.x = x;
    this.y = y;
    this.z = z;
    this.w = w;
    return this;
  }
  /**
   * Convert to a readable format
   * @return "x,y,z,w"
   */


  toString() {
    return `${this.x},${this.y},${this.z},${this.w}`;
  }
  /**
   * Convert to an Array
   * @return [x, y, z, w]
   */


  toArray() {
    return [this.x, this.y, this.z, this.w];
  }
  /**
   * Set the quaternion components given an axis and an angle in radians.
   */


  setFromAxisAngle(vector, angle) {
    const s = Math.sin(angle * 0.5);
    this.x = vector.x * s;
    this.y = vector.y * s;
    this.z = vector.z * s;
    this.w = Math.cos(angle * 0.5);
    return this;
  }
  /**
   * Converts the quaternion to [ axis, angle ] representation.
   * @param targetAxis A vector object to reuse for storing the axis.
   * @return An array, first element is the axis and the second is the angle in radians.
   */


  toAxisAngle(targetAxis) {
    if (targetAxis === void 0) {
      targetAxis = new Vec3();
    }

    this.normalize(); // if w>1 acos and sqrt will produce errors, this cant happen if quaternion is normalised

    const angle = 2 * Math.acos(this.w);
    const s = Math.sqrt(1 - this.w * this.w); // assuming quaternion normalised then w is less than 1, so term always positive.

    if (s < 0.001) {
      // test to avoid divide by zero, s is always positive due to sqrt
      // if s close to zero then direction of axis not important
      targetAxis.x = this.x; // if it is important that axis is normalised then replace with x=1; y=z=0;

      targetAxis.y = this.y;
      targetAxis.z = this.z;
    } else {
      targetAxis.x = this.x / s; // normalise axis

      targetAxis.y = this.y / s;
      targetAxis.z = this.z / s;
    }

    return [targetAxis, angle];
  }
  /**
   * Set the quaternion value given two vectors. The resulting rotation will be the needed rotation to rotate u to v.
   */


  setFromVectors(u, v) {
    if (u.isAntiparallelTo(v)) {
      const t1 = sfv_t1;
      const t2 = sfv_t2;
      u.tangents(t1, t2);
      this.setFromAxisAngle(t1, Math.PI);
    } else {
      const a = u.cross(v);
      this.x = a.x;
      this.y = a.y;
      this.z = a.z;
      this.w = Math.sqrt(u.length() ** 2 * v.length() ** 2) + u.dot(v);
      this.normalize();
    }

    return this;
  }
  /**
   * Multiply the quaternion with an other quaternion.
   */


  mult(quat, target) {
    if (target === void 0) {
      target = new Quaternion();
    }

    const ax = this.x;
    const ay = this.y;
    const az = this.z;
    const aw = this.w;
    const bx = quat.x;
    const by = quat.y;
    const bz = quat.z;
    const bw = quat.w;
    target.x = ax * bw + aw * bx + ay * bz - az * by;
    target.y = ay * bw + aw * by + az * bx - ax * bz;
    target.z = az * bw + aw * bz + ax * by - ay * bx;
    target.w = aw * bw - ax * bx - ay * by - az * bz;
    return target;
  }
  /**
   * Get the inverse quaternion rotation.
   */


  inverse(target) {
    if (target === void 0) {
      target = new Quaternion();
    }

    const x = this.x;
    const y = this.y;
    const z = this.z;
    const w = this.w;
    this.conjugate(target);
    const inorm2 = 1 / (x * x + y * y + z * z + w * w);
    target.x *= inorm2;
    target.y *= inorm2;
    target.z *= inorm2;
    target.w *= inorm2;
    return target;
  }
  /**
   * Get the quaternion conjugate
   */


  conjugate(target) {
    if (target === void 0) {
      target = new Quaternion();
    }

    target.x = -this.x;
    target.y = -this.y;
    target.z = -this.z;
    target.w = this.w;
    return target;
  }
  /**
   * Normalize the quaternion. Note that this changes the values of the quaternion.
   */


  normalize() {
    let l = Math.sqrt(this.x * this.x + this.y * this.y + this.z * this.z + this.w * this.w);

    if (l === 0) {
      this.x = 0;
      this.y = 0;
      this.z = 0;
      this.w = 0;
    } else {
      l = 1 / l;
      this.x *= l;
      this.y *= l;
      this.z *= l;
      this.w *= l;
    }

    return this;
  }
  /**
   * Approximation of quaternion normalization. Works best when quat is already almost-normalized.
   * @author unphased, https://github.com/unphased
   */


  normalizeFast() {
    const f = (3.0 - (this.x * this.x + this.y * this.y + this.z * this.z + this.w * this.w)) / 2.0;

    if (f === 0) {
      this.x = 0;
      this.y = 0;
      this.z = 0;
      this.w = 0;
    } else {
      this.x *= f;
      this.y *= f;
      this.z *= f;
      this.w *= f;
    }

    return this;
  }
  /**
   * Multiply the quaternion by a vector
   */


  vmult(v, target) {
    if (target === void 0) {
      target = new Vec3();
    }

    const x = v.x;
    const y = v.y;
    const z = v.z;
    const qx = this.x;
    const qy = this.y;
    const qz = this.z;
    const qw = this.w; // q*v

    const ix = qw * x + qy * z - qz * y;
    const iy = qw * y + qz * x - qx * z;
    const iz = qw * z + qx * y - qy * x;
    const iw = -qx * x - qy * y - qz * z;
    target.x = ix * qw + iw * -qx + iy * -qz - iz * -qy;
    target.y = iy * qw + iw * -qy + iz * -qx - ix * -qz;
    target.z = iz * qw + iw * -qz + ix * -qy - iy * -qx;
    return target;
  }
  /**
   * Copies value of source to this quaternion.
   * @return this
   */


  copy(quat) {
    this.x = quat.x;
    this.y = quat.y;
    this.z = quat.z;
    this.w = quat.w;
    return this;
  }
  /**
   * Convert the quaternion to euler angle representation. Order: YZX, as this page describes: https://www.euclideanspace.com/maths/standards/index.htm
   * @param order Three-character string, defaults to "YZX"
   */


  toEuler(target, order) {
    if (order === void 0) {
      order = 'YZX';
    }

    let heading;
    let attitude;
    let bank;
    const x = this.x;
    const y = this.y;
    const z = this.z;
    const w = this.w;

    switch (order) {
      case 'YZX':
        const test = x * y + z * w;

        if (test > 0.499) {
          // singularity at north pole
          heading = 2 * Math.atan2(x, w);
          attitude = Math.PI / 2;
          bank = 0;
        }

        if (test < -0.499) {
          // singularity at south pole
          heading = -2 * Math.atan2(x, w);
          attitude = -Math.PI / 2;
          bank = 0;
        }

        if (heading === undefined) {
          const sqx = x * x;
          const sqy = y * y;
          const sqz = z * z;
          heading = Math.atan2(2 * y * w - 2 * x * z, 1 - 2 * sqy - 2 * sqz); // Heading

          attitude = Math.asin(2 * test); // attitude

          bank = Math.atan2(2 * x * w - 2 * y * z, 1 - 2 * sqx - 2 * sqz); // bank
        }

        break;

      default:
        throw new Error(`Euler order ${order} not supported yet.`);
    }

    target.y = heading;
    target.z = attitude;
    target.x = bank;
  }
  /**
   * Set the quaternion components given Euler angle representation.
   *
   * @param order The order to apply angles: 'XYZ' or 'YXZ' or any other combination.
   *
   * See {@link https://www.mathworks.com/matlabcentral/fileexchange/20696-function-to-convert-between-dcm-euler-angles-quaternions-and-euler-vectors MathWorks} reference
   */


  setFromEuler(x, y, z, order) {
    if (order === void 0) {
      order = 'XYZ';
    }

    const c1 = Math.cos(x / 2);
    const c2 = Math.cos(y / 2);
    const c3 = Math.cos(z / 2);
    const s1 = Math.sin(x / 2);
    const s2 = Math.sin(y / 2);
    const s3 = Math.sin(z / 2);

    if (order === 'XYZ') {
      this.x = s1 * c2 * c3 + c1 * s2 * s3;
      this.y = c1 * s2 * c3 - s1 * c2 * s3;
      this.z = c1 * c2 * s3 + s1 * s2 * c3;
      this.w = c1 * c2 * c3 - s1 * s2 * s3;
    } else if (order === 'YXZ') {
      this.x = s1 * c2 * c3 + c1 * s2 * s3;
      this.y = c1 * s2 * c3 - s1 * c2 * s3;
      this.z = c1 * c2 * s3 - s1 * s2 * c3;
      this.w = c1 * c2 * c3 + s1 * s2 * s3;
    } else if (order === 'ZXY') {
      this.x = s1 * c2 * c3 - c1 * s2 * s3;
      this.y = c1 * s2 * c3 + s1 * c2 * s3;
      this.z = c1 * c2 * s3 + s1 * s2 * c3;
      this.w = c1 * c2 * c3 - s1 * s2 * s3;
    } else if (order === 'ZYX') {
      this.x = s1 * c2 * c3 - c1 * s2 * s3;
      this.y = c1 * s2 * c3 + s1 * c2 * s3;
      this.z = c1 * c2 * s3 - s1 * s2 * c3;
      this.w = c1 * c2 * c3 + s1 * s2 * s3;
    } else if (order === 'YZX') {
      this.x = s1 * c2 * c3 + c1 * s2 * s3;
      this.y = c1 * s2 * c3 + s1 * c2 * s3;
      this.z = c1 * c2 * s3 - s1 * s2 * c3;
      this.w = c1 * c2 * c3 - s1 * s2 * s3;
    } else if (order === 'XZY') {
      this.x = s1 * c2 * c3 - c1 * s2 * s3;
      this.y = c1 * s2 * c3 - s1 * c2 * s3;
      this.z = c1 * c2 * s3 + s1 * s2 * c3;
      this.w = c1 * c2 * c3 + s1 * s2 * s3;
    }

    return this;
  }

  clone() {
    return new Quaternion(this.x, this.y, this.z, this.w);
  }
  /**
   * Performs a spherical linear interpolation between two quat
   *
   * @param toQuat second operand
   * @param t interpolation amount between the self quaternion and toQuat
   * @param target A quaternion to store the result in. If not provided, a new one will be created.
   * @returns {Quaternion} The "target" object
   */


  slerp(toQuat, t, target) {
    if (target === void 0) {
      target = new Quaternion();
    }

    const ax = this.x;
    const ay = this.y;
    const az = this.z;
    const aw = this.w;
    let bx = toQuat.x;
    let by = toQuat.y;
    let bz = toQuat.z;
    let bw = toQuat.w;
    let omega;
    let cosom;
    let sinom;
    let scale0;
    let scale1; // calc cosine

    cosom = ax * bx + ay * by + az * bz + aw * bw; // adjust signs (if necessary)

    if (cosom < 0.0) {
      cosom = -cosom;
      bx = -bx;
      by = -by;
      bz = -bz;
      bw = -bw;
    } // calculate coefficients


    if (1.0 - cosom > 0.000001) {
      // standard case (slerp)
      omega = Math.acos(cosom);
      sinom = Math.sin(omega);
      scale0 = Math.sin((1.0 - t) * omega) / sinom;
      scale1 = Math.sin(t * omega) / sinom;
    } else {
      // "from" and "to" quaternions are very close
      //  ... so we can do a linear interpolation
      scale0 = 1.0 - t;
      scale1 = t;
    } // calculate final values


    target.x = scale0 * ax + scale1 * bx;
    target.y = scale0 * ay + scale1 * by;
    target.z = scale0 * az + scale1 * bz;
    target.w = scale0 * aw + scale1 * bw;
    return target;
  }
  /**
   * Rotate an absolute orientation quaternion given an angular velocity and a time step.
   */


  integrate(angularVelocity, dt, angularFactor, target) {
    if (target === void 0) {
      target = new Quaternion();
    }

    const ax = angularVelocity.x * angularFactor.x,
          ay = angularVelocity.y * angularFactor.y,
          az = angularVelocity.z * angularFactor.z,
          bx = this.x,
          by = this.y,
          bz = this.z,
          bw = this.w;
    const half_dt = dt * 0.5;
    target.x += half_dt * (ax * bw + ay * bz - az * by);
    target.y += half_dt * (ay * bw + az * bx - ax * bz);
    target.z += half_dt * (az * bw + ax * by - ay * bx);
    target.w += half_dt * (-ax * bx - ay * by - az * bz);
    return target;
  }

}
const sfv_t1 = new Vec3();
const sfv_t2 = new Vec3();

/**
 * The available shape types.
 */
const SHAPE_TYPES = {
  /** SPHERE */
  SPHERE: 1,

  /** PLANE */
  PLANE: 2,

  /** BOX */
  BOX: 4,

  /** COMPOUND */
  COMPOUND: 8,

  /** CONVEXPOLYHEDRON */
  CONVEXPOLYHEDRON: 16,

  /** HEIGHTFIELD */
  HEIGHTFIELD: 32,

  /** PARTICLE */
  PARTICLE: 64,

  /** CYLINDER */
  CYLINDER: 128,

  /** TRIMESH */
  TRIMESH: 256
};
/**
 * ShapeType
 */

/**
 * Base class for shapes
 */
class Shape {
  /**
   * Identifier of the Shape.
   */

  /**
   * The type of this shape. Must be set to an int > 0 by subclasses.
   */

  /**
   * The local bounding sphere radius of this shape.
   */

  /**
   * Whether to produce contact forces when in contact with other bodies. Note that contacts will be generated, but they will be disabled.
   * @default true
   */

  /**
   * @default 1
   */

  /**
   * @default -1
   */

  /**
   * Optional material of the shape that regulates contact properties.
   */

  /**
   * The body to which the shape is added to.
   */

  /**
   * All the Shape types.
   */
  constructor(options) {
    if (options === void 0) {
      options = {};
    }

    this.id = Shape.idCounter++;
    this.type = options.type || 0;
    this.boundingSphereRadius = 0;
    this.collisionResponse = options.collisionResponse ? options.collisionResponse : true;
    this.collisionFilterGroup = options.collisionFilterGroup !== undefined ? options.collisionFilterGroup : 1;
    this.collisionFilterMask = options.collisionFilterMask !== undefined ? options.collisionFilterMask : -1;
    this.material = options.material ? options.material : null;
    this.body = null;
  }
  /**
   * Computes the bounding sphere radius.
   * The result is stored in the property `.boundingSphereRadius`
   */


  updateBoundingSphereRadius() {
    throw `computeBoundingSphereRadius() not implemented for shape type ${this.type}`;
  }
  /**
   * Get the volume of this shape
   */


  volume() {
    throw `volume() not implemented for shape type ${this.type}`;
  }
  /**
   * Calculates the inertia in the local frame for this shape.
   * @see http://en.wikipedia.org/wiki/List_of_moments_of_inertia
   */


  calculateLocalInertia(mass, target) {
    throw `calculateLocalInertia() not implemented for shape type ${this.type}`;
  }
  /**
   * @todo use abstract for these kind of methods
   */


  calculateWorldAABB(pos, quat, min, max) {
    throw `calculateWorldAABB() not implemented for shape type ${this.type}`;
  }

}
Shape.idCounter = 0;
Shape.types = SHAPE_TYPES;

/**
 * Transformation utilities.
 */
class Transform {
  /**
   * position
   */

  /**
   * quaternion
   */
  constructor(options) {
    if (options === void 0) {
      options = {};
    }

    this.position = new Vec3();
    this.quaternion = new Quaternion();

    if (options.position) {
      this.position.copy(options.position);
    }

    if (options.quaternion) {
      this.quaternion.copy(options.quaternion);
    }
  }
  /**
   * Get a global point in local transform coordinates.
   */


  pointToLocal(worldPoint, result) {
    return Transform.pointToLocalFrame(this.position, this.quaternion, worldPoint, result);
  }
  /**
   * Get a local point in global transform coordinates.
   */


  pointToWorld(localPoint, result) {
    return Transform.pointToWorldFrame(this.position, this.quaternion, localPoint, result);
  }
  /**
   * vectorToWorldFrame
   */


  vectorToWorldFrame(localVector, result) {
    if (result === void 0) {
      result = new Vec3();
    }

    this.quaternion.vmult(localVector, result);
    return result;
  }
  /**
   * pointToLocalFrame
   */


  static pointToLocalFrame(position, quaternion, worldPoint, result) {
    if (result === void 0) {
      result = new Vec3();
    }

    worldPoint.vsub(position, result);
    quaternion.conjugate(tmpQuat$1);
    tmpQuat$1.vmult(result, result);
    return result;
  }
  /**
   * pointToWorldFrame
   */


  static pointToWorldFrame(position, quaternion, localPoint, result) {
    if (result === void 0) {
      result = new Vec3();
    }

    quaternion.vmult(localPoint, result);
    result.vadd(position, result);
    return result;
  }
  /**
   * vectorToWorldFrame
   */


  static vectorToWorldFrame(quaternion, localVector, result) {
    if (result === void 0) {
      result = new Vec3();
    }

    quaternion.vmult(localVector, result);
    return result;
  }
  /**
   * vectorToLocalFrame
   */


  static vectorToLocalFrame(position, quaternion, worldVector, result) {
    if (result === void 0) {
      result = new Vec3();
    }

    quaternion.w *= -1;
    quaternion.vmult(worldVector, result);
    quaternion.w *= -1;
    return result;
  }

}
const tmpQuat$1 = new Quaternion();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
[new Vec3(), new Vec3(), new Vec3(), new Vec3(), new Vec3(), new Vec3(), new Vec3(), new Vec3()];
new Vec3();
new Quaternion();
new AABB();
new Mat3();
new Mat3();
new Mat3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();

new Vec3();
new Vec3();
new Quaternion();
new Vec3();
new Vec3();
new Vec3();
new Vec3();

/**
 * Storage for Ray casting data
 */
class RaycastResult {
  /**
   * rayFromWorld
   */

  /**
   * rayToWorld
   */

  /**
   * hitNormalWorld
   */

  /**
   * hitPointWorld
   */

  /**
   * hasHit
   */

  /**
   * shape
   */

  /**
   * body
   */

  /**
   * The index of the hit triangle, if the hit shape was a trimesh
   */

  /**
   * Distance to the hit. Will be set to -1 if there was no hit
   */

  /**
   * If the ray should stop traversing the bodies
   */
  constructor() {
    this.rayFromWorld = new Vec3();
    this.rayToWorld = new Vec3();
    this.hitNormalWorld = new Vec3();
    this.hitPointWorld = new Vec3();
    this.hasHit = false;
    this.shape = null;
    this.body = null;
    this.hitFaceIndex = -1;
    this.distance = -1;
    this.shouldStop = false;
  }
  /**
   * Reset all result data.
   */


  reset() {
    this.rayFromWorld.setZero();
    this.rayToWorld.setZero();
    this.hitNormalWorld.setZero();
    this.hitPointWorld.setZero();
    this.hasHit = false;
    this.shape = null;
    this.body = null;
    this.hitFaceIndex = -1;
    this.distance = -1;
    this.shouldStop = false;
  }
  /**
   * abort
   */


  abort() {
    this.shouldStop = true;
  }
  /**
   * Set result data.
   */


  set(rayFromWorld, rayToWorld, hitNormalWorld, hitPointWorld, shape, body, distance) {
    this.rayFromWorld.copy(rayFromWorld);
    this.rayToWorld.copy(rayToWorld);
    this.hitNormalWorld.copy(hitNormalWorld);
    this.hitPointWorld.copy(hitPointWorld);
    this.shape = shape;
    this.body = body;
    this.distance = distance;
  }

}

let _Shape$types$SPHERE, _Shape$types$PLANE, _Shape$types$BOX, _Shape$types$CYLINDER, _Shape$types$CONVEXPO, _Shape$types$HEIGHTFI, _Shape$types$TRIMESH;

/**
 * RAY_MODES
 */
const RAY_MODES = {
  /** CLOSEST */
  CLOSEST: 1,

  /** ANY */
  ANY: 2,

  /** ALL */
  ALL: 4
};
/**
 * RayMode
 */

_Shape$types$SPHERE = Shape.types.SPHERE;
_Shape$types$PLANE = Shape.types.PLANE;
_Shape$types$BOX = Shape.types.BOX;
_Shape$types$CYLINDER = Shape.types.CYLINDER;
_Shape$types$CONVEXPO = Shape.types.CONVEXPOLYHEDRON;
_Shape$types$HEIGHTFI = Shape.types.HEIGHTFIELD;
_Shape$types$TRIMESH = Shape.types.TRIMESH;

/**
 * A line in 3D space that intersects bodies and return points.
 */
class Ray {
  /**
   * from
   */

  /**
   * to
   */

  /**
   * direction
   */

  /**
   * The precision of the ray. Used when checking parallelity etc.
   * @default 0.0001
   */

  /**
   * Set to `false` if you don't want the Ray to take `collisionResponse` flags into account on bodies and shapes.
   * @default true
   */

  /**
   * If set to `true`, the ray skips any hits with normal.dot(rayDirection) < 0.
   * @default false
   */

  /**
   * collisionFilterMask
   * @default -1
   */

  /**
   * collisionFilterGroup
   * @default -1
   */

  /**
   * The intersection mode. Should be Ray.ANY, Ray.ALL or Ray.CLOSEST.
   * @default RAY.ANY
   */

  /**
   * Current result object.
   */

  /**
   * Will be set to `true` during intersectWorld() if the ray hit anything.
   */

  /**
   * User-provided result callback. Will be used if mode is Ray.ALL.
   */

  /**
   * CLOSEST
   */

  /**
   * ANY
   */

  /**
   * ALL
   */
  get [_Shape$types$SPHERE]() {
    return this._intersectSphere;
  }

  get [_Shape$types$PLANE]() {
    return this._intersectPlane;
  }

  get [_Shape$types$BOX]() {
    return this._intersectBox;
  }

  get [_Shape$types$CYLINDER]() {
    return this._intersectConvex;
  }

  get [_Shape$types$CONVEXPO]() {
    return this._intersectConvex;
  }

  get [_Shape$types$HEIGHTFI]() {
    return this._intersectHeightfield;
  }

  get [_Shape$types$TRIMESH]() {
    return this._intersectTrimesh;
  }

  constructor(from, to) {
    if (from === void 0) {
      from = new Vec3();
    }

    if (to === void 0) {
      to = new Vec3();
    }

    this.from = from.clone();
    this.to = to.clone();
    this.direction = new Vec3();
    this.precision = 0.0001;
    this.checkCollisionResponse = true;
    this.skipBackfaces = false;
    this.collisionFilterMask = -1;
    this.collisionFilterGroup = -1;
    this.mode = Ray.ANY;
    this.result = new RaycastResult();
    this.hasHit = false;

    this.callback = result => {};
  }
  /**
   * Do itersection against all bodies in the given World.
   * @return True if the ray hit anything, otherwise false.
   */


  intersectWorld(world, options) {
    this.mode = options.mode || Ray.ANY;
    this.result = options.result || new RaycastResult();
    this.skipBackfaces = !!options.skipBackfaces;
    this.collisionFilterMask = typeof options.collisionFilterMask !== 'undefined' ? options.collisionFilterMask : -1;
    this.collisionFilterGroup = typeof options.collisionFilterGroup !== 'undefined' ? options.collisionFilterGroup : -1;
    this.checkCollisionResponse = typeof options.checkCollisionResponse !== 'undefined' ? options.checkCollisionResponse : true;

    if (options.from) {
      this.from.copy(options.from);
    }

    if (options.to) {
      this.to.copy(options.to);
    }

    this.callback = options.callback || (() => {});

    this.hasHit = false;
    this.result.reset();
    this.updateDirection();
    this.getAABB(tmpAABB$1);
    tmpArray.length = 0;
    world.broadphase.aabbQuery(world, tmpAABB$1, tmpArray);
    this.intersectBodies(tmpArray);
    return this.hasHit;
  }
  /**
   * Shoot a ray at a body, get back information about the hit.
   * @deprecated @param result set the result property of the Ray instead.
   */


  intersectBody(body, result) {
    if (result) {
      this.result = result;
      this.updateDirection();
    }

    const checkCollisionResponse = this.checkCollisionResponse;

    if (checkCollisionResponse && !body.collisionResponse) {
      return;
    }

    if ((this.collisionFilterGroup & body.collisionFilterMask) === 0 || (body.collisionFilterGroup & this.collisionFilterMask) === 0) {
      return;
    }

    const xi = intersectBody_xi;
    const qi = intersectBody_qi;

    for (let i = 0, N = body.shapes.length; i < N; i++) {
      const shape = body.shapes[i];

      if (checkCollisionResponse && !shape.collisionResponse) {
        continue; // Skip
      }

      body.quaternion.mult(body.shapeOrientations[i], qi);
      body.quaternion.vmult(body.shapeOffsets[i], xi);
      xi.vadd(body.position, xi);
      this.intersectShape(shape, qi, xi, body);

      if (this.result.shouldStop) {
        break;
      }
    }
  }
  /**
   * Shoot a ray at an array bodies, get back information about the hit.
   * @param bodies An array of Body objects.
   * @deprecated @param result set the result property of the Ray instead.
   *
   */


  intersectBodies(bodies, result) {
    if (result) {
      this.result = result;
      this.updateDirection();
    }

    for (let i = 0, l = bodies.length; !this.result.shouldStop && i < l; i++) {
      this.intersectBody(bodies[i]);
    }
  }
  /**
   * Updates the direction vector.
   */


  updateDirection() {
    this.to.vsub(this.from, this.direction);
    this.direction.normalize();
  }

  intersectShape(shape, quat, position, body) {
    const from = this.from; // Checking boundingSphere

    const distance = distanceFromIntersection(from, this.direction, position);

    if (distance > shape.boundingSphereRadius) {
      return;
    }

    const intersectMethod = this[shape.type];

    if (intersectMethod) {
      intersectMethod.call(this, shape, quat, position, body, shape);
    }
  }

  _intersectBox(box, quat, position, body, reportedShape) {
    return this._intersectConvex(box.convexPolyhedronRepresentation, quat, position, body, reportedShape);
  }

  _intersectPlane(shape, quat, position, body, reportedShape) {
    const from = this.from;
    const to = this.to;
    const direction = this.direction; // Get plane normal

    const worldNormal = new Vec3(0, 0, 1);
    quat.vmult(worldNormal, worldNormal);
    const len = new Vec3();
    from.vsub(position, len);
    const planeToFrom = len.dot(worldNormal);
    to.vsub(position, len);
    const planeToTo = len.dot(worldNormal);

    if (planeToFrom * planeToTo > 0) {
      // "from" and "to" are on the same side of the plane... bail out
      return;
    }

    if (from.distanceTo(to) < planeToFrom) {
      return;
    }

    const n_dot_dir = worldNormal.dot(direction);

    if (Math.abs(n_dot_dir) < this.precision) {
      // No intersection
      return;
    }

    const planePointToFrom = new Vec3();
    const dir_scaled_with_t = new Vec3();
    const hitPointWorld = new Vec3();
    from.vsub(position, planePointToFrom);
    const t = -worldNormal.dot(planePointToFrom) / n_dot_dir;
    direction.scale(t, dir_scaled_with_t);
    from.vadd(dir_scaled_with_t, hitPointWorld);
    this.reportIntersection(worldNormal, hitPointWorld, reportedShape, body, -1);
  }
  /**
   * Get the world AABB of the ray.
   */


  getAABB(aabb) {
    const {
      lowerBound,
      upperBound
    } = aabb;
    const to = this.to;
    const from = this.from;
    lowerBound.x = Math.min(to.x, from.x);
    lowerBound.y = Math.min(to.y, from.y);
    lowerBound.z = Math.min(to.z, from.z);
    upperBound.x = Math.max(to.x, from.x);
    upperBound.y = Math.max(to.y, from.y);
    upperBound.z = Math.max(to.z, from.z);
  }

  _intersectHeightfield(shape, quat, position, body, reportedShape) {
    shape.data;
    shape.elementSize; // Convert the ray to local heightfield coordinates

    const localRay = intersectHeightfield_localRay; //new Ray(this.from, this.to);

    localRay.from.copy(this.from);
    localRay.to.copy(this.to);
    Transform.pointToLocalFrame(position, quat, localRay.from, localRay.from);
    Transform.pointToLocalFrame(position, quat, localRay.to, localRay.to);
    localRay.updateDirection(); // Get the index of the data points to test against

    const index = intersectHeightfield_index;
    let iMinX;
    let iMinY;
    let iMaxX;
    let iMaxY; // Set to max

    iMinX = iMinY = 0;
    iMaxX = iMaxY = shape.data.length - 1;
    const aabb = new AABB();
    localRay.getAABB(aabb);
    shape.getIndexOfPosition(aabb.lowerBound.x, aabb.lowerBound.y, index, true);
    iMinX = Math.max(iMinX, index[0]);
    iMinY = Math.max(iMinY, index[1]);
    shape.getIndexOfPosition(aabb.upperBound.x, aabb.upperBound.y, index, true);
    iMaxX = Math.min(iMaxX, index[0] + 1);
    iMaxY = Math.min(iMaxY, index[1] + 1);

    for (let i = iMinX; i < iMaxX; i++) {
      for (let j = iMinY; j < iMaxY; j++) {
        if (this.result.shouldStop) {
          return;
        }

        shape.getAabbAtIndex(i, j, aabb);

        if (!aabb.overlapsRay(localRay)) {
          continue;
        } // Lower triangle


        shape.getConvexTrianglePillar(i, j, false);
        Transform.pointToWorldFrame(position, quat, shape.pillarOffset, worldPillarOffset);

        this._intersectConvex(shape.pillarConvex, quat, worldPillarOffset, body, reportedShape, intersectConvexOptions);

        if (this.result.shouldStop) {
          return;
        } // Upper triangle


        shape.getConvexTrianglePillar(i, j, true);
        Transform.pointToWorldFrame(position, quat, shape.pillarOffset, worldPillarOffset);

        this._intersectConvex(shape.pillarConvex, quat, worldPillarOffset, body, reportedShape, intersectConvexOptions);
      }
    }
  }

  _intersectSphere(sphere, quat, position, body, reportedShape) {
    const from = this.from;
    const to = this.to;
    const r = sphere.radius;
    const a = (to.x - from.x) ** 2 + (to.y - from.y) ** 2 + (to.z - from.z) ** 2;
    const b = 2 * ((to.x - from.x) * (from.x - position.x) + (to.y - from.y) * (from.y - position.y) + (to.z - from.z) * (from.z - position.z));
    const c = (from.x - position.x) ** 2 + (from.y - position.y) ** 2 + (from.z - position.z) ** 2 - r ** 2;
    const delta = b ** 2 - 4 * a * c;
    const intersectionPoint = Ray_intersectSphere_intersectionPoint;
    const normal = Ray_intersectSphere_normal;

    if (delta < 0) {
      // No intersection
      return;
    } else if (delta === 0) {
      // single intersection point
      from.lerp(to, delta, intersectionPoint);
      intersectionPoint.vsub(position, normal);
      normal.normalize();
      this.reportIntersection(normal, intersectionPoint, reportedShape, body, -1);
    } else {
      const d1 = (-b - Math.sqrt(delta)) / (2 * a);
      const d2 = (-b + Math.sqrt(delta)) / (2 * a);

      if (d1 >= 0 && d1 <= 1) {
        from.lerp(to, d1, intersectionPoint);
        intersectionPoint.vsub(position, normal);
        normal.normalize();
        this.reportIntersection(normal, intersectionPoint, reportedShape, body, -1);
      }

      if (this.result.shouldStop) {
        return;
      }

      if (d2 >= 0 && d2 <= 1) {
        from.lerp(to, d2, intersectionPoint);
        intersectionPoint.vsub(position, normal);
        normal.normalize();
        this.reportIntersection(normal, intersectionPoint, reportedShape, body, -1);
      }
    }
  }

  _intersectConvex(shape, quat, position, body, reportedShape, options) {
    const normal = intersectConvex_normal;
    const vector = intersectConvex_vector;
    const faceList = options && options.faceList || null; // Checking faces

    const faces = shape.faces;
    const vertices = shape.vertices;
    const normals = shape.faceNormals;
    const direction = this.direction;
    const from = this.from;
    const to = this.to;
    const fromToDistance = from.distanceTo(to);
    const Nfaces = faceList ? faceList.length : faces.length;
    const result = this.result;

    for (let j = 0; !result.shouldStop && j < Nfaces; j++) {
      const fi = faceList ? faceList[j] : j;
      const face = faces[fi];
      const faceNormal = normals[fi];
      const q = quat;
      const x = position; // determine if ray intersects the plane of the face
      // note: this works regardless of the direction of the face normal
      // Get plane point in world coordinates...

      vector.copy(vertices[face[0]]);
      q.vmult(vector, vector);
      vector.vadd(x, vector); // ...but make it relative to the ray from. We'll fix this later.

      vector.vsub(from, vector); // Get plane normal

      q.vmult(faceNormal, normal); // If this dot product is negative, we have something interesting

      const dot = direction.dot(normal); // Bail out if ray and plane are parallel

      if (Math.abs(dot) < this.precision) {
        continue;
      } // calc distance to plane


      const scalar = normal.dot(vector) / dot; // if negative distance, then plane is behind ray

      if (scalar < 0) {
        continue;
      } // if (dot < 0) {
      // Intersection point is from + direction * scalar


      direction.scale(scalar, intersectPoint);
      intersectPoint.vadd(from, intersectPoint); // a is the point we compare points b and c with.

      a.copy(vertices[face[0]]);
      q.vmult(a, a);
      x.vadd(a, a);

      for (let i = 1; !result.shouldStop && i < face.length - 1; i++) {
        // Transform 3 vertices to world coords
        b.copy(vertices[face[i]]);
        c.copy(vertices[face[i + 1]]);
        q.vmult(b, b);
        q.vmult(c, c);
        x.vadd(b, b);
        x.vadd(c, c);
        const distance = intersectPoint.distanceTo(from);

        if (!(Ray.pointInTriangle(intersectPoint, a, b, c) || Ray.pointInTriangle(intersectPoint, b, a, c)) || distance > fromToDistance) {
          continue;
        }

        this.reportIntersection(normal, intersectPoint, reportedShape, body, fi);
      } // }

    }
  }
  /**
   * @todo Optimize by transforming the world to local space first.
   * @todo Use Octree lookup
   */


  _intersectTrimesh(mesh, quat, position, body, reportedShape, options) {
    const normal = intersectTrimesh_normal;
    const triangles = intersectTrimesh_triangles;
    const treeTransform = intersectTrimesh_treeTransform;
    const vector = intersectConvex_vector;
    const localDirection = intersectTrimesh_localDirection;
    const localFrom = intersectTrimesh_localFrom;
    const localTo = intersectTrimesh_localTo;
    const worldIntersectPoint = intersectTrimesh_worldIntersectPoint;
    const worldNormal = intersectTrimesh_worldNormal; // Checking faces

    const indices = mesh.indices;
    mesh.vertices; // const normals = mesh.faceNormals

    const from = this.from;
    const to = this.to;
    const direction = this.direction;
    treeTransform.position.copy(position);
    treeTransform.quaternion.copy(quat); // Transform ray to local space!

    Transform.vectorToLocalFrame(position, quat, direction, localDirection);
    Transform.pointToLocalFrame(position, quat, from, localFrom);
    Transform.pointToLocalFrame(position, quat, to, localTo);
    localTo.x *= mesh.scale.x;
    localTo.y *= mesh.scale.y;
    localTo.z *= mesh.scale.z;
    localFrom.x *= mesh.scale.x;
    localFrom.y *= mesh.scale.y;
    localFrom.z *= mesh.scale.z;
    localTo.vsub(localFrom, localDirection);
    localDirection.normalize();
    const fromToDistanceSquared = localFrom.distanceSquared(localTo);
    mesh.tree.rayQuery(this, treeTransform, triangles);

    for (let i = 0, N = triangles.length; !this.result.shouldStop && i !== N; i++) {
      const trianglesIndex = triangles[i];
      mesh.getNormal(trianglesIndex, normal); // determine if ray intersects the plane of the face
      // note: this works regardless of the direction of the face normal
      // Get plane point in world coordinates...

      mesh.getVertex(indices[trianglesIndex * 3], a); // ...but make it relative to the ray from. We'll fix this later.

      a.vsub(localFrom, vector); // If this dot product is negative, we have something interesting

      const dot = localDirection.dot(normal); // Bail out if ray and plane are parallel
      // if (Math.abs( dot ) < this.precision){
      //     continue;
      // }
      // calc distance to plane

      const scalar = normal.dot(vector) / dot; // if negative distance, then plane is behind ray

      if (scalar < 0) {
        continue;
      } // Intersection point is from + direction * scalar


      localDirection.scale(scalar, intersectPoint);
      intersectPoint.vadd(localFrom, intersectPoint); // Get triangle vertices

      mesh.getVertex(indices[trianglesIndex * 3 + 1], b);
      mesh.getVertex(indices[trianglesIndex * 3 + 2], c);
      const squaredDistance = intersectPoint.distanceSquared(localFrom);

      if (!(Ray.pointInTriangle(intersectPoint, b, a, c) || Ray.pointInTriangle(intersectPoint, a, b, c)) || squaredDistance > fromToDistanceSquared) {
        continue;
      } // transform intersectpoint and normal to world


      Transform.vectorToWorldFrame(quat, normal, worldNormal);
      Transform.pointToWorldFrame(position, quat, intersectPoint, worldIntersectPoint);
      this.reportIntersection(worldNormal, worldIntersectPoint, reportedShape, body, trianglesIndex);
    }

    triangles.length = 0;
  }
  /**
   * @return True if the intersections should continue
   */


  reportIntersection(normal, hitPointWorld, shape, body, hitFaceIndex) {
    const from = this.from;
    const to = this.to;
    const distance = from.distanceTo(hitPointWorld);
    const result = this.result; // Skip back faces?

    if (this.skipBackfaces && normal.dot(this.direction) > 0) {
      return;
    }

    result.hitFaceIndex = typeof hitFaceIndex !== 'undefined' ? hitFaceIndex : -1;

    switch (this.mode) {
      case Ray.ALL:
        this.hasHit = true;
        result.set(from, to, normal, hitPointWorld, shape, body, distance);
        result.hasHit = true;
        this.callback(result);
        break;

      case Ray.CLOSEST:
        // Store if closer than current closest
        if (distance < result.distance || !result.hasHit) {
          this.hasHit = true;
          result.hasHit = true;
          result.set(from, to, normal, hitPointWorld, shape, body, distance);
        }

        break;

      case Ray.ANY:
        // Report and stop.
        this.hasHit = true;
        result.hasHit = true;
        result.set(from, to, normal, hitPointWorld, shape, body, distance);
        result.shouldStop = true;
        break;
    }
  }
  /**
   * As per "Barycentric Technique" as named
   * {@link https://www.blackpawn.com/texts/pointinpoly/default.html here} but without the division
   */


  static pointInTriangle(p, a, b, c) {
    c.vsub(a, v0);
    b.vsub(a, v1);
    p.vsub(a, v2);
    const dot00 = v0.dot(v0);
    const dot01 = v0.dot(v1);
    const dot02 = v0.dot(v2);
    const dot11 = v1.dot(v1);
    const dot12 = v1.dot(v2);
    let u;
    let v;
    return (u = dot11 * dot02 - dot01 * dot12) >= 0 && (v = dot00 * dot12 - dot01 * dot02) >= 0 && u + v < dot00 * dot11 - dot01 * dot01;
  }

}
Ray.CLOSEST = RAY_MODES.CLOSEST;
Ray.ANY = RAY_MODES.ANY;
Ray.ALL = RAY_MODES.ALL;
const tmpAABB$1 = new AABB();
const tmpArray = [];
const v1 = new Vec3();
const v2 = new Vec3();
const intersectBody_xi = new Vec3();
const intersectBody_qi = new Quaternion();
const intersectPoint = new Vec3();
const a = new Vec3();
const b = new Vec3();
const c = new Vec3();
new Vec3();
new RaycastResult();
const intersectConvexOptions = {
  faceList: [0]
};
const worldPillarOffset = new Vec3();
const intersectHeightfield_localRay = new Ray();
const intersectHeightfield_index = [];
const Ray_intersectSphere_intersectionPoint = new Vec3();
const Ray_intersectSphere_normal = new Vec3();
const intersectConvex_normal = new Vec3();
new Vec3();
new Vec3();
const intersectConvex_vector = new Vec3();
const intersectTrimesh_normal = new Vec3();
const intersectTrimesh_localDirection = new Vec3();
const intersectTrimesh_localFrom = new Vec3();
const intersectTrimesh_localTo = new Vec3();
const intersectTrimesh_worldNormal = new Vec3();
const intersectTrimesh_worldIntersectPoint = new Vec3();
new AABB();
const intersectTrimesh_triangles = [];
const intersectTrimesh_treeTransform = new Transform();
const v0 = new Vec3();
const intersect = new Vec3();

function distanceFromIntersection(from, direction, position) {
  // v0 is vector from from to position
  position.vsub(from, v0);
  const dot = v0.dot(direction); // intersect = direction*dot + from

  direction.scale(dot, intersect);
  intersect.vadd(from, intersect);
  const distance = position.distanceTo(intersect);
  return distance;
}
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3(); // Temp vectors

new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Ray();
new Vec3();
new Vec3();
new Vec3();
[new Vec3(1, 0, 0), new Vec3(0, 1, 0), new Vec3(0, 0, 1)];
new Vec3();
new Vec3();
new Vec3();
new Vec3();

new Vec3();
new Vec3();
new Vec3();
new Vec3();

new Vec3();
new Vec3();
new Vec3(); // bilateral constraint between two dynamic objects
new Vec3();
new Vec3();
new Vec3(); // Temp vectors for calculation

new Vec3(); // Relative velocity

new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3(); // from https://en.wikipedia.org/wiki/Barycentric_coordinate_system
new Vec3();
new AABB();
new Vec3();
new AABB();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new AABB();
new Vec3();
new Transform();
new AABB();

// Naming rule: based of the order in SHAPE_TYPES,
// the first part of the method is formed by the
// shape type that comes before, in the second part
// there is the shape type that comes after in the SHAPE_TYPES list
({
  sphereSphere: Shape.types.SPHERE,
  spherePlane: Shape.types.SPHERE | Shape.types.PLANE,
  boxBox: Shape.types.BOX | Shape.types.BOX,
  sphereBox: Shape.types.SPHERE | Shape.types.BOX,
  planeBox: Shape.types.PLANE | Shape.types.BOX,
  convexConvex: Shape.types.CONVEXPOLYHEDRON,
  sphereConvex: Shape.types.SPHERE | Shape.types.CONVEXPOLYHEDRON,
  planeConvex: Shape.types.PLANE | Shape.types.CONVEXPOLYHEDRON,
  boxConvex: Shape.types.BOX | Shape.types.CONVEXPOLYHEDRON,
  sphereHeightfield: Shape.types.SPHERE | Shape.types.HEIGHTFIELD,
  boxHeightfield: Shape.types.BOX | Shape.types.HEIGHTFIELD,
  convexHeightfield: Shape.types.CONVEXPOLYHEDRON | Shape.types.HEIGHTFIELD,
  sphereParticle: Shape.types.PARTICLE | Shape.types.SPHERE,
  planeParticle: Shape.types.PLANE | Shape.types.PARTICLE,
  boxParticle: Shape.types.BOX | Shape.types.PARTICLE,
  convexParticle: Shape.types.PARTICLE | Shape.types.CONVEXPOLYHEDRON,
  cylinderCylinder: Shape.types.CYLINDER,
  sphereCylinder: Shape.types.SPHERE | Shape.types.CYLINDER,
  planeCylinder: Shape.types.PLANE | Shape.types.CYLINDER,
  boxCylinder: Shape.types.BOX | Shape.types.CYLINDER,
  convexCylinder: Shape.types.CONVEXPOLYHEDRON | Shape.types.CYLINDER,
  heightfieldCylinder: Shape.types.HEIGHTFIELD | Shape.types.CYLINDER,
  particleCylinder: Shape.types.PARTICLE | Shape.types.CYLINDER,
  sphereTrimesh: Shape.types.SPHERE | Shape.types.TRIMESH,
  planeTrimesh: Shape.types.PLANE | Shape.types.TRIMESH
});
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Quaternion();
new Quaternion();

new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new AABB();
new Vec3();
new Vec3(); // See http://bulletphysics.com/Bullet/BulletFull/SphereTriangleDetector_8cpp_source.html

new Vec3();
new Vec3();
new Vec3();

new Vec3();
new Vec3();
new Vec3();
new Vec3();
[new Vec3(), new Vec3(), new Vec3(), new Vec3(), new Vec3(), new Vec3()];
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3(); // WIP

new Quaternion();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();
new Vec3();

new AABB();
new Ray(); // performance.now() fallback on Date.now()

const performance = globalThis.performance || {};

if (!performance.now) {
  let nowOffset = Date.now();

  if (performance.timing && performance.timing.navigationStart) {
    nowOffset = performance.timing.navigationStart;
  }

  performance.now = () => Date.now() - nowOffset;
}

new Vec3(); // Dispatched after the world has stepped forward in time.

function CannonDebugger(scene, world, _temp) {
  let {
    color = 0x00ff00,
    scale = 1,
    onInit,
    onUpdate
  } = _temp === void 0 ? {} : _temp;
  const _meshes = [];

  const _material = new MeshBasicMaterial({
    color: color != null ? color : 0x00ff00,
    wireframe: true
  });

  const _tempVec0 = new Vec3();

  const _tempVec1 = new Vec3();

  const _tempVec2 = new Vec3();

  const _tempQuat0 = new Quaternion();

  const _sphereGeometry = new SphereGeometry(1);

  const _boxGeometry = new BoxGeometry(1, 1, 1);

  const _planeGeometry = new PlaneGeometry(10, 10, 10, 10); // Move the planeGeometry forward a little bit to prevent z-fighting


  _planeGeometry.translate(0, 0, 0.0001);

  function createConvexPolyhedronGeometry(shape) {
    const geometry = new BufferGeometry(); // Add vertices

    const positions = [];

    for (let i = 0; i < shape.vertices.length; i++) {
      const vertex = shape.vertices[i];
      positions.push(vertex.x, vertex.y, vertex.z);
    }

    geometry.setAttribute('position', new Float32BufferAttribute(positions, 3)); // Add faces

    const indices = [];

    for (let i = 0; i < shape.faces.length; i++) {
      const face = shape.faces[i];
      const a = face[0];

      for (let j = 1; j < face.length - 1; j++) {
        const b = face[j];
        const c = face[j + 1];
        indices.push(a, b, c);
      }
    }

    geometry.setIndex(indices);
    geometry.computeBoundingSphere();
    geometry.computeVertexNormals();
    return geometry;
  }

  function createTrimeshGeometry(shape) {
    const geometry = new BufferGeometry();
    const positions = [];
    const v0 = _tempVec0;
    const v1 = _tempVec1;
    const v2 = _tempVec2;

    for (let i = 0; i < shape.indices.length / 3; i++) {
      shape.getTriangleVertices(i, v0, v1, v2);
      positions.push(v0.x, v0.y, v0.z);
      positions.push(v1.x, v1.y, v1.z);
      positions.push(v2.x, v2.y, v2.z);
    }

    geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
    geometry.computeBoundingSphere();
    geometry.computeVertexNormals();
    return geometry;
  }

  function createHeightfieldGeometry(shape) {
    const geometry = new BufferGeometry();
    const s = shape.elementSize || 1; // assumes square heightfield, else i*x, j*y

    const positions = shape.data.flatMap((row, i) => row.flatMap((z, j) => [i * s, j * s, z]));
    const indices = [];

    for (let xi = 0; xi < shape.data.length - 1; xi++) {
      for (let yi = 0; yi < shape.data[xi].length - 1; yi++) {
        const stride = shape.data[xi].length;
        const index = xi * stride + yi;
        indices.push(index + 1, index + stride, index + stride + 1);
        indices.push(index + stride, index + 1, index);
      }
    }

    geometry.setIndex(indices);
    geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
    geometry.computeBoundingSphere();
    geometry.computeVertexNormals();
    return geometry;
  }

  function createMesh(shape) {
    let mesh = new Mesh();
    const {
      SPHERE,
      BOX,
      PLANE,
      CYLINDER,
      CONVEXPOLYHEDRON,
      TRIMESH,
      HEIGHTFIELD
    } = Shape.types;

    switch (shape.type) {
      case SPHERE:
        {
          mesh = new Mesh(_sphereGeometry, _material);
          break;
        }

      case BOX:
        {
          mesh = new Mesh(_boxGeometry, _material);
          break;
        }

      case PLANE:
        {
          mesh = new Mesh(_planeGeometry, _material);
          break;
        }

      case CYLINDER:
        {
          const geometry = new CylinderGeometry(shape.radiusTop, shape.radiusBottom, shape.height, shape.numSegments);
          mesh = new Mesh(geometry, _material);
          shape.geometryId = geometry.id;
          break;
        }

      case CONVEXPOLYHEDRON:
        {
          const geometry = createConvexPolyhedronGeometry(shape);
          mesh = new Mesh(geometry, _material);
          shape.geometryId = geometry.id;
          break;
        }

      case TRIMESH:
        {
          const geometry = createTrimeshGeometry(shape);
          mesh = new Mesh(geometry, _material);
          shape.geometryId = geometry.id;
          break;
        }

      case HEIGHTFIELD:
        {
          const geometry = createHeightfieldGeometry(shape);
          mesh = new Mesh(geometry, _material);
          shape.geometryId = geometry.id;
          break;
        }
    }

    scene.add(mesh);
    return mesh;
  }

  function scaleMesh(mesh, shape) {
    const {
      SPHERE,
      BOX,
      PLANE,
      CYLINDER,
      CONVEXPOLYHEDRON,
      TRIMESH,
      HEIGHTFIELD
    } = Shape.types;

    switch (shape.type) {
      case SPHERE:
        {
          const {
            radius
          } = shape;
          mesh.scale.set(radius * scale, radius * scale, radius * scale);
          break;
        }

      case BOX:
        {
          mesh.scale.copy(shape.halfExtents);
          mesh.scale.multiplyScalar(2 * scale);
          break;
        }

      case PLANE:
        {
          break;
        }

      case CYLINDER:
        {
          mesh.scale.set(1 * scale, 1 * scale, 1 * scale);
          break;
        }

      case CONVEXPOLYHEDRON:
        {
          mesh.scale.set(1 * scale, 1 * scale, 1 * scale);
          break;
        }

      case TRIMESH:
        {
          mesh.scale.copy(shape.scale).multiplyScalar(scale);
          break;
        }

      case HEIGHTFIELD:
        {
          mesh.scale.set(1 * scale, 1 * scale, 1 * scale);
          break;
        }
    }
  }

  function typeMatch(mesh, shape) {
    if (!mesh) return false;
    const {
      geometry
    } = mesh;
    return geometry instanceof SphereGeometry && shape.type === Shape.types.SPHERE || geometry instanceof BoxGeometry && shape.type === Shape.types.BOX || geometry instanceof PlaneGeometry && shape.type === Shape.types.PLANE || geometry.id === shape.geometryId && shape.type === Shape.types.CYLINDER || geometry.id === shape.geometryId && shape.type === Shape.types.CONVEXPOLYHEDRON || geometry.id === shape.geometryId && shape.type === Shape.types.TRIMESH || geometry.id === shape.geometryId && shape.type === Shape.types.HEIGHTFIELD;
  }

  function updateMesh(index, shape) {
    let mesh = _meshes[index];
    let didCreateNewMesh = false;

    if (!typeMatch(mesh, shape)) {
      if (mesh) scene.remove(mesh);
      _meshes[index] = mesh = createMesh(shape);
      didCreateNewMesh = true;
    }

    scaleMesh(mesh, shape);
    return didCreateNewMesh;
  }

  function update() {
    const meshes = _meshes;
    const shapeWorldPosition = _tempVec0;
    const shapeWorldQuaternion = _tempQuat0;
    let meshIndex = 0;

    for (const body of world.bodies) {
      for (let i = 0; i !== body.shapes.length; i++) {
        const shape = body.shapes[i];
        const didCreateNewMesh = updateMesh(meshIndex, shape);
        const mesh = meshes[meshIndex];

        if (mesh) {
          // Get world position
          body.quaternion.vmult(body.shapeOffsets[i], shapeWorldPosition);
          body.position.vadd(shapeWorldPosition, shapeWorldPosition); // Get world quaternion

          body.quaternion.mult(body.shapeOrientations[i], shapeWorldQuaternion); // Copy to meshes

          mesh.position.copy(shapeWorldPosition);
          mesh.quaternion.copy(shapeWorldQuaternion);
          if (didCreateNewMesh && onInit instanceof Function) onInit(body, mesh, shape);
          if (!didCreateNewMesh && onUpdate instanceof Function) onUpdate(body, mesh, shape);
        }

        meshIndex++;
      }
    }

    for (let i = meshIndex; i < meshes.length; i++) {
      const mesh = meshes[i];
      if (mesh) scene.remove(mesh);
    }

    meshes.length = meshIndex;
  }

  return {
    update
  };
}

const debugContext = /*#__PURE__*/createContext(null);
const useDebugContext = () => useContext(debugContext);

const physicsContext = /*#__PURE__*/createContext(null);
const usePhysicsContext = () => {
  const context = useContext(physicsContext);
  if (!context) throw new Error('Physics context not found. @react-three/cannon & components can only be used within a Physics provider');
  return context;
};

const q$2 = new Quaternion$2();
const s$1 = new Vector3(1, 1, 1);
const v$1 = new Vector3();
const m$1 = new Matrix4();

const getMatrix = o => {
  if (o instanceof InstancedMesh) {
    o.getMatrixAt(parseInt(o.uuid.split('/')[1]), m$1);
    return m$1;
  }

  return o.matrix;
};

function DebugProvider(_ref) {
  let {
    children,
    color = 'black',
    impl = CannonDebugger,
    scale = 1
  } = _ref;
  const [{
    bodies,
    bodyMap
  }] = useState({
    bodies: [],
    bodyMap: {}
  });
  const {
    refs
  } = usePhysicsContext();
  const [scene] = useState(() => new Scene());
  const cannonDebuggerRef = useRef(impl(scene, {
    bodies
  }, {
    color,
    scale
  }));
  useFrame(() => {
    for (const uuid in bodyMap) {
      getMatrix(refs[uuid]).decompose(v$1, q$2, s$1);
      bodyMap[uuid].position.copy(v$1);
      bodyMap[uuid].quaternion.copy(q$2);
    }

    cannonDebuggerRef.current.update();
  });
  const api = useMemo(() => ({
    add(uuid, props, type) {
      const body = propsToBody({
        props,
        type,
        uuid
      });
      bodies.push(body);
      bodyMap[uuid] = body;
    },

    remove(uuid) {
      const index = bodies.indexOf(bodyMap[uuid]);
      if (index !== -1) bodies.splice(index, 1);
      delete bodyMap[uuid];
    }

  }), [bodies, bodyMap]);
  return /*#__PURE__*/jsxs(debugContext.Provider, {
    value: api,
    children: [/*#__PURE__*/jsx("primitive", {
      object: scene
    }), children]
  });
}

const temp = new Object3D();

function useForwardedRef(ref) {
  const nullRef = useRef(null);
  return ref && typeof ref !== 'function' ? ref : nullRef;
}

function capitalize(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function getUUID(ref, index) {
  const suffix = index === undefined ? '' : `/${index}`;
  if (typeof ref === 'function') return null;
  return ref && ref.current && `${ref.current.uuid}${suffix}`;
}

const e = new Euler();
const q$1 = new Quaternion$2();

const quaternionToRotation = callback => {
  return v => callback(e.setFromQuaternion(q$1.fromArray(v)).toArray());
};

let incrementingId = 0;

function subscribe(ref, worker, subscriptions, type, index, target) {
  if (target === void 0) {
    target = 'bodies';
  }

  return callback => {
    const id = incrementingId++;
    subscriptions[id] = {
      [type]: callback
    };
    const uuid = getUUID(ref, index);
    uuid && worker.subscribe({
      props: {
        id,
        target,
        type
      },
      uuid
    });
    return () => {
      delete subscriptions[id];
      worker.unsubscribe({
        props: id
      });
    };
  };
}

function prepare(object, _ref) {
  let {
    position = [0, 0, 0],
    rotation = [0, 0, 0],
    userData = {}
  } = _ref;
  object.userData = userData;
  object.position.set(...position);
  object.rotation.set(...rotation);
  object.updateMatrix();
}

function setupCollision(events, _ref2, uuid) {
  let {
    onCollide,
    onCollideBegin,
    onCollideEnd
  } = _ref2;
  events[uuid] = {
    collide: onCollide,
    collideBegin: onCollideBegin,
    collideEnd: onCollideEnd
  };
}

function useBody(type, fn, argsFn, fwdRef, deps) {
  if (fwdRef === void 0) {
    fwdRef = null;
  }

  if (deps === void 0) {
    deps = [];
  }

  const ref = useForwardedRef(fwdRef);
  const {
    events,
    refs,
    scaleOverrides,
    subscriptions,
    worker
  } = usePhysicsContext();
  const debugApi = useDebugContext();
  useLayoutEffect(() => {
    if (!ref.current) {
      // When the reference isn't used we create a stub
      // The body doesn't have a visual representation but can still be constrained
      // Yes, this type may be technically incorrect
      ref.current = new Object3D();
    }

    const object = ref.current;
    const currentWorker = worker;
    const objectCount = object instanceof InstancedMesh ? (object.instanceMatrix.setUsage(DynamicDrawUsage), object.count) : 1;
    const uuid = object instanceof InstancedMesh ? new Array(objectCount).fill(0).map((_, i) => `${object.uuid}/${i}`) : [object.uuid];
    const props = object instanceof InstancedMesh ? uuid.map((id, i) => {
      const props = fn(i);
      prepare(temp, props);
      object.setMatrixAt(i, temp.matrix);
      object.instanceMatrix.needsUpdate = true;
      refs[id] = object;
      debugApi == null ? void 0 : debugApi.add(id, props, type);
      setupCollision(events, props, id);
      return { ...props,
        args: argsFn(props.args)
      };
    }) : uuid.map((id, i) => {
      const props = fn(i);
      prepare(object, props);
      refs[id] = object;
      debugApi == null ? void 0 : debugApi.add(id, props, type);
      setupCollision(events, props, id);
      return { ...props,
        args: argsFn(props.args)
      };
    }); // Register on mount, unregister on unmount

    currentWorker.addBodies({
      props: props.map(_ref3 => {
        let {
          onCollide,
          onCollideBegin,
          onCollideEnd,
          ...serializableProps
        } = _ref3;
        return {
          onCollide: Boolean(onCollide),
          ...serializableProps
        };
      }),
      type,
      uuid
    });
    return () => {
      uuid.forEach(id => {
        delete refs[id];
        debugApi == null ? void 0 : debugApi.remove(id);
        delete events[id];
      });
      currentWorker.removeBodies({
        uuid
      });
    };
  }, deps);
  const api = useMemo(() => {
    const makeAtomic = (type, index) => {
      const op = `set${capitalize(type)}`;
      return {
        set: value => {
          const uuid = getUUID(ref, index);
          uuid && worker[op]({
            props: value,
            uuid
          });
        },
        subscribe: subscribe(ref, worker, subscriptions, type, index)
      };
    };

    const makeQuaternion = index => {
      const type = 'quaternion';
      return {
        copy: _ref4 => {
          let {
            w,
            x,
            y,
            z
          } = _ref4;
          const uuid = getUUID(ref, index);
          uuid && worker.setQuaternion({
            props: [x, y, z, w],
            uuid
          });
        },
        set: (x, y, z, w) => {
          const uuid = getUUID(ref, index);
          uuid && worker.setQuaternion({
            props: [x, y, z, w],
            uuid
          });
        },
        subscribe: subscribe(ref, worker, subscriptions, type, index)
      };
    };

    const makeRotation = index => {
      return {
        copy: _ref5 => {
          let {
            x,
            y,
            z
          } = _ref5;
          const uuid = getUUID(ref, index);
          uuid && worker.setRotation({
            props: [x, y, z],
            uuid
          });
        },
        set: (x, y, z) => {
          const uuid = getUUID(ref, index);
          uuid && worker.setRotation({
            props: [x, y, z],
            uuid
          });
        },
        subscribe: callback => {
          const id = incrementingId++;
          const target = 'bodies';
          const type = 'quaternion';
          const uuid = getUUID(ref, index);
          subscriptions[id] = {
            [type]: quaternionToRotation(callback)
          };
          uuid && worker.subscribe({
            props: {
              id,
              target,
              type
            },
            uuid
          });
          return () => {
            delete subscriptions[id];
            worker.unsubscribe({
              props: id
            });
          };
        }
      };
    };

    const makeVec = (type, index) => {
      const op = `set${capitalize(type)}`;
      return {
        copy: _ref6 => {
          let {
            x,
            y,
            z
          } = _ref6;
          const uuid = getUUID(ref, index);
          uuid && worker[op]({
            props: [x, y, z],
            uuid
          });
        },
        set: (x, y, z) => {
          const uuid = getUUID(ref, index);
          uuid && worker[op]({
            props: [x, y, z],
            uuid
          });
        },
        subscribe: subscribe(ref, worker, subscriptions, type, index)
      };
    };

    function makeApi(index) {
      return {
        allowSleep: makeAtomic('allowSleep', index),
        angularDamping: makeAtomic('angularDamping', index),
        angularFactor: makeVec('angularFactor', index),
        angularVelocity: makeVec('angularVelocity', index),

        applyForce(force, worldPoint) {
          const uuid = getUUID(ref, index);
          uuid && worker.applyForce({
            props: [force, worldPoint],
            uuid
          });
        },

        applyImpulse(impulse, worldPoint) {
          const uuid = getUUID(ref, index);
          uuid && worker.applyImpulse({
            props: [impulse, worldPoint],
            uuid
          });
        },

        applyLocalForce(force, localPoint) {
          const uuid = getUUID(ref, index);
          uuid && worker.applyLocalForce({
            props: [force, localPoint],
            uuid
          });
        },

        applyLocalImpulse(impulse, localPoint) {
          const uuid = getUUID(ref, index);
          uuid && worker.applyLocalImpulse({
            props: [impulse, localPoint],
            uuid
          });
        },

        applyTorque(torque) {
          const uuid = getUUID(ref, index);
          uuid && worker.applyTorque({
            props: [torque],
            uuid
          });
        },

        collisionFilterGroup: makeAtomic('collisionFilterGroup', index),
        collisionFilterMask: makeAtomic('collisionFilterMask', index),
        collisionResponse: makeAtomic('collisionResponse', index),
        fixedRotation: makeAtomic('fixedRotation', index),
        isTrigger: makeAtomic('isTrigger', index),
        linearDamping: makeAtomic('linearDamping', index),
        linearFactor: makeVec('linearFactor', index),
        mass: makeAtomic('mass', index),
        material: makeAtomic('material', index),
        position: makeVec('position', index),
        quaternion: makeQuaternion(index),
        rotation: makeRotation(index),

        scaleOverride(scale) {
          const uuid = getUUID(ref, index);
          if (uuid) scaleOverrides[uuid] = new Vector3(...scale);
        },

        sleep() {
          const uuid = getUUID(ref, index);
          uuid && worker.sleep({
            uuid
          });
        },

        sleepSpeedLimit: makeAtomic('sleepSpeedLimit', index),
        sleepTimeLimit: makeAtomic('sleepTimeLimit', index),
        userData: makeAtomic('userData', index),
        velocity: makeVec('velocity', index),

        wakeUp() {
          const uuid = getUUID(ref, index);
          uuid && worker.wakeUp({
            uuid
          });
        }

      };
    }

    const cache = {};
    return { ...makeApi(undefined),
      at: index => cache[index] || (cache[index] = makeApi(index))
    };
  }, []);
  return [ref, api];
}

function makeTriplet(v) {
  return v instanceof Vector3 ? [v.x, v.y, v.z] : v;
}

function usePlane(fn, fwdRef, deps) {
  return useBody('Plane', fn, () => [], fwdRef, deps);
}
function useBox(fn, fwdRef, deps) {
  const defaultBoxArgs = [1, 1, 1];
  return useBody('Box', fn, function (args) {
    if (args === void 0) {
      args = defaultBoxArgs;
    }

    return args;
  }, fwdRef, deps);
}
function useCylinder(fn, fwdRef, deps) {
  return useBody('Cylinder', fn, function (args) {
    if (args === void 0) {
      args = [];
    }

    return args;
  }, fwdRef, deps);
}
function useHeightfield(fn, fwdRef, deps) {
  return useBody('Heightfield', fn, args => args, fwdRef, deps);
}
function useParticle(fn, fwdRef, deps) {
  return useBody('Particle', fn, () => [], fwdRef, deps);
}
function useSphere(fn, fwdRef, deps) {
  return useBody('Sphere', fn, function (args) {
    if (args === void 0) {
      args = [1];
    }

    if (!Array.isArray(args)) throw new Error('useSphere args must be an array');
    return [args[0]];
  }, fwdRef, deps);
}
function useTrimesh(fn, fwdRef, deps) {
  return useBody('Trimesh', fn, args => args, fwdRef, deps);
}
function useConvexPolyhedron(fn, fwdRef, deps) {
  return useBody('ConvexPolyhedron', fn, function (_temp) {
    let [vertices, faces, normals, axes, boundingSphereRadius] = _temp === void 0 ? [] : _temp;
    return [vertices && vertices.map(makeTriplet), faces, normals && normals.map(makeTriplet), axes && axes.map(makeTriplet), boundingSphereRadius];
  }, fwdRef, deps);
}
function useCompoundBody(fn, fwdRef, deps) {
  return useBody('Compound', fn, args => args, fwdRef, deps);
}

function useConstraint(type, bodyA, bodyB, optns, deps) {
  if (optns === void 0) {
    optns = {};
  }

  if (deps === void 0) {
    deps = [];
  }

  const {
    worker
  } = usePhysicsContext();
  const uuid = MathUtils.generateUUID();
  const refA = useForwardedRef(bodyA);
  const refB = useForwardedRef(bodyB);
  useEffect(() => {
    if (refA.current && refB.current) {
      worker.addConstraint({
        props: [refA.current.uuid, refB.current.uuid, optns],
        type,
        uuid
      });
      return () => worker.removeConstraint({
        uuid
      });
    }
  }, deps);
  const api = useMemo(() => {
    const enableDisable = {
      disable: () => worker.disableConstraint({
        uuid
      }),
      enable: () => worker.enableConstraint({
        uuid
      })
    };

    if (type === 'Hinge') {
      return { ...enableDisable,
        disableMotor: () => worker.disableConstraintMotor({
          uuid
        }),
        enableMotor: () => worker.enableConstraintMotor({
          uuid
        }),
        setMotorMaxForce: value => worker.setConstraintMotorMaxForce({
          props: value,
          uuid
        }),
        setMotorSpeed: value => worker.setConstraintMotorSpeed({
          props: value,
          uuid
        })
      };
    }

    return enableDisable;
  }, deps);
  return [refA, refB, api];
}

function usePointToPointConstraint(bodyA, bodyB, optns, deps) {
  if (bodyA === void 0) {
    bodyA = null;
  }

  if (bodyB === void 0) {
    bodyB = null;
  }

  if (deps === void 0) {
    deps = [];
  }

  return useConstraint('PointToPoint', bodyA, bodyB, optns, deps);
}
function useConeTwistConstraint(bodyA, bodyB, optns, deps) {
  if (bodyA === void 0) {
    bodyA = null;
  }

  if (bodyB === void 0) {
    bodyB = null;
  }

  if (deps === void 0) {
    deps = [];
  }

  return useConstraint('ConeTwist', bodyA, bodyB, optns, deps);
}
function useDistanceConstraint(bodyA, bodyB, optns, deps) {
  if (bodyA === void 0) {
    bodyA = null;
  }

  if (bodyB === void 0) {
    bodyB = null;
  }

  if (deps === void 0) {
    deps = [];
  }

  return useConstraint('Distance', bodyA, bodyB, optns, deps);
}
function useHingeConstraint(bodyA, bodyB, optns, deps) {
  if (bodyA === void 0) {
    bodyA = null;
  }

  if (bodyB === void 0) {
    bodyB = null;
  }

  if (deps === void 0) {
    deps = [];
  }

  return useConstraint('Hinge', bodyA, bodyB, optns, deps);
}
function useLockConstraint(bodyA, bodyB, optns, deps) {
  if (bodyA === void 0) {
    bodyA = null;
  }

  if (bodyB === void 0) {
    bodyB = null;
  }

  if (deps === void 0) {
    deps = [];
  }

  return useConstraint('Lock', bodyA, bodyB, optns, deps);
}
function useSpring(bodyA, bodyB, optns, deps) {
  if (bodyA === void 0) {
    bodyA = null;
  }

  if (bodyB === void 0) {
    bodyB = null;
  }

  if (deps === void 0) {
    deps = [];
  }

  const {
    worker
  } = usePhysicsContext();
  const [uuid] = useState(() => MathUtils.generateUUID());
  const refA = useForwardedRef(bodyA);
  const refB = useForwardedRef(bodyB);
  useEffect(() => {
    if (refA.current && refB.current) {
      worker.addSpring({
        props: [refA.current.uuid, refB.current.uuid, optns],
        uuid
      });
      return () => {
        worker.removeSpring({
          uuid
        });
      };
    }
  }, deps);
  const api = useMemo(() => ({
    setDamping: value => worker.setSpringDamping({
      props: value,
      uuid
    }),
    setRestLength: value => worker.setSpringRestLength({
      props: value,
      uuid
    }),
    setStiffness: value => worker.setSpringStiffness({
      props: value,
      uuid
    })
  }), deps);
  return [refA, refB, api];
}

function useRay(mode, options, callback, deps) {
  if (deps === void 0) {
    deps = [];
  }

  const {
    worker,
    events
  } = usePhysicsContext();
  const [uuid] = useState(() => MathUtils.generateUUID());
  useEffect(() => {
    events[uuid] = {
      rayhit: callback
    };
    worker.addRay({
      props: { ...options,
        mode
      },
      uuid
    });
    return () => {
      worker.removeRay({
        uuid
      });
      delete events[uuid];
    };
  }, deps);
}

function useRaycastClosest(options, callback, deps) {
  if (deps === void 0) {
    deps = [];
  }

  useRay('Closest', options, callback, deps);
}
function useRaycastAny(options, callback, deps) {
  if (deps === void 0) {
    deps = [];
  }

  useRay('Any', options, callback, deps);
}
function useRaycastAll(options, callback, deps) {
  if (deps === void 0) {
    deps = [];
  }

  useRay('All', options, callback, deps);
}

function isString(v) {
  return typeof v === 'string';
}

function useRaycastVehicle(fn, fwdRef, deps) {
  if (fwdRef === void 0) {
    fwdRef = null;
  }

  if (deps === void 0) {
    deps = [];
  }

  const ref = useForwardedRef(fwdRef);
  const {
    worker,
    subscriptions
  } = usePhysicsContext();
  useLayoutEffect(() => {
    if (!ref.current) {
      // When the reference isn't used we create a stub
      // The body doesn't have a visual representation but can still be constrained
      // Yes, this type may be technically incorrect
      ref.current = new Object3D();
    }

    const currentWorker = worker;
    const uuid = ref.current.uuid;
    const {
      chassisBody,
      indexForwardAxis = 2,
      indexRightAxis = 0,
      indexUpAxis = 1,
      wheelInfos,
      wheels
    } = fn();
    const chassisBodyUUID = getUUID(chassisBody);
    const wheelUUIDs = wheels.map(ref => getUUID(ref));
    if (!chassisBodyUUID || !wheelUUIDs.every(isString)) return;
    currentWorker.addRaycastVehicle({
      props: [chassisBodyUUID, wheelUUIDs, wheelInfos, indexForwardAxis, indexRightAxis, indexUpAxis],
      uuid
    });
    return () => {
      currentWorker.removeRaycastVehicle({
        uuid
      });
    };
  }, deps);
  const api = useMemo(() => {
    return {
      applyEngineForce(value, wheelIndex) {
        const uuid = getUUID(ref);
        uuid && worker.applyRaycastVehicleEngineForce({
          props: [value, wheelIndex],
          uuid
        });
      },

      setBrake(brake, wheelIndex) {
        const uuid = getUUID(ref);
        uuid && worker.setRaycastVehicleBrake({
          props: [brake, wheelIndex],
          uuid
        });
      },

      setSteeringValue(value, wheelIndex) {
        const uuid = getUUID(ref);
        uuid && worker.setRaycastVehicleSteeringValue({
          props: [value, wheelIndex],
          uuid
        });
      },

      sliding: {
        subscribe: subscribe(ref, worker, subscriptions, 'sliding', undefined, 'vehicles')
      }
    };
  }, deps);
  return [ref, api];
}
function useContactMaterial(materialA, materialB, options, deps) {
  if (deps === void 0) {
    deps = [];
  }

  const {
    worker
  } = usePhysicsContext();
  const [uuid] = useState(() => MathUtils.generateUUID());
  useEffect(() => {
    worker.addContactMaterial({
      props: [materialA, materialB, options],
      uuid
    });
    return () => {
      worker.removeContactMaterial({
        uuid
      });
    };
  }, deps);
}

const v = new Vector3();
const s = new Vector3(1, 1, 1);
const q = new Quaternion$2();
const m = new Matrix4();

function apply(index, positions, quaternions, scale, object) {
  if (scale === void 0) {
    scale = s;
  }

  if (index !== undefined) {
    m.compose(v.fromArray(positions, index * 3), q.fromArray(quaternions, index * 4), scale);

    if (object) {
      object.matrixAutoUpdate = false;
      object.matrix.copy(m);
    }

    return m;
  }

  return m.identity();
}

function PhysicsProvider(_ref) {
  let {
    allowSleep = false,
    axisIndex = 0,
    broadphase = 'Naive',
    children,
    defaultContactMaterial = {
      contactEquationStiffness: 1e6
    },
    frictionGravity = null,
    gravity = [0, -9.81, 0],
    isPaused = false,
    iterations = 5,
    maxSubSteps = 10,
    quatNormalizeFast = false,
    quatNormalizeSkip = 0,
    shouldInvalidate = true,
    size = 1000,
    solver = 'GS',
    stepSize = 1 / 60,
    tolerance = 0.001
  } = _ref;
  const {
    invalidate
  } = useThree();
  const [{
    bodies,
    events,
    refs,
    scaleOverrides,
    subscriptions,
    worker
  }] = useState(() => ({
    bodies: {},
    events: {},
    refs: {},
    scaleOverrides: {},
    subscriptions: {},
    worker: new CannonWorkerAPI({
      allowSleep,
      axisIndex,
      broadphase,
      defaultContactMaterial,
      frictionGravity,
      gravity,
      iterations,
      quatNormalizeFast,
      quatNormalizeSkip,
      size,
      solver,
      tolerance
    })
  }));
  let timeSinceLastCalled = 0;
  const loop = useCallback((_, delta) => {
    if (isPaused) return;
    timeSinceLastCalled += delta;
    worker.step({
      maxSubSteps,
      stepSize,
      timeSinceLastCalled
    });
    timeSinceLastCalled = 0;
  }, [isPaused, maxSubSteps, stepSize]);

  const collideHandler = _ref2 => {
    var _events$target;

    let {
      body,
      contact: {
        bi,
        bj,
        ...contactRest
      },
      target,
      ...rest
    } = _ref2;
    const cb = (_events$target = events[target]) == null ? void 0 : _events$target.collide;
    cb && cb({
      body: refs[body],
      contact: {
        bi: refs[bi],
        bj: refs[bj],
        ...contactRest
      },
      target: refs[target],
      ...rest
    });
  };

  const collideBeginHandler = _ref3 => {
    var _events$bodyA, _events$bodyB;

    let {
      bodyA,
      bodyB
    } = _ref3;
    const cbA = (_events$bodyA = events[bodyA]) == null ? void 0 : _events$bodyA.collideBegin;
    cbA && cbA({
      body: refs[bodyB],
      op: 'event',
      target: refs[bodyA],
      type: 'collideBegin'
    });
    const cbB = (_events$bodyB = events[bodyB]) == null ? void 0 : _events$bodyB.collideBegin;
    cbB && cbB({
      body: refs[bodyA],
      op: 'event',
      target: refs[bodyB],
      type: 'collideBegin'
    });
  };

  const collideEndHandler = _ref4 => {
    var _events$bodyA2, _events$bodyB2;

    let {
      bodyA,
      bodyB
    } = _ref4;
    const cbA = (_events$bodyA2 = events[bodyA]) == null ? void 0 : _events$bodyA2.collideEnd;
    cbA && cbA({
      body: refs[bodyB],
      op: 'event',
      target: refs[bodyA],
      type: 'collideEnd'
    });
    const cbB = (_events$bodyB2 = events[bodyB]) == null ? void 0 : _events$bodyB2.collideEnd;
    cbB && cbB({
      body: refs[bodyA],
      op: 'event',
      target: refs[bodyB],
      type: 'collideEnd'
    });
  };

  const frameHandler = _ref5 => {
    let {
      active,
      bodies: uuids = [],
      observations,
      positions,
      quaternions
    } = _ref5;

    for (let i = 0; i < uuids.length; i++) {
      bodies[uuids[i]] = i;
    }

    observations.forEach(_ref6 => {
      let [id, value, type] = _ref6;
      const subscription = subscriptions[id] || {};
      const cb = subscription[type]; // HELP: We clearly know the type of the callback, but typescript can't deal with it

      cb && cb(value);
    });

    if (active) {
      for (const ref of Object.values(refs)) {
        if (ref instanceof InstancedMesh) {
          for (let i = 0; i < ref.count; i++) {
            const uuid = `${ref.uuid}/${i}`;
            const index = bodies[uuid];

            if (index !== undefined) {
              ref.setMatrixAt(i, apply(index, positions, quaternions, scaleOverrides[uuid]));
              ref.instanceMatrix.needsUpdate = true;
            }
          }
        } else {
          const scale = scaleOverrides[ref.uuid] || ref.scale;
          apply(bodies[ref.uuid], positions, quaternions, scale, ref);
        }
      }

      if (shouldInvalidate) {
        invalidate();
      }
    }
  };

  const rayhitHandler = _ref7 => {
    var _events$uuid;

    let {
      body,
      ray: {
        uuid,
        ...rayRest
      },
      ...rest
    } = _ref7;
    const cb = (_events$uuid = events[uuid]) == null ? void 0 : _events$uuid.rayhit;
    cb && cb({
      body: body ? refs[body] : null,
      ray: {
        uuid,
        ...rayRest
      },
      ...rest
    });
  }; // Run loop *after* all the physics objects have ran theirs!
  // Otherwise the buffers will be invalidated by the browser


  useFrame(loop);
  useEffect(() => {
    worker.connect();
    worker.init();
    worker.on('collide', collideHandler);
    worker.on('collideBegin', collideBeginHandler);
    worker.on('collideEnd', collideEndHandler);
    worker.on('frame', frameHandler);
    worker.on('rayhit', rayhitHandler);
    return () => {
      worker.terminate();
      worker.removeAllListeners();
    };
  }, []);
  useEffect(() => {
    worker.axisIndex = axisIndex;
  }, [axisIndex]);
  useEffect(() => {
    worker.broadphase = broadphase;
  }, [broadphase]);
  useEffect(() => {
    worker.gravity = gravity;
  }, [gravity]);
  useEffect(() => {
    worker.iterations = iterations;
  }, [iterations]);
  useEffect(() => {
    worker.tolerance = tolerance;
  }, [tolerance]);
  const value = useMemo(() => ({
    bodies,
    events,
    refs,
    scaleOverrides,
    subscriptions,
    worker
  }), [bodies, events, refs, subscriptions, worker]);
  return /*#__PURE__*/jsx(physicsContext.Provider, {
    value: value,
    children: children
  });
}

export { DebugProvider as Debug, PhysicsProvider as Physics, useBox, useCompoundBody, useConeTwistConstraint, useContactMaterial, useConvexPolyhedron, useCylinder, useDistanceConstraint, useHeightfield, useHingeConstraint, useLockConstraint, useParticle, usePlane, usePointToPointConstraint, useRaycastAll, useRaycastAny, useRaycastClosest, useRaycastVehicle, useSphere, useSpring, useTrimesh };
